import React, { useEffect, useMemo, useRef, useState, useCallback, lazy, Suspense } from "react";
import "./App.css";
import { auth, db } from "./firebase";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  verifyPasswordResetCode,
  confirmPasswordReset,
} from "firebase/auth";
import {
  addDoc,
  collection,
  getDocs,
  getDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  onSnapshot,
} from "firebase/firestore";

import {
  TextInput,
  HamburgerIcon,
} from "./components/ui";

// Onboarding
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import MobileOnboardingCarousel from "./components/onboarding/MobileOnboardingCarousel";
import { getIntroSteps, introOptions } from "./components/onboarding/desktopOnboardingSteps";
import { useViewport } from "./hooks/useViewport";
import { useViewNavigation } from "./hooks/useViewNavigation";
import { getUTMParameters } from "./utils/utmTracking";

// Keep small/lightweight components and UI elements as regular imports
import SeasonSetupView from "./components/views/SeasonSetupView";
import SeasonEditView from "./components/views/SeasonEditView";
import TournamentCreateView from "./components/views/TournamentCreateView";
import TournamentEditView from "./components/views/TournamentEditView";
import PlayerSeasonSelectors from "./components/views/PlayerSeasonSelectors";
import MatchDetailsForm from "./components/views/MatchDetailsForm";
import MatchMediaUpload from "./components/views/MatchMediaUpload";
import UpgradePromptModal from "./components/views/UpgradePromptModal";
import { SyncStatusIndicator } from "./components/ui";
import { useSubscription } from "./hooks/useSubscription";
import { canAddPlayer, canAddSeason, canAccessTrends } from "./utils/subscriptionGates";
import { verifyAndActivateSubscription } from "./services/subscriptionVerification";
import { redirectToCustomerPortal } from "./services/customerPortal";
import PrimaryNavigation from "./components/PrimaryNavigation";
import TeamSecondaryNav from "./components/TeamSecondaryNav";
import PlayerSecondaryNav from "./components/PlayerSecondaryNav";
import BlogHeader from "./components/blog/BlogHeader";
import DesktopSidebar from "./components/DesktopSidebar";
import TeamSelectionModal from "./components/modals/TeamSelectionModal";
import { buildMailto, escapeCsv } from "./utils/stats";

// Lazy load larger views for better code splitting and performance
const LandingView = lazy(() => import("./components/views/LandingView"));
const PublicPricingView = lazy(() => import("./components/views/PublicPricingView"));
const PricingView = lazy(() => import("./components/views/PricingView"));
const PlayerSetupView = lazy(() => import("./components/views/PlayerSetupView"));
const PrivacyView = lazy(() => import("./components/views/PrivacyView"));
const ChangelogView = lazy(() => import("./components/views/ChangelogView"));
const ContactView = lazy(() => import("./components/views/ContactView"));

// Lazy load heavy views for better performance
const TeamListView = lazy(() => import("./components/views/TeamListView"));
const TeamSetupView = lazy(() => import("./components/views/TeamSetupView"));
const CoachDashboardView = lazy(() => import("./components/views/CoachDashboardView"));
const RosterManagementView = lazy(() => import("./components/views/RosterManagementView"));
const PlayerOverviewView = lazy(() => import("./components/views/PlayerOverviewView"));
const SharedPlayerStatsView = lazy(() => import("./components/views/SharedPlayerStatsView"));
const TeamSeasonManagementView = lazy(() => import("./components/views/TeamSeasonManagementView"));
const TeamSeasonDetailView = lazy(() => import("./components/views/TeamSeasonDetailView"));
const TeamTournamentManagementView = lazy(() => import("./components/views/TeamTournamentManagementView"));
const TeamTournamentDetailView = lazy(() => import("./components/views/TeamTournamentDetailView"));
const TeamMatchRecordView = lazy(() => import("./components/views/TeamMatchRecordView"));
const TeamStatsView = lazy(() => import("./components/views/TeamStatsView"));
const TeamReportsView = lazy(() => import("./components/views/TeamReportsView"));
const PlayerReportCardsView = lazy(() => import("./components/views/PlayerReportCardsView"));
const TeamComparisonView = lazy(() => import("./components/views/TeamComparisonView"));
const TeamPlayerProgressView = lazy(() => import("./components/views/TeamPlayerProgressView"));
const TeamLineupsView = lazy(() => import("./components/views/TeamLineupsView"));
const TeamLineupEditorView = lazy(() => import("./components/views/TeamLineupEditorView"));
const WorkListGeneratorView = lazy(() => import("./components/views/WorkListGeneratorView"));
const BlogAdminView = lazy(() => import("./components/views/BlogAdminView"));
const LeaderboardView = lazy(() => import("./components/views/LeaderboardView").then(module => ({ default: module.LeaderboardView })));

// Offline functionality
const OFFLINE_STORAGE_KEYS = {
  MATCHES: 'vballstats_matches',
  PLAYERS: 'vballstats_players', 
  SEASONS: 'vballstats_seasons',
  TOURNAMENTS: 'vballstats_tournaments',
  SYNC_QUEUE: 'vballstats_sync_queue',
  ADMIN_TEAMS: 'vballstats_admin_teams',
  ADMIN_TEAM_ROSTERS: 'vballstats_admin_team_rosters',
  ADMIN_TEAM_SEASONS: 'vballstats_admin_team_seasons',
  ADMIN_TEAM_MATCHES: 'vballstats_admin_team_matches'
};

// Feature flags
const FEATURES = {
  TOURNAMENTS: process.env.REACT_APP_TOURNAMENTS_ENABLED === 'true' || false
};

// Offline utilities
const OfflineManager = {
  // Check if we're online
  isOnline: () => navigator.onLine,
  
  // Save data to local storage
  saveToLocal: (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error('Failed to save to local storage:', error);
      return false;
    }
  },
  
  // Load data from local storage
  loadFromLocal: (key) => {
    try {
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Failed to load from local storage:', error);
      return null;
    }
  },
  
  // Add operation to sync queue
  addToSyncQueue: (operation, data) => {
    const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
    queue.push({
      id: Date.now() + Math.random(),
      operation,
      data,
      timestamp: new Date().toISOString()
    });
    OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE, queue);
  },
  
  // Process sync queue
  processSyncQueue: async () => {
    const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
    if (queue.length === 0) return;
    
    
    for (const item of queue) {
      try {
        await OfflineManager.executeOperation(item.operation, item.data);
      } catch (error) {
        console.error(`Failed to sync operation ${item.operation}:`, error);
      }
    }
    
    // Clear the queue after processing
    OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE, []);
  },

  // Clean up pending changes for deleted players
  cleanupDeletedPlayerChanges: (deletedPlayerId) => {
    const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
    const filteredQueue = queue.filter(item => {
      // Remove items that belong to the deleted player
      if (item.data && item.data.playerId === deletedPlayerId) {
        console.log(`Removing pending ${item.operation} for deleted player ${deletedPlayerId}`);
        return false;
      }
      return true;
    });
    
    if (filteredQueue.length !== queue.length) {
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE, filteredQueue);
      console.log(`Cleaned up ${queue.length - filteredQueue.length} pending changes for deleted player`);
      return queue.length - filteredQueue.length; // Return number of items cleaned up
    }
    return 0;
  },

  // Clean up pending changes for deleted seasons
  cleanupDeletedSeasonChanges: (deletedPlayerId, deletedSeasonId) => {
    const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
    const filteredQueue = queue.filter(item => {
      // Remove items that belong to the deleted season
      if (item.data && item.data.playerId === deletedPlayerId && item.data.seasonId === deletedSeasonId) {
        console.log(`Removing pending ${item.operation} for deleted season ${deletedSeasonId}`);
        return false;
      }
      return true;
    });
    
    if (filteredQueue.length !== queue.length) {
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE, filteredQueue);
      console.log(`Cleaned up ${queue.length - filteredQueue.length} pending changes for deleted season`);
    }
  },

  // Get pending operations count
  getPendingOperations: () => {
    const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
    return queue;
  },
  
  // Execute a queued operation
  executeOperation: async (operation, data) => {
    const userId = data.userId;
    if (!userId) {
      throw new Error('User ID is required for sync operations');
    }

    switch (operation) {
      case 'saveMatch':
        const matchRef = collection(db, "users", userId, "players", data.playerId, "seasons", data.seasonId, "matches");
        await addDoc(matchRef, data);
        break;
      case 'updateMatch':
        const updateMatchRef = doc(db, "users", userId, "players", data.data.playerId, "seasons", data.data.seasonId, "matches", data.id);
        await updateDoc(updateMatchRef, data.data);
        break;
      case 'deleteMatch':
        const deleteMatchRef = doc(db, "users", userId, "players", data.data.playerId, "seasons", data.data.seasonId, "matches", data.id);
        await deleteDoc(deleteMatchRef);
        break;
      case 'savePlayer':
        const playerRef = collection(db, "users", userId, "players");
        await addDoc(playerRef, data);
        break;
      case 'updatePlayer':
        const updatePlayerRef = doc(db, "users", userId, "players", data.id);
        await updateDoc(updatePlayerRef, data.data);
        break;
      case 'deletePlayer':
        const deletePlayerRef = doc(db, "users", userId, "players", data.id);
        await deleteDoc(deletePlayerRef);
        break;
      case 'saveSeason':
        const seasonRef = collection(db, "users", userId, "players", data.playerId, "seasons");
        await addDoc(seasonRef, data);
        break;
      case 'updateSeason':
        const updateSeasonRef = doc(db, "users", userId, "players", data.data.playerId, "seasons", data.id);
        await updateDoc(updateSeasonRef, data.data);
        break;
      case 'deleteSeason':
        const deleteSeasonRef = doc(db, "users", userId, "players", data.data.playerId, "seasons", data.id);
        await deleteDoc(deleteSeasonRef);
        break;
      case 'updateUserPrefs':
        // Settings changes are now local-only, so this is a no-op
        // This allows existing queued settings changes to be cleared
        break;
      case 'createTeam':
        const createTeamRef = collection(db, "admin_teams");
        await addDoc(createTeamRef, data);
        break;
      case 'updateTeam':
        const updateTeamRef = doc(db, "admin_teams", data.id);
        await updateDoc(updateTeamRef, data.data);
        break;
      case 'addTeamPlayer':
        const addTeamPlayerRef = collection(db, "admin_teams", data.teamId, "roster");
        await addDoc(addTeamPlayerRef, data.data);
        break;
      case 'updateTeamPlayer':
        const updateTeamPlayerRef = doc(db, "admin_teams", data.teamId, "roster", data.playerId);
        await updateDoc(updateTeamPlayerRef, data.data);
        break;
      case 'addTeamMatch':
        const addTeamMatchRef = collection(db, "admin_teams", data.teamId, "seasons", data.seasonId, "matches");
        await addDoc(addTeamMatchRef, data.data);
        // Clean up local storage after successful sync
        const teamMatchKey = `vballstats_team_matches_${data.teamId}_${data.seasonId}`;
        localStorage.removeItem(teamMatchKey);
        break;
      case 'updateTeamMatch':
        const updateTeamMatchRef = doc(db, "admin_teams", data.teamId, "seasons", data.seasonId, "matches", data.matchId);
        await updateDoc(updateTeamMatchRef, data.data);
        break;
      case 'deleteTeamMatch':
        const deleteTeamMatchRef = doc(db, "admin_teams", data.teamId, "seasons", data.seasonId, "matches", data.matchId);
        await deleteDoc(deleteTeamMatchRef);
        break;
      case 'saveTournament':
        const tournamentRef = collection(db, "users", userId, "players", data.playerId, "seasons", data.seasonId, "tournaments");
        await addDoc(tournamentRef, data);
        break;
      case 'updateTournament':
        const updateTournamentRef = doc(db, "users", userId, "players", data.data.playerId, "seasons", data.data.seasonId, "tournaments", data.id);
        await updateDoc(updateTournamentRef, data.data);
        break;
      case 'deleteTournament':
        const deleteTournamentRef = doc(db, "users", userId, "players", data.data.playerId, "seasons", data.data.seasonId, "tournaments", data.id);
        await deleteDoc(deleteTournamentRef);
        break;
      default:
        console.warn(`Unknown operation: ${operation}`);
    }
  }
};



/* ---------- UI Stats list (now includes HE, DE, BT, BE, Tip, Half Block) ---------- */
const STAT_FIELDS = [
  { key: "sets", label: "Blocks" },
  { key: "halfBlock", label: "Half Block" }, // NEW - for double blocks (0.5 credit)
  { key: "assists", label: "Assists" },
  { key: "kills", label: "Kills" },
  { key: "hitInPlay", label: "Hit in Play" },
  { key: "hittingErrors", label: "Hitting Errors" }, // NEW
  { key: "tip", label: "Tips" }, // NEW
  { key: "serveAttempts", label: "Serve Attempts" },
  { key: "serviceErrors", label: "Service Errors" },
  { key: "aces", label: "Aces" },
  { key: "digs", label: "Digs" },
  { key: "digErrors", label: "Dig Errors" }, // NEW
  { key: "blockTouch", label: "Block Touch" }, // NEW
  { key: "blockError", label: "Block Error" }, // NEW
  { key: "srTouches", label: "SR Touches" }, // NEW
];

const EMPTY_DATA = {
  playerName: "",
  matchDate: "",
  opponent: "",
  email: "",
  sets: 0,
  halfBlock: 0,       // NEW - for double blocks (0.5 credit each)
  assists: 0,
  kills: 0,
  hitInPlay: 0,
  hittingErrors: 0,   // NEW
  serveAttempts: 0,
  serviceErrors: 0,
  aces: 0,
  digs: 0,
  digErrors: 0,       // NEW
  blockTouch: 0,      // NEW
  blockError: 0,      // NEW
  tip: 0,             // NEW
  srTouches: 0,       // NEW

  // NEW: per-set scores [{us:number|string, opp:number|string}]
  setScores: [],
  
  // NEW: match notes
  notes: "",
  
  // NEW: public sharing fields
  isPublic: false,
  shareToken: null,
  
  // NEW: match media
  matchMedia: [],
};

const STORAGE_KEY = "vball_single_player_stats_v1";

// --- utility functions ---
function generateShareToken() {
  return Math.random().toString(36).substring(2, 8);
}

// --- input hardening helpers ---
const SAFE_TEXT_MAX = 120;

function cleanText(s, max = SAFE_TEXT_MAX) {
  if (typeof s !== "string") return "";
  return s
    .replace(/[\p{Cc}<>\u007F]/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

// Live variant: preserves trailing space while typing (fixes "can’t add a space" UX)
function cleanTextLive(s, max = SAFE_TEXT_MAX) {
  if (typeof s !== "string") return "";
  return s
    .replace(/[\p{Cc}<>\u007F]/gu, "")
    .replace(/[ \t\r\n]+/g, " ")
    .slice(0, max);
}

function toNonNegInt(v, max = 100000) {
  const n = parseInt(v, 10);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(n, max);
}

// NEW: derive set wins/losses from an array of {us,opp}
function getSetWL(sets = []) {
  let w = 0, l = 0;
  for (const s of sets) {
    const us = Number(s?.us || 0);
    const opp = Number(s?.opp || 0);
    if (us > opp) w++;
    else if (opp > us) l++;
  }
  return { w, l };
}

// NEW: stringify set scores for the table/CSV (e.g., "25–18, 19–25, 15–13")
function formatSetScores(sets = []) {
  if (!sets.length) return "—";
  return sets.map((s) => `${Number(s?.us || 0)}–${Number(s?.opp || 0)}`).join(", ");
}

export default function App() {
  /* ----- Auth state ----- */
  const [user, setUser] = useState(null);

  /* ----- Subscription state ----- */
  const { isPro, isCoach, subscriptionEndDate, loading: subscriptionLoading } = useSubscription(user);

  const [showLanding, setShowLanding] = useState(true); // show landing page by default
  const [authMode, setAuthMode] = useState("signin"); // or "signup"
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authName, setAuthName] = useState("");
  const [resetMode, setResetMode] = useState(false); // manual reset form toggle
  const [pendingDeletePlayerId, setPendingDeletePlayerId] = useState(null);
  // NEW: confirm delete match + clear stats modals
  const [pendingDeleteMatchId, setPendingDeleteMatchId] = useState(null);
  const [confirmClearStatsOpen, setConfirmClearStatsOpen] = useState(false);
  const [viewNotesMatch, setViewNotesMatch] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesEditText, setNotesEditText] = useState("");
  const [showOfflineDeleteModal, setShowOfflineDeleteModal] = useState(false);
  // Info modal for Match History abbreviations & formulas
  const [showStatsInfo, setShowStatsInfo] = useState(false);
  // Share success modal
  const [showShareModal, setShowShareModal] = useState(false);
  // Match save success modal
  const [showMatchSaveModal, setShowMatchSaveModal] = useState(false);
  const [wasOfflineMatchSave, setWasOfflineMatchSave] = useState(false);
  // Toggle for floating set score widget
  const [isFloatingWidgetExpanded, setIsFloatingWidgetExpanded] = useState(false);
  // Track which sets are collapsed in the floating widget
  const [collapsedSets, setCollapsedSets] = useState(new Set());
  // Track scoreboard button position
  const [scoreboardButtonY, setScoreboardButtonY] = useState(50); // percentage from top
  const [isDraggingScoreboard, setIsDraggingScoreboard] = useState(false);
  const [dragStartY, setDragStartY] = useState(null);
  // Active stats tab for Record Match view
  const [activeStatsTab, setActiveStatsTab] = useState("offense");
  // Match Notes collapse state
  const [isMatchNotesExpanded, setIsMatchNotesExpanded] = useState(false);
  // Set Scores collapse state
  const [isSetScoresExpanded, setIsSetScoresExpanded] = useState(false);
  // Player & Season collapse state
  const [isPlayerSeasonExpanded, setIsPlayerSeasonExpanded] = useState(false);
  // Match Details collapse state
  const [isMatchDetailsExpanded, setIsMatchDetailsExpanded] = useState(false);
  const [isMatchMediaExpanded, setIsMatchMediaExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxImages, setLightboxImages] = useState([]);
  // Account form state
  const [accountForm, setAccountForm] = useState({ name: "" });
  const [accountLoading, setAccountLoading] = useState(false);
  const [initialUserDataLoaded, setInitialUserDataLoaded] = useState(false);

  // Upgrade modal state
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeModalFeature, setUpgradeModalFeature] = useState('player');

  // NEW: edit state for matches
  const [editingMatchId, setEditingMatchId] = useState(null);
  const [originalPlayerId, setOriginalPlayerId] = useState(null);
  const [originalSeasonId, setOriginalSeasonId] = useState(null);

  // Position key modal (Create Player only)
  const [showPosInfo, setShowPosInfo] = useState(false);

  // Offline functionality state
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isTestingOffline, setIsTestingOffline] = useState(false);
  const [offlineDataCount, setOfflineDataCount] = useState(0);
  const [showOfflineInfo, setShowOfflineInfo] = useState(false);
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [pendingChangesError, setPendingChangesError] = useState("");
  
  // Track previous user to detect actual user changes vs offline mode changes
  const prevUserRef = useRef(null);

  // Onboarding state
  const { isMobile } = useViewport();
  const [runOnboarding, setRunOnboarding] = useState(false);
  const [showMobileOnboarding, setShowMobileOnboarding] = useState(false);

  // Inline banners (auth)
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");

  // Global notice (app-wide)
  const [notice, setNotice] = useState(null); // {type:'success'|'error'|'info', text:string}
  const noticeTimer = useRef(null);
  function showNotice(type, text) {
    setNotice({ type, text });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 4000);
  }
  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  // Initialize image cache cleanup
  useEffect(() => {
    // Clean up expired cache entries on app start
    if (typeof imageCache !== 'undefined') {
      imageCache.cleanup();
    }
  }, []);

  // Shake + autofocus on sign-in password errors
  const pwInputRef = useRef(null);
  const [shakePw, setShakePw] = useState(false);

  // In-app password reset link parsing
  const [resetLink, setResetLink] = useState({
    mode: null,
    oobCode: null,
    email: null,
    verifying: false,
  });
  const newPwRef = useRef(null);
  const [resetPageError, setResetPageError] = useState("");
  const [resetPageSuccess, setResetPageSuccess] = useState("");

  // Multi-player + seasons
  const [players, setPlayers] = useState([]); // [{id, name, number, team}]
  const [activePlayerId, setActivePlayerId] = useState(null);
  const [seasons, setSeasons] = useState([]); // seasons for active player
  const [activeSeasonId, setActiveSeasonId] = useState(null);

  // Memoize the shared player object to prevent infinite re-renders in SharedPlayerStatsView
  // Use JSON.stringify to create a stable dependency that only changes when the actual data changes
  const sharedPlayersStr = useMemo(() =>
    JSON.stringify(user?.sharedPlayers || []),
    [user?.sharedPlayers]
  );

  const currentSharedPlayer = useMemo(() => {
    if (!user?.sharedPlayers || !activePlayerId) return null;
    return user.sharedPlayers.find(p => p.playerId === activePlayerId);
  }, [sharedPlayersStr, activePlayerId]);
  
  // Tournaments (feature flag controlled)
  const [tournaments, setTournaments] = useState([]); // tournaments for active season
  const [activeTournamentId, setActiveTournamentId] = useState(null);
  const [tournamentForm, setTournamentForm] = useState({ name: "", date: "", location: "" });
  const [showTournaments, setShowTournaments] = useState(false);
  const [tournamentLoading, setTournamentLoading] = useState(false);
  const [pendingDeleteTournamentId, setPendingDeleteTournamentId] = useState(null);

  // Season stats section collapse states
  const [showOffensiveStats, setShowOffensiveStats] = useState(false);
  const [showServingStats, setShowServingStats] = useState(false);
  const [showDefensiveStats, setShowDefensiveStats] = useState(false);

  // Tournament match expansion state
  const [expandedTournamentMatch, setExpandedTournamentMatch] = useState(null);
  
  // Sync status tracking
  const [syncStatus, setSyncStatus] = useState({
    isOnline: navigator.onLine,
    pendingCount: 0,
    lastSync: null,
    isSyncing: false,
    syncError: null
  });

  // Notification badges for shared players
  const [sharedPlayerNotifications, setSharedPlayerNotifications] = useState({});

  // Update sync status
  const updateSyncStatus = useCallback((updates) => {
    setSyncStatus(prev => ({ ...prev, ...updates }));
  }, []);

  // Count pending operations
  const updatePendingCount = useCallback(() => {
    const pendingOps = OfflineManager.getPendingOperations();
    updateSyncStatus({ pendingCount: pendingOps.length });
  }, [updateSyncStatus]);

  // Online/offline status tracking
  useEffect(() => {
    const handleOnline = () => {
      updateSyncStatus({ isOnline: true, syncError: null });
      updatePendingCount();
    };
    
    const handleOffline = () => {
      updateSyncStatus({ isOnline: false });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    // Initial pending count
    updatePendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateSyncStatus, updatePendingCount]);
  const [editingTournament, setEditingTournament] = useState(null);
  const [tournamentEditForm, setTournamentEditForm] = useState({
    name: "",
    location: "",
    startDate: "",
    endDate: "",
    notes: ""
  });

  // Views:
  // 'dashboard' | 'record' | 'player-setup' | 'season-detail' | 'season-setup' | 'season-edit' | 'tournament-create' | 'tournament-edit' | 'tournament-detail' | 'match-history' | 'teams' | 'team-setup' | 'team-dashboard'
          const [view, setViewState] = useState(() => {
    // Initialize view from URL path to avoid unwanted redirects
    const path = window.location.pathname;
    if (path === '/' || path === '') return 'landing';
    if (path === '/dashboard') return 'dashboard';
    if (path === '/plans') return 'pricing';
    // For special routes handled outside the main app flow, return null to skip view navigation
    if (path === '/team-player-reports' || path.match(/^\/match\/[a-zA-Z0-9]+$/)) return null;
    // For other paths, let useViewNavigation handle it
    return 'dashboard';
  });
        const [trendsSeasonId, setTrendsSeasonId] = useState(null);
        const [trendsPlayerId, setTrendsPlayerId] = useState(null);
  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  // Wrapper for setView that scrolls to top
  const setView = useCallback((newView) => {
    setViewState(newView);
    window.scrollTo(0, 0);
    // Refresh team dashboard when navigating to it
    if (newView === 'team-dashboard') {
      setDashboardRefreshKey(prev => prev + 1);
    }
  }, []);

  // Team management state
  const [teams, setTeams] = useState(undefined); // Start as undefined to distinguish "not loaded" from "loaded but empty"
  const [teamSeasons, setTeamSeasons] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(null);

  // Sync view state with URL (enables code splitting and better performance)
  // Now includes player/team IDs for fully qualified URLs like /player/:id or /team/:id
  useViewNavigation(view, setViewState, activePlayerId, activeSeasonId, activeTeamId);
  const [activeTeamSeason, setActiveTeamSeason] = useState(null);
  const [activeTeamTournament, setActiveTeamTournament] = useState(null);
  const [activeLineupId, setActiveLineupId] = useState(null);
  const [teamTournamentAutoOpenForm, setTeamTournamentAutoOpenForm] = useState(false);
  const [activeRosterPlayerId, setActiveRosterPlayerId] = useState(null);
  const [showAddPlayerCodeModal, setShowAddPlayerCodeModal] = useState(false);
  const [playerCode, setPlayerCode] = useState('');
  const [editingMatch, setEditingMatch] = useState(null);
  const [rosterAutoOpenForm, setRosterAutoOpenForm] = useState(false);
  const [seasonDetailInitialTab, setSeasonDetailInitialTab] = useState('history');

  // Reset auto-open form flag when leaving tournaments view
  useEffect(() => {
    if (view !== "team-tournaments") {
      setTeamTournamentAutoOpenForm(false);
    }
  }, [view]);

  // Reset auto-open form flag when leaving roster view
  useEffect(() => {
    if (view !== "team-roster") {
      setRosterAutoOpenForm(false);
    }
  }, [view]);

  // Clear active roster player when leaving team-roster view
  useEffect(() => {
    if (view !== "team-roster") {
      setActiveRosterPlayerId(null);
    }
  }, [view]);

  // One-time migration: Add share codes to existing players (Coach only)
  useEffect(() => {
    const runMigration = async () => {
      // Only run for coaches and only once
      const migrationKey = 'playerCodeMigrationComplete';
      if (user && teams?.length > 0 && !localStorage.getItem(migrationKey)) {
        try {
          const { migratePlayerShareCodes } = await import('./services/playerCodeService');
          await migratePlayerShareCodes();
          localStorage.setItem(migrationKey, 'true');
        } catch (error) {
          console.error('[Migration] ❌ Failed:', error);
        }
      }
    };
    runMigration();
  }, [user, teams]);

  // GA: send a virtual page view whenever the in-app "view" changes
  useEffect(() => {
    if (!window.gtag) return; // GA not loaded yet
    const page_path = `/${view}`;
    window.gtag("event", "page_view", {
      page_title: `VBall Stats — ${view}`,
      page_path,
    });
  }, [view]);

  const [playerEditMode, setPlayerEditMode] = useState("create"); // 'create' | 'edit'

  useEffect(() => {
    let userDocUnsub = null;

    const authUnsub = onAuthStateChanged(auth, async (u) => {
      // Clean up previous user document listener if exists
      if (userDocUnsub) {
        userDocUnsub();
        userDocUnsub = null;
      }

      if (u) {
        // Set up real-time listener for user document to get sharedPlayers and other data
        try {
          const userDocRef = doc(db, 'users', u.uid);

          userDocUnsub = onSnapshot(userDocRef, (userDocSnap) => {
            if (userDocSnap.exists()) {
              // Merge Firebase Auth user with Firestore user data
              const firestoreData = userDocSnap.data();
              setUser((prevUser) => {
                // Only update if data actually changed to prevent unnecessary re-renders
                const newUser = {
                  ...(prevUser || u),
                  ...firestoreData,
                  uid: u.uid, // Ensure uid from auth is preserved
                  email: u.email // Ensure email from auth is preserved
                };

                // Deep equality check for sharedPlayers array to prevent remounts
                const prevSharedPlayers = JSON.stringify(prevUser?.sharedPlayers || []);
                const newSharedPlayers = JSON.stringify(newUser.sharedPlayers || []);

                if (prevSharedPlayers === newSharedPlayers && prevUser) {
                  // Data hasn't changed, return previous user object to maintain reference
                  return prevUser;
                }

                return newUser;
              });
            } else {
              // User document doesn't exist yet, just use auth user
              setUser(u);
            }
            setInitialUserDataLoaded(true);
          }, (error) => {
            console.error('Error loading user document:', error);
            setUser(u); // Fallback to auth user only
            setInitialUserDataLoaded(true);
          });
        } catch (error) {
          console.error('Error setting up user document listener:', error);
          setUser(u); // Fallback to auth user only
          setInitialUserDataLoaded(true);
        }
      } else {
        setUser(null);
        setInitialUserDataLoaded(true);
      }
    });

    return () => {
      authUnsub();
      if (userDocUnsub) {
        userDocUnsub();
      }
    };
  }, []);

  // Check for signup URL parameter or /signup path
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;

    if (urlParams.get('signup') === 'true' || pathname === '/signup') {
      setShowLanding(false);
      setAuthMode('signup');

      // Clean up URL but preserve invite parameter if present
      const invite = urlParams.get('invite');
      const newUrl = invite ? `/?invite=${invite}` : '/';
      window.history.replaceState({}, document.title, newUrl);
    }
  }, []);

  // Auto-verify subscription after successful checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkoutSuccess = params.get('checkout') === 'success';

    if (checkoutSuccess && user && user.email) {
      // Remove the checkout param from URL
      window.history.replaceState({}, '', window.location.pathname);

      // Verify and activate subscription
      const verifySubscription = async () => {
        try {
          showNotice('info', 'Verifying your subscription...');
          const result = await verifyAndActivateSubscription(user.email, user.uid);

          if (result.success) {
            showNotice('success', '🎉 Welcome to Pro! Your subscription is now active.');
            // Refresh will show Pro features
            setTimeout(() => window.location.reload(), 2000);
          } else {
            showNotice('error', result.message || 'Could not verify subscription. Please contact support.');
          }
        } catch (error) {
          console.error('Error verifying subscription:', error);
          showNotice('error', 'Could not verify subscription. Please refresh the page or contact support.');
        }
      };

      verifySubscription();
    }
  }, [user]);

  // Offline functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Don't auto-sync or refresh data - let users stay in their current view
      // and manually sync when they want to
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Override online status for testing
    const actualOnline = isTestingOffline ? false : navigator.onLine;
    setIsOnline(actualOnline);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isTestingOffline, user, activePlayerId]);

  // Update offline data count
  useEffect(() => {
    const updateOfflineCount = () => {
      const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
      setOfflineDataCount(queue.length);
    };

    updateOfflineCount();
    const interval = setInterval(updateOfflineCount, 1000);
    return () => clearInterval(interval);
  }, []);

  const refreshPlayers = useCallback(async () => {
    if (!user) return [];

    // If offline, load from local storage
    if (!isOnline) {
      const localPlayers = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.PLAYERS) || [];
      const items = localPlayers.filter(p => p.userId === user.uid);
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setPlayers(items);

      // Load all seasons for sidebar (filter out seasons without playerId)
      const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SEASONS) || [];
      const userSeasons = localSeasons.filter(s => s.userId === user.uid && s.playerId);
      setAllSeasons(userSeasons);

      return items;
    }

    // If online, load from Firebase and cache locally
    try {
      const ref = collection(db, "users", user.uid, "players");
      const snap = await getDocs(ref);
      const items = snap.docs.map((d) => ({ id: d.id, userId: user.uid, ...d.data() }));
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setPlayers(items);

      // Cache to local storage (players are global per user, so this is fine to overwrite)
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.PLAYERS, items);

      // Load all seasons for sidebar from cache (they get cached as players load their seasons)
      const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SEASONS) || [];
      const userSeasons = localSeasons.filter(s => s.userId === user.uid);
      setAllSeasons(userSeasons);

      return items;
    } catch (error) {
      console.error('Failed to load players from Firebase:', error);
      // Fallback to local storage
      const localPlayers = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.PLAYERS) || [];
      const items = localPlayers.filter(p => p.userId === user.uid);
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setPlayers(items);

      // Load all seasons for sidebar (filter out seasons without playerId)
      const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SEASONS) || [];
      const userSeasons = localSeasons.filter(s => s.userId === user.uid && s.playerId);
      setAllSeasons(userSeasons);

      return items;
    }
  }, [user, isOnline]);

  const refreshTeams = useCallback(async () => {
    if (!user || !isCoach) {
      setTeams([]);
      return [];
    }

    // If offline, load from local storage
    if (!isOnline) {
      const localTeams = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.ADMIN_TEAMS) || [];
      const items = localTeams.filter(t => t.ownerUid === user.uid);
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTeams(items);
      return items;
    }

    // If online, load from Firebase and cache locally
    try {
      const ref = collection(db, "admin_teams");
      const snap = await getDocs(ref);
      const items = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(t => t.ownerUid === user.uid);
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTeams(items);

      // Cache to local storage
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.ADMIN_TEAMS, items);

      return items;
    } catch (error) {
      console.error('Failed to load teams from Firebase:', error);
      // Fallback to local storage
      const localTeams = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.ADMIN_TEAMS) || [];
      const items = localTeams.filter(t => t.ownerUid === user.uid);
      items.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTeams(items);
      return items;
    }
  }, [user, isCoach, isOnline]);

  const refreshTeamSeasons = useCallback(async () => {
    if (!user || !isCoach) {
      setTeamSeasons([]);
      return [];
    }

    // If offline, load from local storage
    if (!isOnline) {
      const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.ADMIN_TEAM_SEASONS) || [];
      setTeamSeasons(localSeasons);
      return localSeasons;
    }

    // If online, load from Firebase (seasons are subcollections under each team)
    try {
      // First get all teams
      const teamsRef = collection(db, "admin_teams");
      const teamsSnap = await getDocs(teamsRef);
      const userTeams = teamsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(t => t.ownerUid === user.uid);

      // Then get seasons for each team
      const allSeasons = [];
      for (const team of userTeams) {
        const seasonsRef = collection(db, "admin_teams", team.id, "seasons");
        const seasonsSnap = await getDocs(seasonsRef);
        const teamSeasons = seasonsSnap.docs.map(d => ({
          id: d.id,
          teamId: team.id, // Add teamId to link season to team
          ...d.data()
        }));
        allSeasons.push(...teamSeasons);
      }

      allSeasons.sort((a, b) => (b.year || 0) - (a.year || 0));
      setTeamSeasons(allSeasons);

      // Cache to local storage
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.ADMIN_TEAM_SEASONS, allSeasons);

      return allSeasons;
    } catch (error) {
      console.error('Failed to load team seasons from Firebase:', error);
      // Fallback to local storage
      const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.ADMIN_TEAM_SEASONS) || [];
      setTeamSeasons(localSeasons);
      return localSeasons;
    }
  }, [user, isCoach, isOnline]);

  // Auto-refresh team seasons only when initially loading teams view, not on every view change
  useEffect(() => {
    if (isCoach && view === 'teams') {
      refreshTeamSeasons();
    }
  }, [isCoach, refreshTeamSeasons]); // Removed 'view' dependency to prevent refresh on every navigation

  const refreshSeasons = useCallback(
    async (playerId) => {
      if (!user || !playerId) {
        setSeasons([]);
        return [];
      }

      // If offline, load from local storage
      if (!isOnline) {
        const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SEASONS) || [];
        const items = localSeasons.filter(s => s.userId === user.uid && s.playerId === playerId);
        items.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
        setSeasons(items);

        // Update all seasons for sidebar (filter out seasons without playerId)
        const userSeasons = localSeasons.filter(s => s.userId === user.uid && s.playerId);
        setAllSeasons(userSeasons);

        return items;
      }

      // If online, load from Firebase and cache locally
      try {
        const ref = collection(db, "users", user.uid, "players", playerId, "seasons");
        const snap = await getDocs(ref);
        const items = snap.docs.map((d) => ({ id: d.id, userId: user.uid, playerId, ...d.data() }));
        items.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
        setSeasons(items);

        // Merge into local storage cache instead of overwriting
        const existingSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SEASONS) || [];
        const otherPlayersSeasons = existingSeasons.filter(s => s.userId === user.uid && s.playerId !== playerId);
        const allSeasonsData = [...otherPlayersSeasons, ...items];
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.SEASONS, allSeasonsData);

        // Update all seasons for sidebar
        setAllSeasons(allSeasonsData);

        return items;
      } catch (error) {
        console.error('Failed to load seasons from Firebase:', error);
        // Fallback to local storage
        const localSeasons = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SEASONS) || [];
        const items = localSeasons.filter(s => s.userId === user.uid && s.playerId === playerId);
        items.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
        setSeasons(items);

        // Update all seasons for sidebar (filter out seasons without playerId)
        const userSeasons = localSeasons.filter(s => s.userId === user.uid && s.playerId);
        setAllSeasons(userSeasons);

        return items;
      }
    },
    [user, isOnline]
  );

  // Refresh seasons when active player changes
  useEffect(() => {
    if (activePlayerId && user) {
      refreshSeasons(activePlayerId);
    }
  }, [activePlayerId, user, refreshSeasons]);

  // Handle player change from secondary nav
  useEffect(() => {
    const handlePlayerChange = async (event) => {
      const playerId = event.detail;
      setActivePlayerId(playerId);

      // Load seasons for the new player
      if (playerId) {
        const updatedSeasons = await refreshSeasons(playerId);
        const newSeasonId = updatedSeasons[0]?.id || null;
        setActiveSeasonId(newSeasonId);

        // Update user document
        if (user) {
          await setDoc(
            doc(db, "users", user.uid),
            {
              activePlayerId: playerId,
              activeSeasonId: newSeasonId
            },
            { merge: true }
          );
        }

        // Navigate to player dashboard
        setView('player-dashboard');
      }
    };

    window.addEventListener('playerChange', handlePlayerChange);
    return () => window.removeEventListener('playerChange', handlePlayerChange);
  }, [user, refreshSeasons, setView]);

  // Refresh tournaments for active season
  const refreshTournaments = useCallback(
    async (seasonId) => {
      if (!user || !seasonId || !FEATURES.TOURNAMENTS) {
        setTournaments([]);
        return [];
      }
      
      // If offline, load from local storage
      if (!isOnline) {
        const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const items = localTournaments.filter(t => t.userId === user.uid && t.seasonId === seasonId);
        items.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
        setTournaments(items);
        return items;
      }
      
      // If online, load from Firebase and cache locally
      try {
        const ref = collection(db, "users", user.uid, "players", activePlayerId, "seasons", seasonId, "tournaments");
        const snap = await getDocs(ref);
        const items = snap.docs.map((d) => ({ 
          id: d.id, 
          userId: user.uid, 
          playerId: activePlayerId, 
          seasonId, 
          ...d.data() 
        }));
        items.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
        setTournaments(items);
        
        // Merge into local storage cache instead of overwriting
        const existingTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const otherSeasonsTournaments = existingTournaments.filter(t => t.userId === user.uid && t.seasonId !== seasonId);
        const allTournaments = [...otherSeasonsTournaments, ...items];
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, allTournaments);
        return items;
      } catch (error) {
        console.error('Failed to load tournaments from Firebase:', error);
        // Fallback to local storage
        const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const items = localTournaments.filter(t => t.userId === user.uid && t.seasonId === seasonId);
        items.sort((a, b) => (b.createdAtISO || "").localeCompare(a.createdAtISO || ""));
        setTournaments(items);
        return items;
      }
    },
    [user, isOnline, activePlayerId]
  );

  // Load user preferences (active player/season) and decide initial view
  const loadUserData = useCallback(async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    let activeP = null;
    let activeS = null;

    // Check local preferences first (for offline mode changes)
    const localPrefs = OfflineManager.loadFromLocal('vballstats_user_prefs') || {};
    if (localPrefs.activePlayerId) {
      activeP = localPrefs.activePlayerId;
      activeS = localPrefs.activeSeasonId || null;
    } else if (userSnap.exists()) {
      // Fall back to Firebase preferences if no local preferences
      const d = userSnap.data();
      activeP = d?.activePlayerId || null;
      activeS = d?.activeSeasonId || null;
    }

    const pList = await refreshPlayers();
    if (!activeP) activeP = pList[0]?.id || null;
    setActivePlayerId(activeP);

    const sList = await refreshSeasons(activeP);
    if (!activeS || !sList.find((s) => s.id === activeS)) activeS = sList[0]?.id || null;
    setActiveSeasonId(activeS);

    // Load teams for coaches
    // Note: Team loading is also handled by a separate useEffect that watches isCoach
    // to handle race conditions with subscription loading
    if (isCoach) {
      await refreshTeams();
      await refreshTeamSeasons();
    }

    // Update Firebase with current preferences
    // Only update if user document exists (don't create it here)
    if (userSnap.exists()) {
      await setDoc(
        userRef,
        { activePlayerId: activeP || null, activeSeasonId: activeS || null },
        { merge: true }
      );
    }

    // Clear local preferences after syncing to Firebase
    if (localPrefs.activePlayerId) {
      OfflineManager.saveToLocal('vballstats_user_prefs', {});
    }
  }, [user, refreshPlayers, refreshSeasons, refreshTeams, refreshTeamSeasons, isCoach, subscriptionLoading]);


  // Load account data when user changes
  const loadAccountData = useCallback(async () => {
    if (!user) return;
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);
    if (userSnap.exists()) {
      const data = userSnap.data();
      setAccountForm({
        name: data?.name || "",
        email: data?.email || user.email || ""
      });
    } else {
      setAccountForm({
        name: "",
        email: user.email || ""
      });
    }
  }, [user]);


  // Save account data
  const saveAccountData = async (e) => {
    e?.preventDefault?.();
    if (!user) return;

    setAccountLoading(true);
    try {
      const userRef = doc(db, "users", user.uid);
      const updates = {
        name: cleanText(accountForm.name, 80)
      };
      
      await setDoc(userRef, updates, { merge: true });
      showNotice("success", "Account updated successfully.");
    } catch (err) {
      console.error("Account update error:", err);
      showNotice("error", "Could not update account.");
    } finally {
      setAccountLoading(false);
    }
  };

  useEffect(() => {
    const userChanged = prevUserRef.current !== user?.uid;
    prevUserRef.current = user?.uid;

    if (user) {
      // Only load user data if the user UID actually changed
      // This prevents reloading when just the user object updates (e.g., lastViewedAt)
      if (userChanged) {
        loadUserData();
        loadAccountData();
        setView("dashboard");
      }
    } else if (userChanged) {
      // Only clear data and redirect when user actually logged out
      // Don't change view on initial load when user is null
      setPlayers([]);
      setActivePlayerId(null);
      setSeasons([]);
      setActiveSeasonId(null);
      setView("landing");
    }
  }, [user, loadUserData, loadAccountData, setView]);

  // Load teams when user becomes a coach (handles race condition with subscription loading)
  useEffect(() => {
    async function loadTeamsWhenCoach() {
      if (user && isCoach && !subscriptionLoading) {
        await refreshTeams();
        await refreshTeamSeasons();
      }
    }
    loadTeamsWhenCoach();
  }, [user, isCoach, subscriptionLoading, refreshTeams, refreshTeamSeasons]);

  // Check for new stats notifications on shared players
  useEffect(() => {
    async function checkForNewStats() {
      if (!user || !user.sharedPlayers || user.sharedPlayers.length === 0) {
        setSharedPlayerNotifications({});
        return;
      }

      try {
        const { checkAllSharedPlayersForNewStats } = await import('./services/notificationService');
        const notifications = await checkAllSharedPlayersForNewStats(user.sharedPlayers);
        setSharedPlayerNotifications(notifications);
      } catch (error) {
        console.error('Error checking for new stats:', error);
      }
    }

    checkForNewStats();
  }, [user]);

  // Update lastViewedAt when viewing a shared player's stats
  useEffect(() => {
    async function updateViewed() {
      if (view === 'shared-player-stats' && activePlayerId && user) {
        try {
          const { updateLastViewed } = await import('./services/notificationService');
          await updateLastViewed(user.uid, activePlayerId);

          // Remove the notification badge for this player
          setSharedPlayerNotifications(prev => ({
            ...prev,
            [activePlayerId]: false
          }));
        } catch (error) {
          console.error('Error updating last viewed:', error);
        }
      }
    }

    updateViewed();
    // Note: 'user' is intentionally omitted from dependencies to prevent infinite loop
    // when lastViewedAt is updated in Firestore and triggers onSnapshot listener
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, activePlayerId]);

  // Check if user needs onboarding
  useEffect(() => {
    async function checkOnboarding() {
      if (!user) return;

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const userData = userSnap.data();
        const needsOnboarding = userData.onboardingCompleted === false;

        if (needsOnboarding && view === 'dashboard') {
          if (isMobile) {
            setShowMobileOnboarding(true);
          } else {
            // Delay to ensure dashboard is fully rendered
            setTimeout(() => {
              const intro = introJs.tour();
              intro.setOptions({
                ...introOptions,
                steps: getIntroSteps()
              });

              intro.oncomplete(() => {
                handleOnboardingComplete();
              });

              intro.onexit(() => {
                handleOnboardingSkip();
              });

              intro.start();
            }, 1500);
          }
        }
      }
    }

    checkOnboarding();
  }, [user, view, isMobile]);

  // Handle onboarding completion
  const handleOnboardingComplete = async () => {
    if (!user) return;

    try {
      await updateDoc(doc(db, "users", user.uid), {
        onboardingCompleted: true,
        onboardingCompletedAt: new Date(),
        onboardingDevice: isMobile ? 'mobile' : 'desktop'
      });

      setRunOnboarding(false);
      setShowMobileOnboarding(false);
    } catch (err) {
      console.error('Error saving onboarding completion:', err);
    }
  };

  const handleOnboardingSkip = async () => {
    if (!user) return;

    try {
      await updateDoc(doc(db, "users", user.uid), {
        onboardingCompleted: true,
        onboardingSkipped: true,
        onboardingCompletedAt: new Date(),
        onboardingDevice: isMobile ? 'mobile' : 'desktop'
      });

      setRunOnboarding(false);
      setShowMobileOnboarding(false);
    } catch (err) {
      console.error('Error saving onboarding skip:', err);
    }
  };

  // Detect if opened with a password reset link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get("mode");
    const oobCode = params.get("oobCode");
    if (mode === "resetPassword" && oobCode) {
      setResetLink((s) => ({ ...s, verifying: true }));
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setResetLink({ mode, oobCode, email, verifying: false });
          setTimeout(() => newPwRef.current?.focus(), 0);
        })
        .catch((err) => {
          setResetPageError(err?.message || "Invalid or expired reset link.");
          clearAuthParamsFromUrl();
          setResetLink({ mode: null, oobCode: null, email: null, verifying: false });
        });
    }
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    try {
      await signInWithEmailAndPassword(auth, authEmail, authPassword);
      setAuthEmail("");
      setAuthPassword("");
    } catch (err) {
      const code = err?.code || "";
      let msg = "Sign in failed. Please try again.";
      if (code === "auth/wrong-password" || code === "auth/invalid-credential") {
        msg = "Incorrect email or password. Try again, or use “Forgot password?”.";
      } else if (code === "auth/user-not-found") {
        msg = "No account found for that email.";
      } else if (code === "auth/too-many-requests") {
        msg = "Too many attempts. Try again later or reset your password.";
      } else if (err?.message) {
        msg = err.message;
      }
      setAuthError(msg);
      setShakePw(true);
      pwInputRef.current?.focus();
      setTimeout(() => setShakePw(false), 450);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, authEmail, authPassword);
      const user = userCredential.user;

      // Get UTM parameters from localStorage (captured on landing page)
      const utmData = getUTMParameters();

      // Save user profile data to Firestore
      await setDoc(doc(db, "users", user.uid), {
        name: cleanText(authName, 80),
        email: cleanText(authEmail, 254),
        activePlayerId: null,
        activeSeasonId: null,
        onboardingCompleted: false, // New users need onboarding
        createdAt: new Date(),
        // Save acquisition source for analytics
        ...(utmData && {
          acquisitionSource: {
            source: utmData.source,
            medium: utmData.medium,
            campaign: utmData.campaign,
            term: utmData.term,
            content: utmData.content,
            landingPage: utmData.landingPage,
            signupTimestamp: new Date()
          }
        })
      });

      // Fire Facebook Pixel conversion tracking
      if (window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'VBallStat Account Creation',
          status: 'completed',
          value: 0, // Set to customer lifetime value if known
          currency: 'USD'
        });
      }

      // Fire Google Ads conversion tracking
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-861052396/Um9WCKCUstEbEOy7ypoD',
          'transaction_id': user.uid
        });

        // Also track as GA4 event
        window.gtag('event', 'sign_up', {
          method: 'email',
          user_id: user.uid,
          ...(utmData && {
            utm_source: utmData.source,
            utm_medium: utmData.medium,
            utm_campaign: utmData.campaign
          })
        });
      }

      setAuthEmail("");
      setAuthPassword("");
      setAuthName("");
      setAuthSuccess("Account created. Add a player to get started.");
      setView("dashboard");
    } catch (err) {
      const code = err?.code || "";
      let msg = "Sign up failed.";
      if (code === "auth/email-already-in-use") msg = "That email is already in use.";
      else if (code === "auth/invalid-email") msg = "Please enter a valid email.";
      else if (code === "auth/weak-password") msg = "Password is too weak (min 6 chars).";
      else if (err?.message) msg = err.message;
      setAuthError(msg);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setView("dashboard");
  };

  const handleAddPlayerByCode = async () => {
    if (!playerCode.trim()) {
      showNotice('error', 'Please enter a player code');
      return;
    }

    try {
      const { addPlayerByCode } = await import('./services/playerCodeService');
      const result = await addPlayerByCode(playerCode.trim().toUpperCase(), user.uid);

      showNotice('success', `Success! You now have access to ${result.playerName}'s stats.`);
      setShowAddPlayerCodeModal(false);
      setPlayerCode('');

      // The real-time listener will automatically update the user data with the new shared player
    } catch (error) {
      console.error('Error adding player by code:', error);
      showNotice('error', error.message || 'Failed to add player. Please check the code and try again.');
    }
  };

  // Send reset email that points back to YOUR app
  async function handlePasswordReset(e) {
    e.preventDefault();
    setAuthError("");
    setAuthSuccess("");
    if (!authEmail) {
      setAuthError("Enter your email above, then click ‘Send reset link’.");
      return;
    }
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/?from=reset`,
        handleCodeInApp: true,
      };
      await sendPasswordResetEmail(auth, authEmail, actionCodeSettings);
      setAuthSuccess("Reset link sent. Check your inbox — and your spam/junk folder.");
    } catch (err) {
      const msg = err?.message || "Could not send reset link.";
      setAuthError(msg);
    }
  }

  // Complete the in-app reset
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  async function submitNewPassword(e) {
    e.preventDefault();
    setResetPageError("");
    setResetPageSuccess("");
    if (!resetLink?.oobCode) return;
    if (newPw.length < 6) {
      setResetPageError("Password must be at least 6 characters.");
      newPwRef.current?.focus();
      return;
    }
    if (newPw !== newPw2) {
      setResetPageError("Passwords do not match.");
      newPwRef.current?.focus();
      return;
    }
    try {
      await confirmPasswordReset(auth, resetLink.oobCode, newPw);
      clearAuthParamsFromUrl();
      setResetLink({ mode: null, oobCode: null, email: null, verifying: false });
      setAuthSuccess("Password updated. You can now sign in.");
      setNewPw("");
      setNewPw2("");
    } catch (err) {
      setResetPageError(err?.message || "Unable to reset password.");
    }
  }

  function clearAuthParamsFromUrl() {
    const url = new URL(window.location.href);
    ["mode", "oobCode", "apiKey", "lang", "continueUrl", "from"].forEach((k) =>
      url.searchParams.delete(k)
    );
    window.history.replaceState({}, "", url.toString());
  }

  function switchAuthMode(next) {
    setAuthMode(next);
    setResetMode(false);
    setAuthError("");
    setAuthName(""); // Clear name field when switching modes
  }

  /* ----- Form state (local) ----- */
  const [data, setData] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : { ...EMPTY_DATA };
    } catch {
      return { ...EMPTY_DATA };
    }
  });
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch {}
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [data]);

  const onOpponentChange = (e) => {
    const val = e.target.value;
    setData((d) => ({ ...d, opponent: cleanTextLive(val, 60) })); // preserves trailing space while typing
  };

  // Sync form Player Name from selected player
  useEffect(() => {
    const p = players.find((pl) => pl.id === activePlayerId);
    if (p?.name) setData((d) => ({ ...d, playerName: p.name }));
  }, [players, activePlayerId]);

  const inc = (key) => setData((d) => ({ ...d, [key]: (d[key] || 0) + 1 }));
  const dec = (key) =>
    setData((d) => ({ ...d, [key]: Math.max(0, (d[key] || 0) - 1) }));
  const onField = (key) => (val) => setData((d) => ({ ...d, [key]: val }));

  // NEW: set score handlers (allow empty-string during typing)
  function addSetScore() {
    setData((d) => {
      const currentSets = d.setScores || [];
      const newSets = [...currentSets, { us: 0, opp: 0 }];

      // Auto-collapse the previous set (if it exists)
      const newCollapsedSets = new Set(collapsedSets);
      if (currentSets.length > 0) {
        // Collapse the last set (the one we were just editing)
        newCollapsedSets.add(currentSets.length - 1);
      }
      setCollapsedSets(newCollapsedSets);

      return {
      ...d,
        setScores: newSets,
      };
    });
  }
  function updateSetScore(index, side, value) {
    setData((d) => {
      const next = [...(d.setScores || [])];
      const v = value === "" ? "" : toNonNegInt(value, 99);
      next[index] = { ...next[index], [side]: v };
      return { ...d, setScores: next };
    });
  }
  // function clearSetScore(index, side) {
  //   setData((d) => {
  //     const next = [...(d.setScores || [])];
  //     next[index] = { ...next[index], [side]: "" };
  //     return { ...d, setScores: next };
  //   });
  // }
  function removeSetScore(index) {
    setData((d) => {
      const next = [...(d.setScores || [])];
      next.splice(index, 1);
      return { ...d, setScores: next };
    });
    // Also remove from collapsed sets if it exists
    const newCollapsedSets = new Set(collapsedSets);
    newCollapsedSets.delete(index);
    // Adjust indices for sets after the deleted one
    const adjustedCollapsedSets = new Set();
    newCollapsedSets.forEach(setIndex => {
      if (setIndex > index) {
        adjustedCollapsedSets.add(setIndex - 1);
      } else if (setIndex < index) {
        adjustedCollapsedSets.add(setIndex);
      }
    });
    setCollapsedSets(adjustedCollapsedSets);
  }

  // replaced browser confirm with modal-driven clear
  function actuallyClearStats() {
    const p = players.find((pl) => pl.id === activePlayerId);
    setData({ ...EMPTY_DATA, playerName: p?.name || "" });
  }

  function newMatch() {
    const p = players.find((pl) => pl.id === activePlayerId);
    setEditingMatchId(null); // exit edit mode when creating new
    setData({ ...EMPTY_DATA, playerName: p?.name || "" });
    setView("record");
  }

  // Record Match gate: open modal if prerequisites are missing
  const [gateModal, setGateModal] = useState(null); // 'needPlayer' | 'needSeason' | 'needDate' | 'needOpponent' | 'deleteSeason' | 'deleteLineup' | null
  const [seasonToDelete, setSeasonToDelete] = useState(null);
  const [lineupToDelete, setLineupToDelete] = useState(null);
  const [lineupsRefreshKey, setLineupsRefreshKey] = useState(0);
  const [showTeamSelectionModal, setShowTeamSelectionModal] = useState(false);
  function recordMatchGate() {
    if (!activePlayerId || players.length === 0) {
      setGateModal("needPlayer");
      return;
    }
    if (!activeSeasonId || seasons.length === 0) {
      setGateModal("needSeason");
      return;
    }
    newMatch();
  }

  function recordTeamMatchGate() {
    // Ensure teams is a valid array
    const validTeams = Array.isArray(teams) ? teams : [];

    // Scenario 1: Team already selected (in team context)
    if (activeTeamId) {
      setView("team-record-match");
      return;
    }

    // Scenario 2: Only one team exists - auto-select it
    if (validTeams.length === 1) {
      setActiveTeamId(validTeams[0].id);
      setView("team-record-match");
      return;
    }

    // Scenario 3: Multiple teams, no selection - show modal
    if (validTeams.length > 1) {
      setShowTeamSelectionModal(true);
      return;
    }

    // Scenario 4: No teams at all - prompt to create one
    setNotice({
      type: "warning",
      text: "Please create a team first"
    });
    setView("team-setup");
  }

  function handleTeamSelection(teamId) {
    setActiveTeamId(teamId);
    setShowTeamSelectionModal(false);
    setView("team-record-match");
  }

  // Email CSV: includes Sets W-L, Set Scores, and Notes
  const csv = useMemo(() => {
    const { w: wlW, l: wlL } = getSetWL(data.setScores || []);
    const setScoresStr = formatSetScores(data.setScores || []);

    const headers = [
      "Player Name",
      "Match Date",
      "Opponent",
      ...STAT_FIELDS.map((s) => s.label),
      "Sets W-L",
      "Set Scores",
      "Notes",
    ];

    const values = [
      escapeCsv(data.playerName),
      escapeCsv(data.matchDate),
      escapeCsv(data.opponent),
      ...STAT_FIELDS.map((s) => String(data[s.key] ?? 0)),
      `${wlW}-${wlL}`,
      escapeCsv(setScoresStr),
      escapeCsv(data.notes || ""),
    ];

    return [headers.join(","), values.join(",")].join("\n");
  }, [data]);

  /* ----- Cloud: Matches for selected Player+Season ----- */
  const [matches, setMatches] = useState([]);
  const [allMatches, setAllMatches] = useState([]); // all matches for trends view
  const [allSeasons, setAllSeasons] = useState([]); // all seasons for trends view

  // Sort state for Match History
  const [dateSortDir, setDateSortDir] = useState("desc"); // 'asc' | 'desc'

  // Expanded rows state for match history table
  const [expandedMatchIds, setExpandedMatchIds] = useState(new Set());
  const [expandedTournamentMatchIds, setExpandedTournamentMatchIds] = useState(new Set());

  // Toggle expanded row
  const toggleExpandedRow = (matchId) => {
    setExpandedMatchIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(matchId)) {
        newSet.delete(matchId);
      } else {
        newSet.add(matchId);
      }
      return newSet;
    });
  };

  // Toggle expanded tournament match row
  const toggleExpandedTournamentMatch = (matchId) => {
    setExpandedTournamentMatchIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(matchId)) {
        newSet.delete(matchId);
      } else {
        newSet.add(matchId);
      }
      return newSet;
    });
  };

  // Normalize a comparable date key (YYYY-MM-DD preferred; fallback to createdAtISO)
  function dateKey(m) {
    return m.matchDate || m.createdAtISO || "";
  }

  // Client-side sorted view of matches
  const sortedMatches = useMemo(() => {
    const arr = [...matches];
    arr.sort((a, b) =>
      dateSortDir === "asc"
        ? dateKey(a).localeCompare(dateKey(b))
        : dateKey(b).localeCompare(dateKey(a))
    );
    return arr;
  }, [matches, dateSortDir]);

  const loadMatches = useCallback(async () => {
    if (!user || !activePlayerId || !activeSeasonId) return;
    
    // If offline, load from local storage
    if (!isOnline) {
      const localMatches = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.MATCHES) || [];
      const items = localMatches.filter(m => 
        m.userId === user.uid && 
        m.playerId === activePlayerId && 
        m.seasonId === activeSeasonId
      );
      
      // Sort by matchDate (YYYY-MM-DD) desc, fallback to createdAtISO
      items.sort((a, b) => {
        const aKey = a.matchDate || a.createdAtISO || "";
        const bKey = b.matchDate || b.createdAtISO || "";
        return bKey.localeCompare(aKey);
      });
      
      setMatches(items);
      return;
    }
    
    // If online, load from Firebase and cache locally
    try {
      const ref = collection(
        db,
        "users",
        user.uid,
        "players",
        activePlayerId,
        "seasons",
        activeSeasonId,
        "matches"
      );
      const snap = await getDocs(ref);
      const items = snap.docs.map((d) => ({ 
        id: d.id, 
        userId: user.uid, 
        playerId: activePlayerId, 
        seasonId: activeSeasonId, 
        ...d.data() 
      }));

      // Sort by matchDate (YYYY-MM-DD) desc, fallback to createdAtISO
      items.sort((a, b) => {
        const aKey = a.matchDate || a.createdAtISO || "";
        const bKey = b.matchDate || b.createdAtISO || "";
        return bKey.localeCompare(aKey);
      });

      setMatches(items);
      
      // Merge into local storage cache instead of overwriting
      const existingMatches = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.MATCHES) || [];
      const otherMatches = existingMatches.filter(m => 
        !(m.userId === user.uid && m.playerId === activePlayerId && m.seasonId === activeSeasonId)
      );
      const allMatches = [...otherMatches, ...items];
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.MATCHES, allMatches);
    } catch (error) {
      console.error('Failed to load matches from Firebase:', error);
      // Fallback to local storage
      const localMatches = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.MATCHES) || [];
      const items = localMatches.filter(m => 
        m.userId === user.uid && 
        m.playerId === activePlayerId && 
        m.seasonId === activeSeasonId
      );
      
      items.sort((a, b) => {
        const aKey = a.matchDate || a.createdAtISO || "";
        const bKey = b.matchDate || b.createdAtISO || "";
        return bKey.localeCompare(aKey);
      });
      
      setMatches(items);
    }
  }, [user, activePlayerId, activeSeasonId, isOnline]);

  const loadAllMatches = useCallback(async () => {
    if (!user) return;
    
    // Get all matches and seasons from all players
    const allMatchesData = [];
    const allSeasonsData = [];
    
    for (const player of players) {
      // Get all seasons for this player
      const seasonsRef = collection(
        db,
        "users",
        user.uid,
        "players",
        player.id,
        "seasons"
      );
      const seasonsSnap = await getDocs(seasonsRef);
      const playerSeasons = seasonsSnap.docs.map((d) => ({ 
        id: d.id, 
        playerId: player.id,
        ...d.data() 
      }));
      allSeasonsData.push(...playerSeasons);
      
      // Get all matches for this player's seasons
      for (const season of playerSeasons) {
        const ref = collection(
          db,
          "users",
          user.uid,
          "players",
          player.id,
          "seasons",
          season.id,
          "matches"
        );
        const snap = await getDocs(ref);
        const items = snap.docs.map((d) => ({ 
          id: d.id, 
          playerId: player.id,
          seasonId: season.id,
          ...d.data() 
        }));
        allMatchesData.push(...items);
      }
    }
    
    setAllMatches(allMatchesData);
    setAllSeasons(allSeasonsData);
  }, [user, players]);

  const seasonTotals = useMemo(() => {
    const totals = {
  sets: 0,
  assists: 0,
  kills: 0,
  hitInPlay: 0,
  serveAttempts: 0,
  serviceErrors: 0,
  aces: 0,
  digs: 0,
  hittingErrors: 0, // <-- add
  digErrors: 0,     // <-- add
  blockTouch: 0,    // <-- add
  blockError: 0,    // <-- add
  tip: 0,           // <-- add
  matchesCount: matches.length,
  setWins: 0,
  setLosses: 0,
};

for (const m of matches) {
  totals.sets += Number(m.sets || 0) + (Number(m.halfBlock || 0) * 0.5); // Blocks: full blocks + half blocks (0.5 each)
  totals.assists += Number(m.assists || 0);
  totals.kills += Number(m.kills || 0);
  // Support both new hitInPlay and legacy attackAttempts
  totals.hitInPlay += Number(m.hitInPlay || m.attackAttempts || 0);
  totals.serveAttempts += Number(m.serveAttempts || 0);
  totals.serviceErrors += Number(m.serviceErrors || 0);
  totals.aces += Number(m.aces || 0);
  totals.digs += Number(m.digs || 0);
  totals.hittingErrors += Number(m.hittingErrors || 0); // <-- add
  totals.digErrors += Number(m.digErrors || 0);         // <-- add
  totals.blockTouch += Number(m.blockTouch || 0);       // <-- add
  totals.blockError += Number(m.blockError || 0);       // <-- add
  totals.tip += Number(m.tip || 0);                     // <-- add

  const { w, l } = getSetWL(m.setScores || []);
  totals.setWins += w;
  totals.setLosses += l;
}

const sa = totals.serveAttempts;
const se = totals.serviceErrors;
const servingPct = sa ? Math.round(((sa - Math.max(0, se)) / sa) * 100) : 0;
const acePct = sa ? Math.round((Math.max(0, totals.aces) / sa) * 100) : 0;

// NEW: true hitting efficiency (decimal)
// Total attempts = kills + errors + hits in play
const totalAttempts = totals.kills + totals.hittingErrors + totals.hitInPlay;
const hittingPct = totalAttempts
  ? (totals.kills - totals.hittingErrors) / totalAttempts
  : 0;

// Kill percentage: Kills / Total Attempts
const killPct = totalAttempts
  ? Math.round((totals.kills / totalAttempts) * 100)
  : 0;

return { ...totals, servingPct, acePct, hittingPct, killPct };

  }, [matches]);

  async function saveMatch() {
    if (!user) {
      showNotice("error", "Please sign in first.");
      return;
    }
    if (!activePlayerId) {
      showNotice("error", "Create/select a player first.");
      setActivePlayerId(null);
      setPlayerEditMode("create");
      setView("player-setup");
      return;
    }
    if (!activeSeasonId) {
      showNotice("error", "Create/select a season first.");
      setView("dashboard");
      return;
    }
    if (!data.matchDate || !data.matchDate.trim()) {
      setGateModal("needDate");
      return;
    }
    if (!data.opponent || !data.opponent.trim()) {
      setGateModal("needOpponent");
      return;
    }

    try {
      const p = players.find((pl) => pl.id === activePlayerId);

      // sanitize setScores for saving (empty string -> 0)
      const cleanSets = (data.setScores || []).map((s) => ({
        us: toNonNegInt(s?.us, 99),
        opp: toNonNegInt(s?.opp, 99),
      }));

      const entry = {
        playerId: activePlayerId,
        seasonId: activeSeasonId,
        playerName: cleanText(p?.name || "", 80),
        playerNumber: cleanText(p?.number || "", 8),
        playerTeam: cleanText(p?.team || "", 80),
        matchDate: cleanText(data.matchDate || "", 10),
        opponent: cleanText(data.opponent || "", 60), // trims on save
        email: cleanText(data.email || "", 254),

        // stats
        sets: toNonNegInt(data.sets),
        halfBlock: toNonNegInt(data.halfBlock), // NEW - for double blocks (0.5 credit)
        assists: toNonNegInt(data.assists),
        kills: toNonNegInt(data.kills),
        hitInPlay: toNonNegInt(data.hitInPlay),
        hittingErrors: toNonNegInt(data.hittingErrors), // NEW
        tip: toNonNegInt(data.tip), // NEW
        serveAttempts: toNonNegInt(data.serveAttempts),
        serviceErrors: toNonNegInt(data.serviceErrors),
        aces: toNonNegInt(data.aces),
        digs: toNonNegInt(data.digs),
        digErrors: toNonNegInt(data.digErrors), // NEW
        blockTouch: toNonNegInt(data.blockTouch), // NEW
        blockError: toNonNegInt(data.blockError), // NEW

        // NEW: per-set scores
        setScores: cleanSets,

        // Calculate win/loss from set scores
        ...((() => {
          const { w, l } = getSetWL(cleanSets);
          return { won: w > l, setsWon: w, setsLost: l };
        })()),

        // NEW: match notes
        notes: cleanText(data.notes || "", 500),

        // NEW: match media
        matchMedia: data.matchMedia || [],

        // Tournament (if feature is enabled)
        ...(FEATURES.TOURNAMENTS && { tournamentId: data.tournamentId || null }),
      };

      const basePath = [
        "users", user.uid,
        "players", activePlayerId,
        "seasons", activeSeasonId,
        "matches"
      ];

      // Add user context to entry
      const entryWithContext = {
        ...entry,
        userId: user.uid,
        updatedAtISO: new Date().toISOString()
      };
      
      // Only add createdAtISO for new matches, not updates
      if (!editingMatchId) {
        entryWithContext.createdAtISO = new Date().toISOString();
      }

      if (isOnline) {
        // Online: Save to Firebase
        const wasEditing = !!editingMatchId;
        let finalMatchId = editingMatchId;

        if (editingMatchId) {
          // UPDATE
          // Check if season or player has changed
          const seasonChanged = originalSeasonId && originalSeasonId !== activeSeasonId;
          const playerChanged = originalPlayerId && originalPlayerId !== activePlayerId;

          if (seasonChanged || playerChanged) {
            // Season or player changed: delete from old location, create at new location
            const oldBasePath = [
              "users", user.uid,
              "players", originalPlayerId,
              "seasons", originalSeasonId,
              "matches"
            ];
            const oldMatchRef = doc(db, ...oldBasePath, editingMatchId);
            await deleteDoc(oldMatchRef);

            // Create at new location
            const ref = collection(db, ...basePath);
            const docRef = await addDoc(ref, entryWithContext);
            entryWithContext.id = docRef.id;
            finalMatchId = docRef.id;

            showNotice("success", "Match moved to new season.");
          } else {
            // Same season: just update
            const matchRef = doc(db, ...basePath, editingMatchId);
            await updateDoc(matchRef, entryWithContext);
            showNotice("success", "Match updated.");
          }
        } else {
          // CREATE
          const ref = collection(db, ...basePath);
          const docRef = await addDoc(ref, entryWithContext);
          entryWithContext.id = docRef.id;
          finalMatchId = docRef.id;
        }

        // IMPORTANT: Always update the match document with isPublic and shareToken
        const matchRef = doc(db, ...basePath, finalMatchId);
        await updateDoc(matchRef, {
          isPublic: data.isPublic,
          shareToken: data.shareToken
        });
        
        // Handle public match sharing (only when making public)
        if (data.isPublic && data.shareToken) {
          // Get player photo if available
          const player = players.find(p => p.id === activePlayerId);
          const publicMatchData = {
            ...entryWithContext,
            shareToken: data.shareToken,
            isPublic: true,
            publicAt: new Date().toISOString(),
            photoUrl: player?.photoUrl || null
          };
          
          // Save to public_matches collection
          const publicRef = collection(db, 'public_matches');
          
          if (wasEditing) {
            // UPDATE: Find and update existing public match
            const { query, where, getDocs, updateDoc, doc } = await import('firebase/firestore');
            const q = query(publicRef, where('shareToken', '==', data.shareToken));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
              // Update existing public match
              const publicMatchDoc = querySnapshot.docs[0];
              await updateDoc(doc(db, 'public_matches', publicMatchDoc.id), publicMatchData);
            } else {
              // Create new public match if not found
              await addDoc(publicRef, publicMatchData);
            }
          } else {
            // CREATE: New public match
            await addDoc(publicRef, publicMatchData);
          }
        } else if (wasEditing && !data.isPublic) {
          // Handle removing from public_matches when making private
          const { query, where, getDocs, deleteDoc, doc } = await import('firebase/firestore');
          const publicRef = collection(db, 'public_matches');
          const q = query(publicRef, where('shareToken', '==', data.shareToken));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            // Delete the public match
            const publicMatchDoc = querySnapshot.docs[0];
            await deleteDoc(doc(db, 'public_matches', publicMatchDoc.id));
          }
        }
        
        // Cache to local storage
        const localMatches = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.MATCHES) || [];
        if (wasEditing) {
          const index = localMatches.findIndex(m => m.id === editingMatchId);
          if (index >= 0) {
            localMatches[index] = {
              ...entryWithContext,
              id: finalMatchId,
              isPublic: data.isPublic,
              shareToken: data.shareToken
            };
          }
        } else {
          localMatches.push({
            ...entryWithContext,
            isPublic: data.isPublic,
            shareToken: data.shareToken
          });
        }
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.MATCHES, localMatches);

        if (!wasEditing) {
          showNotice("success", "Match saved!");
        }

        // Clean up editing state
        setEditingMatchId(null);
        setOriginalPlayerId(null);
        setOriginalSeasonId(null);
      } else {
        // Offline: Save locally and queue for sync
        if (editingMatchId) {
          // UPDATE
          const seasonChanged = originalSeasonId && originalSeasonId !== activeSeasonId;
          const playerChanged = originalPlayerId && originalPlayerId !== activePlayerId;

          const localMatches = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.MATCHES) || [];
          const index = localMatches.findIndex(m => m.id === editingMatchId);

          if (seasonChanged || playerChanged) {
            // Season/player changed offline - queue delete of old and create of new
            OfflineManager.addToSyncQueue('deleteMatch', {
              id: editingMatchId,
              data: { playerId: originalPlayerId, seasonId: originalSeasonId }
            });

            OfflineManager.addToSyncQueue('saveMatch', entryWithContext);
            updatePendingCount();
          } else {
            // Same season: just update
            OfflineManager.addToSyncQueue('updateMatch', {
              id: editingMatchId,
              data: {
                ...entryWithContext,
                isPublic: data.isPublic,
                shareToken: data.shareToken
              }
            });
            updatePendingCount();
          }

          setWasOfflineMatchSave(true);
          setShowMatchSaveModal(true);
          setEditingMatchId(null);
          setOriginalPlayerId(null);
          setOriginalSeasonId(null);
        } else {
          // CREATE
          // Don't add to local storage - only queue for sync
          // The match will appear after syncing when online

          OfflineManager.addToSyncQueue('saveMatch', entryWithContext);
          updatePendingCount();
          setWasOfflineMatchSave(true);
          setShowMatchSaveModal(true);
        }
      }

      // Only navigate to season-detail if online (match was actually saved to Firebase)
      // If offline, show modal and stay on record view
      if (isOnline) {
        if (!editingMatchId) {
          showNotice("success", "Match saved!");
        }
        setView("season-detail");
        await loadMatches();
      }
    } catch (err) {
      showNotice("error", err?.message || "Could not save match.");
    }
  }

  useEffect(() => {
    if (view === "season-detail" && user && activePlayerId && activeSeasonId) loadMatches();
  }, [view, user, activePlayerId, activeSeasonId, loadMatches]);

  useEffect(() => {
    if (view === "match-history" && user && activePlayerId && activeSeasonId) loadMatches();
  }, [view, user, activePlayerId, activeSeasonId, loadMatches]);

  useEffect(() => {
    if (user && activeSeasonId && FEATURES.TOURNAMENTS) {
      refreshTournaments(activeSeasonId);
    }
  }, [user, activeSeasonId, refreshTournaments]);

  useEffect(() => {
    if (view === "trends" && user && players.length > 0 && seasons.length > 0) loadAllMatches();
  }, [view, user, players, seasons, loadAllMatches]);

  // replaced browser confirm — deletion is triggered from an in-app modal
  async function removeMatch(id) {
    try {
      const matchRef = doc(
        db,
        "users",
        user.uid,
        "players",
        activePlayerId,
        "seasons",
        activeSeasonId,
        "matches",
        id
      );
      await deleteDoc(matchRef);
      setMatches((m) => m.filter((x) => x.id !== id));
      showNotice("success", "Match deleted.");
    } catch (err) {
      showNotice("error", err.message || "Could not delete match.");
    }
  }

  // Start editing an existing match
  function startEditMatch(m) {
    setEditingMatchId(m.id);
    setOriginalPlayerId(m.playerId || activePlayerId);
    setOriginalSeasonId(m.seasonId || activeSeasonId);
    setData({
      playerName: m.playerName || "",
      matchDate: m.matchDate || "",
      opponent: m.opponent || "",
      email: m.email || "",
      sets: Number(m.sets || 0),
      halfBlock: Number(m.halfBlock || 0), // NEW - for double blocks (0.5 credit)
      assists: Number(m.assists || 0),
      kills: Number(m.kills || 0),
      hitInPlay: Number(m.hitInPlay || m.attackAttempts || 0),
      hittingErrors: Number(m.hittingErrors || 0), // NEW
      tip: Number(m.tip || 0), // NEW
      serveAttempts: Number(m.serveAttempts || 0),
      serviceErrors: Number(m.serviceErrors || 0),
      aces: Number(m.aces || 0),
      digs: Number(m.digs || 0),
      digErrors: Number(m.digErrors || 0), // NEW
      blockTouch: Number(m.blockTouch || 0), // NEW
      blockError: Number(m.blockError || 0), // NEW

      // keep/convert per-set scores (includes the ?? fix)
      setScores: (m.setScores || []).map((s) => ({
        us: Number(s?.us || 0),
        opp: Number(s?.opp ?? s?.them ?? 0),
      })),
      
      // NEW: match notes
      notes: m.notes || "",

      // NEW: match media
      matchMedia: m.matchMedia || [],

      // NEW: public sharing fields
      isPublic: m.isPublic || false,
      shareToken: m.shareToken || null,

      // Tournament (keep existing tournament attachment)
      ...(FEATURES.TOURNAMENTS && { tournamentId: m.tournamentId || null }),
    });
    setView("record");
  }

  // Delete a player (and all their seasons + matches)
  async function deletePlayer(playerId) {
    if (!user) return;

    try {
      const seasonsRef = collection(db, "users", user.uid, "players", playerId, "seasons");
      const seasonsSnap = await getDocs(seasonsRef);

      for (const sDoc of seasonsSnap.docs) {
        const matchesRef = collection(
          db,
          "users",
          user.uid,
          "players",
          playerId,
          "seasons",
          sDoc.id,
          "matches"
        );
        const matchesSnap = await getDocs(matchesRef);

        await Promise.all(
          matchesSnap.docs.map((m) =>
            deleteDoc(
              doc(
                db,
                "users",
                user.uid, // fixed path
                "players",
                playerId,
                "seasons",
                sDoc.id,
                "matches",
                m.id
              )
            )
          )
        );

        await deleteDoc(doc(db, "users", user.uid, "players", playerId, "seasons", sDoc.id));
      }

      await deleteDoc(doc(db, "users", user.uid, "players", playerId));

      // Clean up any pending offline changes for this player
      const cleanedUpCount = OfflineManager.cleanupDeletedPlayerChanges(playerId);
      if (cleanedUpCount > 0) {
        // Update the pending changes count
        const currentQueue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
        setOfflineDataCount(currentQueue.length);
        if (currentQueue.length === 0) {
          setShowPendingChanges(false);
        }
      }

      let newActivePlayerId = activePlayerId === playerId ? null : activePlayerId;
      const updatedPlayers = await refreshPlayers();
      if (!newActivePlayerId && updatedPlayers.length) {
        newActivePlayerId = updatedPlayers[0].id;
      }
      setActivePlayerId(newActivePlayerId);

      let newActiveSeasonId = null;
      if (newActivePlayerId) {
        const updatedSeasons = await refreshSeasons(newActivePlayerId);
        newActiveSeasonId = updatedSeasons[0]?.id || null;
        setActiveSeasonId(newActiveSeasonId);
      } else {
        setSeasons([]);
        setActiveSeasonId(null);
      }

      await setDoc(
        doc(db, "users", user.uid),
        {
          activePlayerId: newActivePlayerId || null,
          activeSeasonId: newActiveSeasonId || null,
        },
        { merge: true }
      );

      showNotice("success", "Player deleted.");
      setView("dashboard");
    } catch (err) {
      showNotice("error", err?.message || "Failed to delete player.");
    }
  }

  // Season deletion
  async function handleDeleteSeason() {
    if (!user || !seasonToDelete) return;

    const { playerId, seasonId, seasonName } = seasonToDelete;

    try {
      // Delete all matches for this season
      const matchesRef = collection(
        db,
        "users",
        user.uid,
        "players",
        playerId,
        "seasons",
        seasonId,
        "matches"
      );
      const matchesSnap = await getDocs(matchesRef);

      await Promise.all(
        matchesSnap.docs.map((m) =>
          deleteDoc(
            doc(
              db,
              "users",
              user.uid,
              "players",
              playerId,
              "seasons",
              seasonId,
              "matches",
              m.id
            )
          )
        )
      );

      // Delete the season itself
      await deleteDoc(doc(db, "users", user.uid, "players", playerId, "seasons", seasonId));

      // Clean up any pending offline changes for this season
      const cleanedUpCount = OfflineManager.cleanupDeletedSeasonChanges(playerId, seasonId);
      if (cleanedUpCount > 0) {
        const currentQueue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
        setOfflineDataCount(currentQueue.length);
        if (currentQueue.length === 0) {
          setShowPendingChanges(false);
        }
      }

      // Refresh seasons
      const updatedSeasons = await refreshSeasons(playerId);

      // Update active season if needed
      if (activeSeasonId === seasonId) {
        const newActiveSeasonId = updatedSeasons[0]?.id || null;
        setActiveSeasonId(newActiveSeasonId);
        await setDoc(
          doc(db, "users", user.uid),
          { activeSeasonId: newActiveSeasonId || null },
          { merge: true }
        );
      }

      showNotice("success", "Season deleted.");
      setGateModal(null);
      setSeasonToDelete(null);
      setView("player-dashboard");
    } catch (err) {
      showNotice("error", err?.message || "Failed to delete season.");
    }
  }

  async function handleDeleteLineup() {
    if (!lineupToDelete) return;

    const { teamId, lineupId, lineupName } = lineupToDelete;

    try {
      await deleteDoc(doc(db, 'admin_teams', teamId, 'lineups', lineupId));

      showNotice("success", "Lineup deleted.");
      setGateModal(null);
      setLineupToDelete(null);
      setLineupsRefreshKey(prev => prev + 1); // Trigger reload of lineups view
    } catch (err) {
      console.error('Error deleting lineup:', err);
      showNotice("error", err?.message || "Failed to delete lineup.");
    }
  }

  // Player create/edit
  const [playerForm, setPlayerForm] = useState({
    name: "",
    number: "",
    team: "",
    position: "",
    photoUrl: null,
    excludeFromLeaderboard: false,
  });
  useEffect(() => {
    if (playerEditMode === "edit") {
      const p = players.find((pl) => pl.id === activePlayerId);
      setPlayerForm({
        name: p?.name || "",
        number: p?.number || "",
        team: p?.team || "",
        position: p?.position || "",
        photoUrl: p?.photoUrl || null,
        excludeFromLeaderboard: p?.excludeFromLeaderboard || false,
      });
    } else {
      setPlayerForm({ name: "", number: "", team: "", position: "", photoUrl: null, excludeFromLeaderboard: false });
    }
  }, [playerEditMode, activePlayerId, players]);

  // Image compression utility
  function compressImage(file, maxWidth = 400, quality = 0.8) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  }

  async function handlePlayerPhotoUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      showNotice("error", "Image must be smaller than 2MB");
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      showNotice("error", "Please select an image file");
      return;
    }

    try {
      showNotice("info", "Compressing image...");
      
      // Compress the image
      const compressedImage = await compressImage(file, 400, 0.8);
      
      // Update the form with the compressed image
      setPlayerForm(prev => ({ ...prev, photoUrl: compressedImage }));
      
      showNotice("success", "Photo uploaded successfully!");
    } catch (error) {
      console.error('Error processing image:', error);
      showNotice("error", "Failed to process image");
    }
  }

  async function handleMatchMediaUpload(event) {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    try {
      showNotice("info", "Processing media files...");
      
      const processedFiles = [];
      
      for (const file of files) {
        // Check file type
        if (!file.type.startsWith('image/')) {
          showNotice("error", `${file.name} is not a supported image file`);
          continue;
        }

        // Check file size
        if (file.type.startsWith('image/') && file.size > 2 * 1024 * 1024) {
          showNotice("error", `${file.name} is too large (max 2MB for images)`);
          continue;
        }


        // Process the file
        if (file.type.startsWith('image/')) {
          // Compress images
          const compressedImage = await compressImage(file, 800, 0.8);
          processedFiles.push({
            type: 'image',
            url: compressedImage,
            name: file.name,
            size: file.size
          });
        }
      }

      // Update the form with processed files
      setData(prev => ({
        ...prev,
        matchMedia: [...(prev.matchMedia || []), ...processedFiles]
      }));

      showNotice("success", `${processedFiles.length} file(s) uploaded successfully!`);
    } catch (error) {
      console.error('Error processing media:', error);
      showNotice("error", "Failed to process media files");
    }
  }


  async function savePlayerProfile(e) {
    e?.preventDefault?.();
    if (!user) return;
    if (!playerForm.name.trim()) {
      showNotice("error", "Player name is required.");
      return;
    }

    if (playerEditMode === "edit" && activePlayerId) {
      const pRef = doc(db, "users", user.uid, "players", activePlayerId);
      await updateDoc(pRef, { ...playerForm });
      showNotice("success", "Player updated.");
      await refreshPlayers();
      setView("dashboard");
    } else {
      // Check if user can add a player (enforce limit)
      if (!canAddPlayer(isPro, players.length, isCoach)) {
        setUpgradeModalFeature('player');
        setUpgradeModalOpen(true);
        return;
      }

      const pRef = await addDoc(collection(db, "users", user.uid, "players"), {
        ...playerForm,
        createdAtISO: new Date().toISOString(),
      });
      setActivePlayerId(pRef.id);
      await setDoc(
        doc(db, "users", user.uid),
        { activePlayerId: pRef.id, activeSeasonId: null },
        { merge: true }
      );
      await refreshPlayers();
      setSeasons([]);
      setActiveSeasonId(null);
      showNotice("success", "Player created. Add a season next.");
      setView("dashboard");
    }
  }

  // ---------- Add Season View (season-setup) ----------
  const [seasonSetupForm, setSeasonSetupForm] = useState({ name: "", year: "", team: "" });
  const [seasonSetupPlayerId, setSeasonSetupPlayerId] = useState(null);

  useEffect(() => {
    if (view === "season-setup") {
      if (players.length === 1) {
        setSeasonSetupPlayerId(players[0].id);
      } else if (activePlayerId) {
        setSeasonSetupPlayerId(activePlayerId);
      } else {
        setSeasonSetupPlayerId(players[0]?.id || null);
      }
    }
    if (view !== "season-setup") {
      setSeasonSetupForm({ name: "", year: "", team: "" });
    }
  }, [view, players, activePlayerId]);

  async function createSeasonFromSetup(e) {
    e?.preventDefault?.();
    if (!user) {
      showNotice("error", "Please sign in first.");
      return;
    }
    if (players.length === 0) {
      showNotice("error", "Create a player first.");
      setActivePlayerId(null);
      setPlayerEditMode("create");
      setView("player-setup");
      return;
    }
    if (!seasonSetupPlayerId) {
      showNotice("error", "Please select a player.");
      return;
    }
    if (!seasonSetupForm.name.trim()) {
      showNotice("error", "Season name is required.");
      return;
    }

    // Check if user can add a season (enforce limit)
    if (!canAddSeason(isPro, seasons.length, isCoach)) {
      setUpgradeModalFeature('season');
      setUpgradeModalOpen(true);
      return;
    }

    const sRef = await addDoc(
      collection(db, "users", user.uid, "players", seasonSetupPlayerId, "seasons"),
      {
        name: seasonSetupForm.name.trim(),
        year: seasonSetupForm.year.trim(),
        team: seasonSetupForm.team.trim(),
        createdAtISO: new Date().toISOString(),
      }
    );

    setActivePlayerId(seasonSetupPlayerId);
    setActiveSeasonId(sRef.id);
    await setDoc(
      doc(db, "users", user.uid),
      { activePlayerId: seasonSetupPlayerId, activeSeasonId: sRef.id },
      { merge: true }
    );
    await refreshSeasons(seasonSetupPlayerId);

    showNotice("success", "Season created.");
    setView("dashboard");
  }

  async function changeActivePlayer(id) {
    if (!user) return;
    setActivePlayerId(id);
    const list = await refreshSeasons(id);
    const firstSeason = list[0]?.id || null;
    setActiveSeasonId(firstSeason);
    
    // Save preferences locally only - no need to sync UI preferences
    const userPrefs = OfflineManager.loadFromLocal('vballstats_user_prefs') || {};
    userPrefs.activePlayerId = id;
    userPrefs.activeSeasonId = firstSeason;
    OfflineManager.saveToLocal('vballstats_user_prefs', userPrefs);
    
    if (view === "season-detail") loadMatches();
  }

  async function changeActiveSeason(id) {
    if (!user) return;
    setActiveSeasonId(id);
    
    // Save preferences locally only - no need to sync UI preferences
    const userPrefs = OfflineManager.loadFromLocal('vballstats_user_prefs') || {};
    userPrefs.activeSeasonId = id;
    OfflineManager.saveToLocal('vballstats_user_prefs', userPrefs);

    // Load tournaments for this season if feature is enabled
    if (FEATURES.TOURNAMENTS) {
      await refreshTournaments(id);
    }
  }

  // Tournament management functions
  async function createTournament(tournamentData, retryCount = 0) {
    if (!user || !activePlayerId || !activeSeasonId || !FEATURES.TOURNAMENTS) {
      return;
    }
    
    const entryWithContext = {
      ...tournamentData,
      userId: user.uid,
      playerId: activePlayerId,
      seasonId: activeSeasonId,
      createdAtISO: new Date().toISOString(),
    };
    
    if (isOnline) {
      try {
        const tournamentRef = await addDoc(
          collection(db, "users", user.uid, "players", activePlayerId, "seasons", activeSeasonId, "tournaments"),
          entryWithContext
        );
        
        console.log('createTournament: Successfully created tournament with ID:', tournamentRef.id);
        
        // Cache to local storage
        const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const entryWithId = { ...entryWithContext, id: tournamentRef.id };
        localTournaments.push(entryWithId);
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, localTournaments);
        
        await refreshTournaments(activeSeasonId);
        showNotice("success", "Tournament created successfully!");
        return tournamentRef.id;
      } catch (error) {
        console.error('Failed to create tournament:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          tournamentData,
          path: `users/${user.uid}/players/${activePlayerId}/seasons/${activeSeasonId}/tournaments`,
          retryCount
        });
        
        // Handle specific network errors with retry
        if ((error.code === 'unavailable' || error.message.includes('QUIC_PROTOCOL_ERROR') || error.message.includes('network')) && retryCount < 2) {
          console.log(`Retrying tournament creation (attempt ${retryCount + 1}/3)...`);
          showNotice("info", "Network error detected. Retrying...");
          
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 1000 + (retryCount * 1000)));
          return createTournament(tournamentData, retryCount + 1);
        }
        
        showNotice("error", `Failed to create tournament: ${error.message}`);
      }
    } else {
      // Offline: Save locally and queue for sync
      const tempId = 'temp_' + Date.now() + '_' + Math.random();
      const entryWithId = { ...entryWithContext, id: tempId };
      
      const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
      localTournaments.push(entryWithId);
      OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, localTournaments);
      
      // Add to sync queue
      OfflineManager.addToSyncQueue('saveTournament', entryWithId);
      updatePendingCount();
      
      await refreshTournaments(activeSeasonId);
      showNotice("success", "Tournament created (offline). Will sync when online.");
      return tempId;
    }
  }

  // Update a tournament
  async function updateTournament(tournamentId, tournamentData) {
    if (!user || !activePlayerId || !activeSeasonId || !FEATURES.TOURNAMENTS) {
      return;
    }

    try {
      if (isOnline) {
        const tournamentRef = doc(db, "users", user.uid, "players", activePlayerId, "seasons", activeSeasonId, "tournaments", tournamentId);
        await updateDoc(tournamentRef, tournamentData);
        
        // Update local storage
        const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const updatedTournaments = localTournaments.map(t => 
          t.id === tournamentId ? { ...t, ...tournamentData } : t
        );
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, updatedTournaments);
      } else {
        // Offline: Update local storage and queue for sync
        const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const updatedTournaments = localTournaments.map(t => 
          t.id === tournamentId ? { ...t, ...tournamentData } : t
        );
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, updatedTournaments);
        
        // Queue for sync when online
        OfflineManager.addToSyncQueue('updateTournament', { 
          id: tournamentId, 
          data: { ...tournamentData, playerId: activePlayerId, seasonId: activeSeasonId, userId: user.uid }
        });
        updatePendingCount();
      }
      
      await refreshTournaments(activeSeasonId);
      showNotice("success", "Tournament updated successfully!");
    } catch (error) {
      console.error("Error updating tournament:", error);
      showNotice("error", "Failed to update tournament. Please try again.");
    }
  }

  // Start editing a tournament
  function startEditTournament(tournament) {
    setEditingTournament(tournament);
    setTournamentEditForm({
      name: tournament.name || "",
      location: tournament.location || "",
      startDate: tournament.startDate || "",
      endDate: tournament.endDate || "",
      notes: tournament.notes || ""
    });
    setView("tournament-edit");
  }

  // Save tournament edits
  async function saveTournamentEdit(e) {
    e.preventDefault();
    if (!editingTournament) return;

    const tournamentData = {
      name: tournamentEditForm.name.trim(),
      location: tournamentEditForm.location.trim(),
      startDate: tournamentEditForm.startDate,
      endDate: tournamentEditForm.endDate,
      notes: tournamentEditForm.notes.trim(),
      updatedAt: new Date().toISOString()
    };

    await updateTournament(editingTournament.id, tournamentData);
    setEditingTournament(null);
    setView("season-detail");
  }

  async function deleteTournament(tournamentId) {
    console.log('deleteTournament: Called with ID:', tournamentId);
    console.log('deleteTournament: Requirements check:', { 
      user: !!user, 
      activePlayerId, 
      activeSeasonId, 
      tournamentsEnabled: FEATURES.TOURNAMENTS,
      isOnline 
    });
    
    if (!user || !activePlayerId || !activeSeasonId || !FEATURES.TOURNAMENTS) {
      console.log('deleteTournament: Missing requirements, returning early');
      return;
    }
    
    if (isOnline) {
      try {
        console.log('deleteTournament: Deleting from Firebase...');
        
        // Check if this is a temporary ID (created offline)
        if (tournamentId.startsWith('temp_')) {
          console.log('deleteTournament: Temporary ID detected, checking if synced to Firebase...');
          
          // Find the tournament in local storage to get its name
          const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
          const tempTournament = localTournaments.find(t => t.id === tournamentId);
          
          if (tempTournament) {
            // Load tournaments from Firebase to find the real one
            const tournamentRef = collection(db, "users", user.uid, "players", activePlayerId, "seasons", activeSeasonId, "tournaments");
            const snap = await getDocs(tournamentRef);
            const firebaseTournaments = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            
            // Find the matching tournament by name and creation time
            const realTournament = firebaseTournaments.find(t => 
              t.name === tempTournament.name && 
              t.createdAtISO === tempTournament.createdAtISO
            );
            
            if (realTournament && !realTournament.id.startsWith('temp_')) {
              console.log('deleteTournament: Found real tournament with ID:', realTournament.id);
              const realTournamentRef = doc(db, "users", user.uid, "players", activePlayerId, "seasons", activeSeasonId, "tournaments", realTournament.id);
              await deleteDoc(realTournamentRef);
              console.log('deleteTournament: Successfully deleted real tournament from Firebase');
            } else {
              console.log('deleteTournament: Tournament not synced to Firebase yet, only deleting from local storage');
              console.log('deleteTournament: Firebase tournaments found:', firebaseTournaments.length);
              console.log('deleteTournament: Looking for tournament with name:', tempTournament.name, 'and time:', tempTournament.createdAtISO);
            }
          }
        } else {
          // Regular deletion for real tournament IDs
          const tournamentRef = doc(db, "users", user.uid, "players", activePlayerId, "seasons", activeSeasonId, "tournaments", tournamentId);
          await deleteDoc(tournamentRef);
          console.log('deleteTournament: Successfully deleted from Firebase');
        }
        
        // Remove from local storage (both temp and real IDs)
        const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
        const updatedTournaments = localTournaments.filter(t => t.id !== tournamentId);
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, updatedTournaments);
        console.log('deleteTournament: Updated local storage');
        
        await refreshTournaments(activeSeasonId);
        console.log('deleteTournament: Refreshed tournaments list');
        showNotice("success", "Tournament deleted successfully!");
      } catch (error) {
        console.error('deleteTournament: Failed to delete tournament:', error);
        showNotice("error", "Failed to delete tournament. Please try again.");
      }
    } else {
      console.log('deleteTournament: Offline mode - deleting locally...');
      // Offline: Remove from local storage and queue for sync
      const localTournaments = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS) || [];
      const tournamentToDelete = localTournaments.find(t => t.id === tournamentId);
      
      if (tournamentToDelete) {
        const updatedTournaments = localTournaments.filter(t => t.id !== tournamentId);
        OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.TOURNAMENTS, updatedTournaments);
        
        // Add to sync queue
        OfflineManager.addToSyncQueue('deleteTournament', { 
          id: tournamentId, 
          data: tournamentToDelete
        });
        updatePendingCount();
        
        await refreshTournaments(activeSeasonId);
        showNotice("success", "Tournament deleted (offline). Will sync when online.");
      } else {
        console.log('deleteTournament: Tournament not found in local storage');
        showNotice("error", "Tournament not found.");
      }
    }
  }

  // Tournament creation form submission
  async function saveTournament(e) {
    e?.preventDefault?.();
    console.log('saveTournament: Form submitted with data:', tournamentForm);
    
    if (tournamentLoading) {
      console.log('saveTournament: Already creating tournament, ignoring duplicate submission');
      return;
    }
    
    if (!user || !activePlayerId || !activeSeasonId || !FEATURES.TOURNAMENTS) {
      // Missing requirements for tournament save
      return;
    }
    
    if (!tournamentForm.name.trim()) {
      showNotice("error", "Please enter a tournament name.");
      return;
    }
    
    setTournamentLoading(true);
    console.log('saveTournament: Calling createTournament...');
    
    try {
      const tournamentId = await createTournament({
        name: cleanText(tournamentForm.name, 80),
        date: tournamentForm.date || null,
        location: cleanText(tournamentForm.location, 100) || null,
      });
      
      console.log('saveTournament: Tournament created successfully, ID:', tournamentId);
      
      // Only reset form and navigate if tournament was actually created
      if (tournamentId) {
        setTournamentForm({ name: "", date: "", location: "" });
        setView("season-detail");
      }
    } catch (error) {
      console.error('saveTournament: Failed to create tournament:', error);
      showNotice("error", "Failed to create tournament. Please try again.");
    } finally {
      setTournamentLoading(false);
    }
  }

  // Calculate tournament-specific stats (comprehensive like season stats)
  function calculateTournamentStats(tournamentId) {
    if (!FEATURES.TOURNAMENTS || !tournamentId) return null;
    
    const tournamentMatches = matches.filter(m => m.tournamentId === tournamentId);
    
    if (tournamentMatches.length === 0) {
      return {
        matchesCount: 0,
        wins: 0,
        losses: 0,
        winRate: 0,
        setWins: 0,
        setLosses: 0,
        kills: 0,
        hitInPlay: 0,
        attackAttempts: 0,
        hittingErrors: 0,
        tip: 0,
        hittingPct: 0,
        killPct: 0,
        aces: 0,
        serveAttempts: 0,
        serviceErrors: 0,
        servingPct: 0,
        acePct: 0,
        digs: 0,
        digErrors: 0,
        blockTouch: 0,
        blockError: 0,
        sets: 0,
        assists: 0
      };
    }
    
    const stats = tournamentMatches.reduce((acc, match) => {
      acc.matchesCount++;
      if (match.won) acc.wins++;
      else acc.losses++;

      // Set wins/losses
      const { w, l } = getSetWL(match.setScores || []);
      acc.setWins += w;
      acc.setLosses += l;

      // Offensive stats
      acc.kills += match.kills || 0;
      acc.hitInPlay += match.hitInPlay || match.attackAttempts || 0;
      acc.attackAttempts += match.hitInPlay || match.attackAttempts || 0;
      acc.hittingErrors += match.hittingErrors || 0;
      acc.tip += match.tip || 0;

      // Serving stats
      acc.aces += match.aces || 0;
      acc.serveAttempts += match.serveAttempts || 0;
      acc.serviceErrors += match.serviceErrors || 0;

      // Defensive stats
      acc.digs += match.digs || 0;
      acc.digErrors += match.digErrors || 0;
      acc.blockTouch += match.blockTouch || 0;
      acc.blockError += match.blockError || 0;
      acc.sets += (match.blocks || 0) + ((match.halfBlock || 0) * 0.5); // Blocks: full blocks + half blocks (0.5 each)
      acc.assists += match.assists || 0;

      return acc;
    }, {
      matchesCount: 0,
      wins: 0,
      losses: 0,
      setWins: 0,
      setLosses: 0,
      kills: 0,
      hitInPlay: 0,
      attackAttempts: 0,
      hittingErrors: 0,
      tip: 0,
      aces: 0,
      serveAttempts: 0,
      serviceErrors: 0,
      digs: 0,
      digErrors: 0,
      blockTouch: 0,
      blockError: 0,
      sets: 0,
      assists: 0
    });
    
    // Calculate derived stats
    stats.winRate = stats.matchesCount > 0 ? Math.round((stats.wins / stats.matchesCount) * 100) : 0;
    // Total attempts = kills + errors + hit in play
    const totalAttempts = stats.kills + stats.hittingErrors + stats.attackAttempts;
    stats.hittingPct = totalAttempts > 0 ? (stats.kills - stats.hittingErrors) / totalAttempts : 0;
    stats.killPct = totalAttempts > 0 ? Math.round((stats.kills / totalAttempts) * 100) : 0;
    stats.servingPct = stats.serveAttempts > 0 ? Math.round(((stats.serveAttempts - stats.serviceErrors) / stats.serveAttempts) * 100) : 0;
    stats.acePct = stats.serveAttempts > 0 ? Math.round((stats.aces / stats.serveAttempts) * 100) : 0;
    
    return stats;
  }

  /* ---------- NEW: Season Edit ---------- */
  const [seasonEditId, setSeasonEditId] = useState(null);
  const [seasonEditForm, setSeasonEditForm] = useState({ name: "", year: "", team: "" });

  function openSeasonEdit(seasonId) {
    const s = seasons.find((x) => x.id === seasonId);
    if (!s) return;
    setSeasonEditId(seasonId);
    setSeasonEditForm({ name: s.name || "", year: s.year || "", team: s.team || "" });
    setView("season-edit");
  }

  async function saveSeasonEdit(e) {
    e?.preventDefault?.();
    if (!user || !activePlayerId || !seasonEditId) return;
    if (!seasonEditForm.name.trim()) {
      showNotice("error", "Season name is required.");
      return;
    }
    try {
      const sRef = doc(db, "users", user.uid, "players", activePlayerId, "seasons", seasonEditId);
      await updateDoc(sRef, {
        name: seasonEditForm.name.trim(),
        year: seasonEditForm.year.trim(),
        team: seasonEditForm.team.trim(),
      });
      await refreshSeasons(activePlayerId);
      setActiveSeasonId(seasonEditId);
      showNotice("success", "Season updated.");
      setView("season-detail");
    } catch (err) {
      showNotice("error", err?.message || "Failed to update season.");
    }
  }

  /* ---------- Nav Menu (Dropdown/Hamburger) ---------- */
  function NavMenu() {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
      function onDocClick(e) {
        if (!open) return;
        if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
      }
      function onEsc(e) {
        if (e.key === "Escape") setOpen(false);
      }
      document.addEventListener("mousedown", onDocClick);
      document.addEventListener("keydown", onEsc);
      return () => {
        document.removeEventListener("mousedown", onDocClick);
        document.removeEventListener("keydown", onEsc);
      };
    }, [open]);

    const disableAddSeason = players.length === 0;
    const disableRecord = !activePlayerId || !activeSeasonId;

    const go = (action) => {
      setOpen(false);
      if (action === "dashboard") {
        setView("dashboard");
        return;
      }
      if (action === "add-player") {
        if (!canAddPlayer(isPro, players.length, isCoach)) {
          setUpgradeModalFeature('player');
          setUpgradeModalOpen(true);
          return;
        }
        setActivePlayerId(null);
        setPlayerEditMode("create");
        setView("player-setup");
        return;
      }
      if (action === "add-season") {
        if (players.length === 0) {
          showNotice("error", "Create a player first.");
          setActivePlayerId(null);
          setPlayerEditMode("create");
          setView("player-setup");
          return;
        }
        if (!canAddSeason(isPro, seasons.length, isCoach)) {
          setUpgradeModalFeature('season');
          setUpgradeModalOpen(true);
          return;
        }
        setView("season-setup");
        return;
      }
      if (action === "record-match") {
        recordMatchGate();
        return;
      }
      if (action === "trends") {
        if (!canAccessTrends(isPro, isCoach)) {
          setUpgradeModalFeature('trends');
          setUpgradeModalOpen(true);
          return;
        }
        setView("trends");
        return;
      }
      if (action === "account") {
        setView("account");
        return;
      }
      if (action === "pricing") {
        setView("pricing");
        return;
      }
      if (action === "teams") {
        setView("teams");
        return;
      }
      if (action === "help") {
        window.location.href = '/help';
        return;
      }
      if (action === "blog-admin") {
        setView("blog-admin");
        return;
      }
      if (action === "sign-out") {
        handleSignOut();
        return;
      }
    };

    return (
      <div className="menu-wrap" ref={wrapRef}>
        <button
          className="menu-button"
          aria-haspopup="menu"
          aria-expanded={open ? "true" : "false"}
          onClick={() => setOpen((o) => !o)}
        >
          <HamburgerIcon className="menu-icon" />
          <span className="menu-label">Menu</span>
        </button>

        {open && (
          <div className="menu-panel" role="menu">
            <button
              className="menu-item"
              role="menuitem"
              onClick={() => go("dashboard")}
            >
              Home
            </button>
            <button
              className="menu-item"
              role="menuitem"
              onClick={() => go("help")}
            >
              Help
            </button>
            <button
              className="menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                window.open("/contact.html", "_blank", "noopener,noreferrer");
              }}
            >
              Contact
            </button>

            <div className="menu-sep" />
            <button className="menu-item" role="menuitem" onClick={() => go("account")}>
              Account
            </button>

            <div className="menu-sep" />
            <button className="menu-item danger" role="menuitem" onClick={() => go("sign-out")}>
              Sign Out
            </button>
          </div>
        )}
      </div>
    );
  }

  /* ---------- Header & Footer (match static page) ---------- */
  const styles = {
    header: { borderBottom: "1px solid var(--border)", background: "#fff", position: "relative", zIndex: 1000 },
    wrap: {
      maxWidth: "var(--page-max)",
      margin: "0 auto",
      padding: "16px var(--page-pad)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
    },
    brand: { display: "flex", alignItems: "center", gap: 12, color: "inherit", textDecoration: "none" },
    brandImg: { height: 36, width: "auto", display: "block" },
    brandName: { fontWeight: 700, letterSpacing: ".2px" },
    headerActions: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      marginLeft: "auto",
      whiteSpace: "nowrap",
    },
    userEmail: { color: "var(--muted)", fontSize: 14 },
    footer: { borderTop: "1px solid var(--border)", color: "var(--muted)", fontSize: 14, background: "#fff" },
    links: { display: "flex", gap: 10, alignItems: "center" },
    linkA: { color: "var(--muted)", textDecoration: "none" },
    dot: { opacity: 0.55 },
  };

  function SiteHeader() {
    return (
      <header className="site-header" style={styles.header}>
        <div className="wrap" style={styles.wrap}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            {/* Left side - Logo */}
            <div>
              <a className="brand" href="/" style={styles.brand}>
                <img
                  src={`${process.env.PUBLIC_URL}/Vballstat-Logo.png`}
                  alt="VBall Stats logo"
                  style={styles.brandImg}
                />
                <span className="name" style={styles.brandName}>
                  VBall Stats
                </span>
              </a>
            </div>
            
            {/* Right side - User actions and offline controls */}
            {user && (
              <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
                {/* Offline Mode Controls */}
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  {/* Pending Changes Pill */}
                  {offlineDataCount > 0 && (
                    <div style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 10px",
                      backgroundColor: "var(--accent)",
                      color: "white",
                      borderRadius: "16px",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: "pointer",
                      animation: "pulse 2s infinite"
                    }}
                    onClick={() => setShowPendingChanges(true)}
                    title="Click to review pending changes"
                    >
                      <span>📝</span>
                      <span>{offlineDataCount} Pending</span>
                    </div>
                  )}
                  
                  {/* Offline mode toggle */}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "600" }}>
                      {isTestingOffline ? "Offline" : "Online"} <span style={{ fontStyle: "italic", fontWeight: "400", fontSize: "11px", color: "var(--muted)" }}>beta</span>
                    </span>
                    <label style={{ 
                      position: "relative", 
                      display: "inline-block", 
                      width: "36px", 
                      height: "20px",
                      cursor: "pointer"
                    }}>
                      <input
                        type="checkbox"
                        checked={!isTestingOffline}
                        onChange={() => {
                          if (isTestingOffline) {
                            setIsTestingOffline(false);
                            setIsOnline(true);
                          } else {
                            setIsTestingOffline(true);
                            setIsOnline(false);
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: isTestingOffline ? "#dc3545" : "#28a745",
                        borderRadius: "20px",
                        transition: "all 0.3s ease",
                        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.2)"
                      }}>
                        <span style={{
                          position: "absolute",
                          content: "''",
                          height: "16px",
                          width: "16px",
                          left: isTestingOffline ? "2px" : "18px",
                          bottom: "2px",
                          backgroundColor: "white",
                          borderRadius: "50%",
                          transition: "all 0.3s ease",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.3)"
                        }} />
                      </span>
                    </label>
                    
                    {/* Info link */}
                    <button
                      onClick={() => setShowOfflineInfo(true)}
                      style={{
                        background: "var(--muted)",
                        border: "none",
                        color: "white",
                        fontSize: "12px",
                        cursor: "pointer",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "600",
                        transition: "all 0.2s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "var(--primary)";
                        e.target.style.transform = "scale(1.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "var(--muted)";
                        e.target.style.transform = "scale(1)";
                      }}
                      title="Learn about offline mode"
                    >
                      ?
                    </button>
                  </div>
                </div>
                
                {/* User menu */}
                <div className="header-actions" style={styles.headerActions}>
                  <div className="user-email" style={styles.userEmail}></div>
                  <NavMenu />
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    );
  }

  function SiteFooter() {
    return (
      <footer className="site-footer" role="contentinfo">
        <div className="footer-content">
          {/* Brand Section */}
          <div className="footer-section footer-brand">
            <div className="footer-logo">
              <img
                src={`${process.env.PUBLIC_URL}/Vballstat-Logo.png`}
                alt="VBall Stats"
                style={{
                  height: '32px',
                  width: 'auto',
                  filter: 'brightness(0) invert(1)'
                }}
              />
              <span style={{ fontWeight: '700', fontSize: '16px' }}>VBall Stats</span>
            </div>
            <p className="footer-tagline">
              Track your volleyball performance and improve your game.
            </p>
          </div>

          {/* Product Links */}
          <div className="footer-section">
            <h4 className="footer-heading">Product</h4>
            <ul className="footer-links">
              <li>
                <a
                  href="/"
                  onClick={(e) => {
                    e.preventDefault();
                    setView('dashboard');
                    window.scrollTo(0, 0);
                  }}
                >
                  Home
                </a>
              </li>
              <li>
                <a href="/pricing.html">
                  Pricing
                </a>
              </li>
              <li>
                <a href="/help">
                  Help Center
                </a>
              </li>
              <li>
                <a href="/free-stat-sheets.html">
                  Free Stat Sheets
                </a>
              </li>
            </ul>
          </div>

          {/* Company Links */}
          <div className="footer-section">
            <h4 className="footer-heading">Company</h4>
            <ul className="footer-links">
              <li>
                <a href="/about">
                  About
                </a>
              </li>
              <li>
                <a href="/contact-us">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links */}
          <div className="footer-section">
            <h4 className="footer-heading">Legal</h4>
            <ul className="footer-links">
              <li>
                <a href="/privacy">
                  Privacy Policy
                </a>
              </li>
              <li>
                <a href="/changelog">
                  Changelog
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <div className="footer-copyright">
              © {new Date().getFullYear()} VBall Stats. All rights reserved.
            </div>
            <div className="footer-disclaimer">
              VBall Stats cannot guarantee that data will be saved, stored, or preserved. Please keep your own backups of anything important, utilizing export features within the app.
            </div>
          </div>
        </div>
      </footer>
    );
  }

  /* ---------- Render ---------- */
  const showingInAppReset = !!resetLink?.oobCode;
  const Notice = ({ type = "info", children, onClose }) => (
    <div className={`notice ${type}`}>
      <div>{children}</div>
      <button className="notice-close" onClick={onClose} aria-label="Close">
        ×
      </button>
    </div>
  );

  // Export CSV for season-detail: includes Sets W-L and Set Scores
  function exportSeasonCsv() {
    const headers = [
      "Player Name",
      "Match Date",
      "Opponent",
      ...STAT_FIELDS.map((s) => s.label),
      "Sets W-L",
      "Set Scores",
      "Notes",
    ];
    const rows = matches.map((m) => {
      const { w, l } = getSetWL(m.setScores || []);
      const setScoresStr = formatSetScores(m.setScores || []);
      return [
        escapeCsv(m.playerName || ""),
        escapeCsv(m.matchDate || ""),
        escapeCsv(m.opponent || ""),
        ...STAT_FIELDS.map((s) => String(m[s.key] ?? 0)),
        `${w}-${l}`,
        escapeCsv(setScoresStr),
        escapeCsv(m.notes || ""),
      ].join(",");
    });
    const csvAll = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvAll], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "vball-season-matches.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    showNotice("success", "Exported season matches as CSV.");
  }

  // Check for public match URL
  const path = window.location.pathname;
  const publicMatchMatch = path.match(/^\/match\/([a-zA-Z0-9]+)$/);

  if (publicMatchMatch) {
    const shareToken = publicMatchMatch[1];
    return <PublicMatchView shareToken={shareToken} />;
  }


  // Check for team player reports URL
  if (path === '/team-player-reports') {
    return <PlayerReportCardsView />;
  }

  // Loading fallback component for Suspense
  const LoadingFallback = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      color: 'var(--muted)'
    }}>
      <div>Loading...</div>
    </div>
  );

  return (
    <div className="app">
      {/* Onboarding - Mobile Carousel */}
      {showMobileOnboarding && (
        <MobileOnboardingCarousel
          onComplete={handleOnboardingComplete}
          onSkip={handleOnboardingSkip}
        />
      )}

      {/* Onboarding - Desktop Tour - Now using introJs directly in useEffect */}

      {/* Desktop Sidebar */}
      {user && (
        <DesktopSidebar
          view={view}
          setView={setView}
          players={players}
          activePlayerId={activePlayerId}
          setActivePlayerId={setActivePlayerId}
          seasons={allSeasons}
          activeSeasonId={activeSeasonId}
          setActiveSeasonId={setActiveSeasonId}
          teams={teams}
          teamSeasons={teamSeasons}
          activeTeamId={activeTeamId}
          setActiveTeamId={setActiveTeamId}
          activeTeamSeason={activeTeamSeason}
          setActiveTeamSeason={setActiveTeamSeason}
          setActiveRosterPlayerId={setActiveRosterPlayerId}
          isPro={isPro}
          isCoach={isCoach}
          user={user}
          onSignOut={handleSignOut}
          isTestingOffline={isTestingOffline}
          setIsTestingOffline={setIsTestingOffline}
          setIsOnline={setIsOnline}
          setShowOfflineInfo={setShowOfflineInfo}
          recordMatchGate={recordMatchGate}
          recordTeamMatchGate={recordTeamMatchGate}
        />
      )}

      {/* Header - Show BlogHeader on auth pages, SiteHeader otherwise */}
      {!user && !showLanding && !showingInAppReset && (
        <BlogHeader
          currentPage=""
          onSignUp={() => {
            setShowLanding(false);
            setAuthMode("signup");
          }}
        />
      )}
      {/* Static-style Header - Hide on landing page and auth pages */}
      {!(showLanding && !user) && (user || showingInAppReset) && <SiteHeader />}

      {/* Primary Navigation */}
      {user && (
        <PrimaryNavigation
          view={view}
          setView={(newView) => {
            setView(newView);
            // Clear active player when navigating away from team roster
            setActiveRosterPlayerId(null);
          }}
          isCoach={isCoach}
          isPro={isPro}
        />
      )}

      {/* Secondary Navigation for Teams */}
      {user && isCoach && (
        <TeamSecondaryNav
          user={user}
          view={view}
          setView={(newView) => {
            setView(newView);
            // Clear active player when changing team views
            setActiveRosterPlayerId(null);
          }}
          activeTeamId={activeTeamId}
          setActiveTeamId={setActiveTeamId}
          onCreateTeam={() => {
            setView('team-setup');
            setActiveRosterPlayerId(null);
          }}
          isCoach={isCoach}
        />
      )}

      {/* Secondary Navigation for Players - Show for all users including coaches */}
      {user && (
        <PlayerSecondaryNav
          user={user}
          view={view}
          setView={setView}
          activePlayerId={activePlayerId}
          activeSeasonId={activeSeasonId}
          players={players}
        />
      )}

      {/* Landing page - full width, outside container */}
      {!user && !showingInAppReset && showLanding && (
        <LandingView
          onGetStarted={() => {
            setShowLanding(false);
            setAuthMode("signup");
          }}
          onSignIn={() => {
            setShowLanding(false);
            setAuthMode("signin");
          }}
        />
      )}

      <Suspense fallback={<LoadingFallback />}>
      <div className="container" style={{ paddingTop: !user && showLanding ? "0" : "20px", maxWidth: view === "pricing" ? "1000px" : undefined }}>
        {/* Global notice */}
        {notice && (
          <Notice type={notice.type} onClose={() => setNotice(null)}>
            {notice.text}
          </Notice>
        )}



        {/* Shared Player Stats View (Parent Read-Only Access) */}
        {user && view === "shared-player-stats" && activePlayerId && currentSharedPlayer && (
          <SharedPlayerStatsView
            user={user}
            sharedPlayer={currentSharedPlayer}
            onBack={() => {
              setView('dashboard');
              setActivePlayerId(null);
            }}
          />
        )}

        {user && view === "account" && (
          <>
            <section className="card auth-card" style={{ marginBottom: "40px" }}>
              <h2 className="section-title">Account Settings</h2>
              <form onSubmit={saveAccountData} className="auth-form">
                <TextInput
                  label="Name"
                  value={accountForm.name}
                  onChange={(v) => setAccountForm(s => ({ ...s, name: v }))}
                  placeholder="Enter your name"
                />
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid var(--border)',
                      borderRadius: 4,
                      fontSize: 14,
                      backgroundColor: 'var(--bg-secondary)',
                      color: 'var(--muted)',
                      cursor: 'not-allowed'
                    }}
                  />
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
                    Contact{" "}
                    <a
                      href="mailto:hello@vballstat.com"
                      style={{ color: 'var(--primary)', textDecoration: 'none' }}
                    >
                      hello@vballstat.com
                    </a>{" "}
                    to change login email address
                  </div>
                </div>
                <div className="actions">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={accountLoading}
                    style={{
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap",
                      opacity: accountLoading ? 0.6 : 1
                    }}
                  >
                    {accountLoading ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => setView("dashboard")}
                    style={{
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Back to Dashboard
                  </button>
                </div>
              </form>
            </section>

            {/* Subscription Management */}
            <section className="card auth-card" style={{ marginBottom: "40px" }}>
              <h2 className="section-title">Subscription</h2>
              <div style={{ padding: '20px 0' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '16px',
                  padding: '16px',
                  background: (isPro || isCoach)
                    ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                    : 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: (isPro || isCoach) ? '2px solid rgba(102, 126, 234, 0.3)' : '1px solid var(--border)'
                }}>
                  <div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      marginBottom: '4px'
                    }}>
                      {isCoach ? '🏆 Coach Member' : isPro ? '✨ Pro Member' : 'Free Plan'}
                    </div>
                    {(isPro || isCoach) && subscriptionEndDate && (
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--muted)'
                      }}>
                        Renews on {subscriptionEndDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    )}
                    {!isPro && !isCoach && (
                      <div style={{
                        fontSize: '14px',
                        color: 'var(--muted)'
                      }}>
                        Limited to 1 player and 1 season
                      </div>
                    )}
                  </div>
                  {(isPro || isCoach) ? (
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={async () => {
                        try {
                          await redirectToCustomerPortal(user.email);
                        } catch (error) {
                          showNotice('error', 'Failed to open subscription management. Please try again.');
                        }
                      }}
                      style={{
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Manage Subscription
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => setView("pricing")}
                      style={{
                        padding: '10px 20px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Upgrade to Pro
                    </button>
                  )}
                </div>
                {isPro && (
                  <div style={{
                    fontSize: '13px',
                    color: 'var(--muted)',
                    padding: '0 16px'
                  }}>
                    Click "Manage Subscription" to update payment method, view invoices, or cancel your subscription.
                  </div>
                )}
              </div>
            </section>
          </>
        )}

        {/* Pricing View */}
        {view === "pricing" && (
          user ? (
            // Authenticated users see the in-app pricing view (free, pro, or coach)
            <PricingView
              user={user}
              onBack={() => setView("dashboard")}
            />
          ) : (
            // Non-authenticated users see the public marketing pricing page
            <PublicPricingView
              onGetStarted={() => {
                window.location.href = '/';
              }}
              onSignIn={() => {
                window.location.href = '/';
              }}
            />
          )
        )}

        {/* Privacy View */}
        {view === "privacy" && (
          <PrivacyView
            onBack={() => setView(user ? "dashboard" : "landing")}
          />
        )}

        {/* Changelog View */}
        {view === "changelog" && (
          <ChangelogView
            onBack={() => setView(user ? "dashboard" : "landing")}
          />
        )}

        {/* Contact View */}
        {view === "contact" && (
          <ContactView
            onBack={() => setView(user ? "dashboard" : "landing")}
          />
        )}

        {/* Blog Admin - Only admin view, public blog is in Blog.js */}
        {user && view === "blog-admin" && (
          <BlogAdminView user={user} />
        )}

        {/* Leaderboard View */}
        {user && view === "leaderboard" && (
          <LeaderboardView
            onBack={() => setView("dashboard")}
          />
        )}

        {/* Team Management Views */}
        {user && isCoach && view === "teams" && (
          <TeamListView
            user={user}
            onSelectTeam={(teamId) => {
              setActiveTeamId(teamId);
              setView("team-dashboard");
            }}
            onCreateTeam={() => setView("team-setup")}
            onBack={() => setView("dashboard")}
            onDeleteTeam={async (teamId) => {
              // Refresh teams list to remove deleted team from sidebar
              await refreshTeams();
              // If the deleted team was active, clear it
              if (activeTeamId === teamId) {
                setActiveTeamId(null);
              }
            }}
          />
        )}

        {user && isCoach && view === "team-setup" && (
          <TeamSetupView
            user={user}
            onComplete={async (teamId) => {
              setActiveTeamId(teamId);
              await refreshTeams(); // Refresh teams list to show new team in sidebar
              setView("team-roster");
            }}
            onBack={() => setView("teams")}
          />
        )}

        {user && isCoach && view === "team-dashboard" && activeTeamId && (
          <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
            <CoachDashboardView
              key={dashboardRefreshKey}
              user={user}
              teamId={activeTeamId}
              onManageRoster={() => {
                setRosterAutoOpenForm(true);
                setView("team-roster");
              }}
              onViewPlayer={(playerId) => {
                setActiveRosterPlayerId(playerId);
                setView("team-roster");
              }}
              onManageSeasons={() => setView("team-seasons")}
              onSelectSeason={(season, initialTab = 'history') => {
                setActiveTeamSeason(season);
                setSeasonDetailInitialTab(initialTab);
                setView("team-season-detail");
              }}
              onManageTournaments={() => {
                setTeamTournamentAutoOpenForm(true);
                setView("team-tournaments");
              }}
              onSelectTournament={(tournament) => {
                setActiveTeamTournament(tournament);
                setView("team-tournament-detail");
              }}
              onRecordMatch={() => setView("team-record-match")}
              onViewStats={() => setView("team-stats")}
              onGenerateWorkList={() => setView("team-work-list")}
              onBack={() => setView("teams")}
            />
          </div>
        )}

        {user && isCoach && view === "team-roster" && activeTeamId && !activeRosterPlayerId && (
          <RosterManagementView
            user={user}
            teamId={activeTeamId}
            autoOpenForm={rosterAutoOpenForm}
            onBack={() => setView("teams")}
            onViewPlayer={(playerId) => {
              setActiveRosterPlayerId(playerId);
            }}
          />
        )}

        {user && isCoach && view === "team-roster" && activeTeamId && activeRosterPlayerId && (
          <PlayerOverviewView
            user={user}
            teamId={activeTeamId}
            playerId={activeRosterPlayerId}
            onBack={() => {
              setActiveRosterPlayerId(null);
            }}
            isPro={isPro}
            subscriptionTier={isCoach ? 'coach_pro' : (isPro ? 'pro' : 'free')}
          />
        )}

        {user && isCoach && view === "team-seasons" && activeTeamId && (
          <TeamSeasonManagementView
            user={user}
            teamId={activeTeamId}
            onSelectSeason={(season, initialTab = 'history') => {
              setActiveTeamSeason(season);
              setSeasonDetailInitialTab(initialTab);
              setView("team-season-detail");
            }}
            onBack={() => setView("team-dashboard")}
          />
        )}

        {user && isCoach && view === "team-season-detail" && activeTeamId && activeTeamSeason && (
          <TeamSeasonDetailView
            user={user}
            teamId={activeTeamId}
            seasonId={activeTeamSeason.id}
            seasonName={activeTeamSeason.name}
            initialTab={seasonDetailInitialTab}
            onBack={() => setView("team-dashboard")}
            onEditMatch={(match) => {
              setEditingMatch(match);
              setView("team-record-match");
            }}
            isOnline={isOnline}
            updatePendingCount={updatePendingCount}
            OfflineManager={OfflineManager}
          />
        )}

        {user && isCoach && view === "team-tournaments" && activeTeamId && (
          <TeamTournamentManagementView
            user={user}
            teamId={activeTeamId}
            autoOpenForm={teamTournamentAutoOpenForm}
            onSelectTournament={(tournament) => {
              setActiveTeamTournament(tournament);
              setView("team-tournament-detail");
            }}
            onBack={() => setView("team-dashboard")}
          />
        )}

        {user && isCoach && view === "team-tournament-detail" && activeTeamId && activeTeamTournament && (
          <TeamTournamentDetailView
            user={user}
            teamId={activeTeamId}
            tournamentId={activeTeamTournament.id}
            tournamentName={activeTeamTournament.name}
            onBack={() => setView("team-dashboard")}
          />
        )}

        {user && isCoach && view === "team-record-match" && activeTeamId && (
          <TeamMatchRecordView
            user={user}
            teamId={activeTeamId}
            teamName={teams?.find(t => t.id === activeTeamId)?.name || ''}
            matchToEdit={editingMatch}
            onComplete={() => {
              setEditingMatch(null);
              // Redirect to match history (team-season-detail) if we have an active season
              if (activeTeamSeason) {
                setView("team-season-detail");
              } else {
                setView("team-dashboard");
              }
            }}
            onBack={() => {
              setEditingMatch(null);
              // Go back to match history if we have an active season, otherwise dashboard
              if (activeTeamSeason) {
                setView("team-season-detail");
              } else {
                setView("team-dashboard");
              }
            }}
            isOnline={isOnline}
            updatePendingCount={updatePendingCount}
            OfflineManager={OfflineManager}
          />
        )}

        {user && isCoach && view === "team-stats" && activeTeamId && (
          <TeamStatsView
            user={user}
            teamId={activeTeamId}
            onBack={() => setView("team-dashboard")}
          />
        )}

        {user && isCoach && view === "team-season-stats" && activeTeamId && activeTeamSeason && (
          <TeamStatsView
            user={user}
            teamId={activeTeamId}
            seasonId={activeTeamSeason.id}
            onBack={() => setView("team-season-detail")}
          />
        )}

        {user && isCoach && view === "team-reports" && activeTeamId && (
          <TeamReportsView
            user={user}
            teamId={activeTeamId}
            onBack={() => setView("team-dashboard")}
            setView={setView}
          />
        )}

        {user && isCoach && view === "team-comparison" && activeTeamId && (
          <TeamComparisonView
            user={user}
            teamId={activeTeamId}
            onBack={() => setView("team-reports")}
          />
        )}

        {user && isCoach && view === "team-player-progress" && activeTeamId && (
          <TeamPlayerProgressView
            user={user}
            teamId={activeTeamId}
            onBack={() => setView("team-reports")}
          />
        )}

        {user && view === "team-lineups" && activeTeamId && (
          <TeamLineupsView
            user={user}
            teamId={activeTeamId}
            onBack={() => setView("team-dashboard")}
            setView={setView}
            setActiveLineupId={setActiveLineupId}
            setGateModal={setGateModal}
            setLineupToDelete={setLineupToDelete}
            refreshTrigger={lineupsRefreshKey}
          />
        )}

        {user && view === "team-lineup-editor" && activeTeamId && (
          <TeamLineupEditorView
            user={user}
            teamId={activeTeamId}
            lineupId={activeLineupId}
            onBack={() => setView("team-lineups")}
          />
        )}

        {user && isCoach && view === "team-work-list" && activeTeamId && (
          <WorkListGeneratorView
            user={user}
            teamId={activeTeamId}
            onBack={() => setView("team-dashboard")}
          />
        )}

        {/* In-app Password Reset view */}
        {!user && showingInAppReset && (
          <section className="card auth-card" style={{ marginBottom: "40px" }}>
            <h2 className="section-title">Set a new password</h2>

            {resetLink.verifying ? (
              <div className="muted">Verifying link…</div>
            ) : (
              <>
                {!!resetPageError && <div className="notice error">{resetPageError}</div>}
                {!!resetPageSuccess && <div className="notice success">{resetPageSuccess}</div>}

                <div className="muted" style={{ marginBottom: 8 }}>
                  Resetting password for <strong>{resetLink.email || "your account"}</strong>
                </div>

                <form onSubmit={submitNewPassword} className="auth-form">
                  <TextInput
                    label="New password"
                    type="password"
                    value={newPw}
                    onChange={(v) => {
                      setNewPw(v);
                      setResetPageError("");
                    }}
                    inputRef={newPwRef}
                  />
                  <TextInput
                    label="Confirm new password"
                    type="password"
                    value={newPw2}
                    onChange={(v) => {
                      setNewPw2(v);
                      setResetPageError("");
                    }}
                  />
                  <div className="actions">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      style={{
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Update password
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => {
                        clearAuthParamsFromUrl();
                        setResetLink({ mode: null, oobCode: null, email: null, verifying: false });
                      }}
                      style={{
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>

                <p className="muted" style={{ marginTop: 8 }}>
                  If you requested a new link but don’t see the email yet, check your spam/junk (or
                  Promotions) folder.
                </p>
              </>
            )}
          </section>
        )}

        {/* Auth view (Sign in / Sign up / manual reset request) */}
        {!user && !showingInAppReset && !showLanding && (
          <section className="card auth-card" style={{ marginBottom: "40px" }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "16px"
            }}>
              <button
                onClick={() => setShowLanding(true)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "14px",
                  cursor: "pointer",
                  padding: "0"
                }}
              >
                ← Back
              </button>
            </div>

            <h2 className="section-title">
              {resetMode ? "Password Reset" : authMode === "signin" ? "Sign In" : "Sign Up"}
            </h2>

            {!!authError && <div className="notice error">{authError}</div>}
            {!!authSuccess && <div className="notice success">{authSuccess}</div>}

            {!resetMode ? (
              <form onSubmit={authMode === "signin" ? handleSignIn : handleSignUp} className="auth-form">
                {authMode === "signup" && (
                  <TextInput
                    label="Name"
                    value={authName}
                    onChange={(v) => {
                      setAuthName(v);
                      setAuthError("");
                    }}
                    placeholder="Enter your name"
                  />
                )}
                <TextInput
                  label="Email"
                  type="email"
                  value={authEmail}
                  onChange={(v) => {
                    setAuthEmail(v);
                    setAuthError("");
                  }}
                />
                <TextInput
                  label="Password"
                  type="password"
                  value={authPassword}
                  onChange={(v) => {
                    setAuthPassword(v);
                    setAuthError("");
                  }}
                  inputRef={pwInputRef}
                  className={shakePw ? "shake" : ""}
                />

                <div className="actions">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    style={{
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {authMode === "signin" ? "Sign In" : "Create Account"}
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => switchAuthMode(authMode === "signin" ? "signup" : "signin")}
                    style={{
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {authMode === "signin" ? "Sign Up" : "Sign In"}
                  </button>
                  {authMode === "signin" && (
                    <button
                      className="btn btn-danger"
                      type="button"
                      onClick={() => {
                        setResetMode(true);
                        setAuthError("");
                        setAuthSuccess("");
                      }}
                      style={{
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Forgot?
                    </button>
                  )}
                </div>
              </form>
            ) : (
              <>
                <form onSubmit={handlePasswordReset} className="auth-form">
                  <TextInput
                    label="Enter your account email"
                    type="email"
                    value={authEmail}
                    onChange={(v) => {
                      setAuthEmail(v);
                      setAuthError("");
                      setAuthSuccess("");
                    }}
                  />
                  <div className="actions">
                    <button
                      className="btn btn-primary"
                      type="submit"
                      style={{
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Send reset link
                    </button>
                    <button
                      className="btn btn-primary"
                      type="button"
                      onClick={() => {
                        setResetMode(false);
                        setAuthError("");
                      }}
                      style={{
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Back to sign in
                    </button>
                  </div>
                </form>
                <p className="muted" style={{ marginTop: 8 }}>
                  Don’t see the email in a minute? Check your spam/junk (or Promotions) folder and
                  mark it as not spam.
                </p>
              </>
            )}
          </section>
        )}

        {/* Player setup (create or edit) */}
        {user && view === "player-setup" && (
          <PlayerSetupView
            playerEditMode={playerEditMode}
            playerForm={playerForm}
            setPlayerForm={setPlayerForm}
            savePlayerProfile={savePlayerProfile}
            players={players}
            setView={setView}
            setShowPosInfo={setShowPosInfo}
            handlePlayerPhotoUpload={handlePlayerPhotoUpload}
          />
        )}

        {/* Add Season view */}
        {user && view === "season-setup" && (
          <SeasonSetupView
            players={players}
            seasonSetupForm={seasonSetupForm}
            setSeasonSetupForm={setSeasonSetupForm}
            seasonSetupPlayerId={seasonSetupPlayerId}
            setSeasonSetupPlayerId={setSeasonSetupPlayerId}
            createSeasonFromSetup={createSeasonFromSetup}
            setView={setView}
            setPlayerEditMode={setPlayerEditMode}
            setActivePlayerId={setActivePlayerId}
          />
        )}

        {/* ---------- NEW: Season Edit view ---------- */}
        {user && view === "season-edit" && (
          <SeasonEditView
            seasonEditForm={seasonEditForm}
            setSeasonEditForm={setSeasonEditForm}
            saveSeasonEdit={saveSeasonEdit}
            setView={setView}
          />
        )}

        {/* ---------- Tournament Creation view ---------- */}
        {user && view === "tournament-create" && FEATURES.TOURNAMENTS && (
          <TournamentCreateView
            tournamentForm={tournamentForm}
            setTournamentForm={setTournamentForm}
            saveTournament={saveTournament}
            tournamentLoading={tournamentLoading}
            setView={setView}
          />
        )}

        {/* ---------- Tournament Edit view ---------- */}
        {user && view === "tournament-edit" && FEATURES.TOURNAMENTS && editingTournament && (
          <TournamentEditView
            tournamentEditForm={tournamentEditForm}
            setTournamentEditForm={setTournamentEditForm}
            saveTournamentEdit={saveTournamentEdit}
            setEditingTournament={setEditingTournament}
            setView={setView}
          />
        )}

        {/* ---------- Tournament Detail view ---------- */}
        {user && view === "tournament-detail" && FEATURES.TOURNAMENTS && activeTournamentId && (
          <section className="card" style={{ marginBottom: "40px" }}>
            {(() => {
              const tournament = tournaments.find(t => t.id === activeTournamentId);
              const stats = calculateTournamentStats(activeTournamentId);
              const tournamentMatches = matches.filter(m => m.tournamentId === activeTournamentId);
              
              if (!tournament) {
                return (
                  <div>
                    <h2 className="section-title">Tournament Not Found</h2>
                    <button
                      className="btn btn-primary"
                      onClick={() => setView("season-detail")}
                      style={{
                        padding: "12px 24px",
                        fontSize: "12px",
                        fontWeight: "600",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        whiteSpace: "nowrap"
                      }}
                    >
                      Back to Season
                    </button>
                  </div>
                );
              }
              
              return (
                <div>
                  {/* Breadcrumb */}
                  <div className="breadcrumb-nav tournament-breadcrumb" style={{ marginBottom: "16px" }}>
                    <button
                      onClick={() => setView("season-detail")}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#007AFF",
                        fontSize: "14px",
                        cursor: "pointer",
                        padding: "0"
                      }}
                    >
                      ← Back to Season
                    </button>
                  </div>

                  <div style={{ marginBottom: "8px" }}>
                    <div style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px"
                    }}>
                      <h2 className="section-title" style={{ margin: "0" }}>
                        {tournament.name}
                      </h2>

                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => startEditTournament(tournament)}
                          style={{
                            width: '40px',
                            height: '40px',
                            background: '#F2F2F7',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#E5E5EA';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#F2F2F7';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          title="Edit tournament"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => setPendingDeleteTournamentId(tournament.id)}
                          style={{
                            width: '40px',
                            height: '40px',
                            background: '#F2F2F7',
                            border: 'none',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#FFEBEA';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#F2F2F7';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                          title="Delete tournament"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                    
                    {/* Tournament Info */}
                    <div className="muted" style={{ marginBottom: "16px" }}>
                      {tournament.startDate && (
                        <div>
                          <strong>Start Date:</strong> {new Date(tournament.startDate).toLocaleDateString()}
                        </div>
                      )}
                      {tournament.endDate && (
                        <div>
                          <strong>End Date:</strong> {new Date(tournament.endDate).toLocaleDateString()}
                        </div>
                      )}
                      {tournament.date && !tournament.startDate && (
                        <div>
                          <strong>Date:</strong> {new Date(tournament.date).toLocaleDateString()}
                        </div>
                      )}
                      {tournament.location && (
                        <div>
                          <strong>Location:</strong> {tournament.location}
                        </div>
                      )}
                      {tournament.notes && (
                        <div style={{ marginTop: "8px", padding: "8px", background: "var(--bg-light)", borderRadius: "4px" }}>
                          <strong>Notes:</strong> {tournament.notes}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tournament Stats - Styled like season overview */}
                  {stats.matchesCount > 0 ? (
                    <section style={{
                      background: "#fff",
                      borderRadius: "12px",
                      padding: "12px",
                      marginBottom: "24px",
                      minHeight: "80px"
                    }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>Tournament Totals</div>
                      </div>

                      {/* Overview Table */}
                      <table style={{
                        width: "100%",
                        marginBottom: "24px",
                        borderCollapse: "collapse",
                        fontSize: "14px"
                      }}>
                        <tbody>
                          <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                            <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Matches</td>
                            <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.matchesCount}</td>
                          </tr>
                          <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                            <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Sets Won</td>
                            <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.setWins}</td>
                          </tr>
                          <tr>
                            <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Sets Lost</td>
                            <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.setLosses}</td>
                          </tr>
                        </tbody>
                      </table>

                      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                        {/* Offensive Stats */}
                        <div>
                          <div
                            onClick={() => setShowOffensiveStats(!showOffensiveStats)}
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#667eea",
                              marginBottom: showOffensiveStats ? "16px" : "0",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              userSelect: "none"
                            }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ fontSize: "20px" }}>⚡</span>
                              Offensive
                            </div>
                            <span style={{ fontSize: "16px", transition: "transform 0.2s ease", transform: showOffensiveStats ? "rotate(90deg)" : "rotate(0)", color: "#000" }}>▶</span>
                          </div>
                          {showOffensiveStats && (
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Kills</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.kills}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Hit in Play</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.hitInPlay}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Hitting Errors</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.hittingErrors}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Tips</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.tip}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Hitting %</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.hittingPct.toFixed(3)}</td>
                              </tr>
                              <tr>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Kill %</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.killPct}%</td>
                              </tr>
                            </tbody>
                          </table>
                          )}
                        </div>

                        {/* Serving Stats */}
                        <div>
                          <div
                            onClick={() => setShowServingStats(!showServingStats)}
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#764ba2",
                              marginBottom: showServingStats ? "16px" : "0",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              userSelect: "none"
                            }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ fontSize: "20px" }}>🏐</span>
                              Serving
                            </div>
                            <span style={{ fontSize: "16px", transition: "transform 0.2s ease", transform: showServingStats ? "rotate(90deg)" : "rotate(0)", color: "#000" }}>▶</span>
                          </div>
                          {showServingStats && (
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Aces</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.aces}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Serve Attempts</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.serveAttempts}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Service Errors</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.serviceErrors}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Serving %</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.servingPct}%</td>
                              </tr>
                              <tr>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Ace %</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.acePct}%</td>
                              </tr>
                            </tbody>
                          </table>
                          )}
                        </div>

                        {/* Defensive & Other Stats */}
                        <div>
                          <div
                            onClick={() => setShowDefensiveStats(!showDefensiveStats)}
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#F39C12",
                              marginBottom: showDefensiveStats ? "16px" : "0",
                              textTransform: "uppercase",
                              letterSpacing: "0.8px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              cursor: "pointer",
                              userSelect: "none"
                            }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                              <span style={{ fontSize: "20px" }}>🛡️</span>
                              Defensive & Other
                            </div>
                            <span style={{ fontSize: "16px", transition: "transform 0.2s ease", transform: showDefensiveStats ? "rotate(90deg)" : "rotate(0)", color: "#000" }}>▶</span>
                          </div>
                          {showDefensiveStats && (
                          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                            <tbody>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Digs</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.digs}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Blocks</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.sets}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Block Touch</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.blockTouch}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Block Errors</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.blockError}</td>
                              </tr>
                              <tr style={{ borderBottom: "1px solid #E3E2DF" }}>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Assists</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.assists}</td>
                              </tr>
                              <tr>
                                <td style={{ padding: "12px 0", color: "#6B6966", fontWeight: "500" }}>Sets (W-L)</td>
                                <td style={{ padding: "12px 0", textAlign: "right", fontWeight: "600", color: "#37352F" }}>{stats.setWins}-{stats.setLosses}</td>
                              </tr>
                            </tbody>
                          </table>
                          )}
                        </div>
                      </div>
                    </section>
                  ) : (
                    <div className="muted" style={{ padding: "20px", textAlign: "center", border: "1px dashed #ddd", borderRadius: "8px", marginBottom: "20px" }}>
                      No matches recorded for this tournament yet.
                    </div>
                  )}

                  {/* Tournament Matches - Detailed table like season view */}
                  {tournamentMatches.length > 0 && (
                    <section style={{
                      background: "#fff",
                      border: "1px solid var(--border)",
                      borderRadius: "12px",
                      padding: "24px",
                      marginBottom: "24px"
                    }}>
                      <div style={{ marginBottom: "16px" }}>
                        <h3 style={{ margin: "0 0 8px 0", fontSize: "18px", fontWeight: "600" }}>Tournament Match History</h3>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <span className="muted" style={{ fontSize: "12px" }}>
                            {tournamentMatches.length} match{tournamentMatches.length !== 1 ? 'es' : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowStatsInfo(true)}
                            aria-label="What do these abbreviations mean?"
                            title="What do these mean?"
                            style={{
                              background: "var(--muted)",
                              border: "none",
                              color: "white",
                              fontSize: "12px",
                              cursor: "pointer",
                              borderRadius: "50%",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontWeight: "600",
                              transition: "all 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "var(--primary)";
                              e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "var(--muted)";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            ?
                          </button>
                        </div>
                      </div>

                      {/* Desktop Table View */}
                      <div className="tournament-matches-table-container">
                        <table className="match-history-table">
                          <thead>
                            <tr>
                              <th style={{ width: "40px" }}></th>
                              <th onClick={() => setDateSortDir((d) => (d === "asc" ? "desc" : "asc"))} style={{ cursor: "pointer" }}>
                                Date {dateSortDir === "asc" ? "▲" : "▼"}
                              </th>
                              <th>Opponent</th>
                              <th>Result</th>
                              <th>Score</th>
                              <th>Hitting %</th>
                              <th>Serving %</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tournamentMatches.length === 0 && (
                              <tr>
                                <td colSpan="8" style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
                                  No matches found for this tournament.
                                </td>
                              </tr>
                            )}
                            {tournamentMatches
                              .sort((a, b) => {
                                const dateA = new Date(a.createdAtISO);
                                const dateB = new Date(b.createdAtISO);
                                return dateSortDir === "asc" ? dateA - dateB : dateB - dateA;
                              })
                              .map((m) => {
                                const aa = Number(m.hitInPlay || m.attackAttempts || 0);
                                const k  = Number(m.kills || 0);
                                const he = Number(m.hittingErrors || 0);
                                const sa = Number(m.serveAttempts || 0);
                                const se = Number(m.serviceErrors || 0);
                                const ac = Number(m.aces || 0);
                                const hittingPctRow = (aa + k + he) ? ((k - he) / (aa + k + he) * 100).toFixed(1) : "0.0";
                                const servingPct = sa ? ((sa - se) / sa * 100).toFixed(1) : "0.0";
                                const { w, l } = getSetWL(m.setScores || []);
                                const isMatchWin = w > l;
                                const isExpanded = expandedTournamentMatchIds.has(m.id);

                                return (
                                  <React.Fragment key={m.id}>
                                    <tr className="match-history-row">
                                      <td>
                                        <button
                                          className="expand-btn"
                                          onClick={() => toggleExpandedTournamentMatch(m.id)}
                                          title={isExpanded ? "Collapse" : "Expand"}
                                        >
                                          {isExpanded ? "▼" : "▶"}
                                        </button>
                                      </td>
                                      <td>{new Date(m.createdAtISO).toLocaleDateString()}</td>
                                      <td style={{ fontWeight: "600" }}>{m.opponent || "Unknown"}</td>
                                      <td>
                                        <span className={`result-badge ${isMatchWin ? 'win' : 'loss'}`}>
                                          {isMatchWin ? "W" : "L"}
                                        </span>
                                      </td>
                                      <td>{w}-{l}</td>
                                      <td>{hittingPctRow}%</td>
                                      <td>{servingPct}%</td>
                                      <td>
                                        <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                                          <button
                                            className="table-action-btn edit-btn"
                                            onClick={() => startEditMatch(m)}
                                            title="Edit match"
                                          >
                                            ✏️
                                          </button>
                                          <button
                                            className="table-action-btn delete-btn"
                                            onClick={() => {
                                              if (!isOnline) {
                                                setShowOfflineDeleteModal(true);
                                              } else {
                                                setPendingDeleteMatchId(m.id);
                                              }
                                            }}
                                            title="Delete match"
                                          >
                                            🗑️
                                          </button>
                                        </div>
                                      </td>
                                    </tr>

                                    {/* Expanded row with full stats */}
                                    {isExpanded && (
                                      <tr className="expanded-row">
                                        <td colSpan="8">
                                          <div className="expanded-content">
                                            <div className="expanded-stats-grid">
                                              {/* Hitting Stats */}
                                              <div className="stat-section">
                                                <h4>Hitting</h4>
                                                <div className="stat-item">
                                                  <span>Kills:</span>
                                                  <strong>{k}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Errors:</span>
                                                  <strong>{he}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Attempts:</span>
                                                  <strong>{aa}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Hitting %:</span>
                                                  <strong>{hittingPctRow}%</strong>
                                                </div>
                                              </div>

                                              {/* Serving Stats */}
                                              <div className="stat-section">
                                                <h4>Serving</h4>
                                                <div className="stat-item">
                                                  <span>Aces:</span>
                                                  <strong>{ac}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Errors:</span>
                                                  <strong>{se}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Attempts:</span>
                                                  <strong>{sa}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Serving %:</span>
                                                  <strong>{servingPct}%</strong>
                                                </div>
                                              </div>

                                              {/* Defense Stats */}
                                              <div className="stat-section">
                                                <h4>Defense</h4>
                                                <div className="stat-item">
                                                  <span>Digs:</span>
                                                  <strong>{m.digs || 0}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Blocks:</span>
                                                  <strong>{((m.blocks || 0) + ((m.halfBlock || 0) * 0.5)).toFixed(1)}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Block Assists:</span>
                                                  <strong>{m.blockAssists || 0}</strong>
                                                </div>
                                                {((m.digErrors || 0) > 0) && (
                                                  <div className="stat-item">
                                                    <span>Dig Errors:</span>
                                                    <strong>{m.digErrors || 0}</strong>
                                                  </div>
                                                )}
                                                {((m.blockError || m.blockErrors || 0) > 0) && (
                                                  <div className="stat-item">
                                                    <span>Block Errors:</span>
                                                    <strong>{m.blockError || m.blockErrors || 0}</strong>
                                                  </div>
                                                )}
                                              </div>

                                              {/* Other Stats */}
                                              <div className="stat-section">
                                                <h4>Other</h4>
                                                <div className="stat-item">
                                                  <span>Tips:</span>
                                                  <strong>{m.tip || 0}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>Assists:</span>
                                                  <strong>{m.assists || 0}</strong>
                                                </div>
                                                <div className="stat-item">
                                                  <span>SR Touches:</span>
                                                  <strong>{m.srTouches || 0}</strong>
                                                </div>
                                              </div>
                                            </div>

                                            {/* Set Scores */}
                                            {m.setScores && m.setScores.length > 0 && (
                                              <div className="set-scores-section">
                                                <h4>Set Scores</h4>
                                                <div className="set-scores-list">
                                                  {m.setScores.map((score, idx) => (
                                                    <div key={idx} className="set-score-item">
                                                      Set {idx + 1}: <strong>{score.us}-{score.opp}</strong>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile-friendly match cards */}
                      <div className="tournament-matches-cards" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        {/* Sort button */}
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "flex-end", 
                          marginBottom: "12px" 
                        }}>
                                <button
                                  onClick={() => setDateSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                                  aria-label={`Sort by date ${dateSortDir === "asc" ? "descending" : "ascending"}`}
                                  style={{
                              background: "var(--bg-light)",
                              border: "1px solid var(--border)",
                              borderRadius: "6px",
                              padding: "6px 8px",
                                    cursor: "pointer",
                                    display: "inline-flex",
                                    alignItems: "center",
                              gap: "6px",
                              fontSize: "12px",
                              color: "var(--text)"
                                  }}
                                >
                            <span>Sort by Date</span>
                                  <span>{dateSortDir === "asc" ? "▲" : "▼"}</span>
                                </button>
                        </div>
                            {tournamentMatches
                              .sort((a, b) => {
                                const dateA = new Date(a.createdAtISO);
                                const dateB = new Date(b.createdAtISO);
                                return dateSortDir === "asc" ? dateA - dateB : dateB - dateA;
                              })
                              .map((match) => {
                                // Calculate percentages like in season table
                                const sa = Number(match.serveAttempts || 0);
                                const se = Number(match.serviceErrors || 0);
                                // const ac = Number(match.aces || 0);
                                const aa = Number(match.hitInPlay || match.attackAttempts || 0);
                                const k = Number(match.kills || 0);
                                const he = Number(match.hittingErrors || 0);
                                const de = Number(match.digErrors || 0);
                                
                                const servingPctRow = sa ? Math.round(((sa - Math.max(0, se)) / sa) * 100) : 0;
                                // const acePctRow = sa ? Math.round((Math.max(0, ac) / sa) * 100) : 0;
                                const totalAttempts = aa + k + he;
                                const hittingPctRow = totalAttempts ? Math.round(((k - Math.max(0, he)) / totalAttempts) * 1000) / 1000 : 0;
                                const killPctRow = totalAttempts ? Math.round((k / totalAttempts) * 100) : 0;
                                
                                const isExpanded = expandedTournamentMatch === match.id;

                                return (
                              <div key={match.id} style={{
                                borderBottom: "1px solid var(--border)",
                                paddingBottom: "16px",
                                marginBottom: "16px"
                              }}>
                                {/* Header row with date, opponent, and result - Clickable */}
                                <div
                                  onClick={() => setExpandedTournamentMatch(isExpanded ? null : match.id)}
                                  style={{
                                    cursor: "pointer",
                                    userSelect: "none"
                                  }}
                                >
                                  <div style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "12px"
                                  }}>
                                    <div>
                                      <div style={{
                                        fontWeight: "700",
                                        color: "var(--text)",
                                        fontSize: "16px",
                                        marginBottom: "4px"
                                      }}>
                                        {new Date(match.createdAtISO).toLocaleDateString()}
                                      </div>
                                      <div style={{
                                        fontSize: "14px",
                                        color: "var(--muted)",
                                        fontWeight: "500"
                                      }}>
                                        vs {match.opponent || "Unknown"}
                                      </div>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                      {/* Match Result Badge */}
                                      <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "6px",
                                        background: match.won ? "var(--success)" : "var(--danger)",
                                        color: "white",
                                        padding: "4px 8px",
                                        borderRadius: "6px",
                                        fontSize: "12px",
                                        fontWeight: "600"
                                      }}>
                                        <span>{match.setsWon || 0}-{match.setsLost || 0}</span>
                                        <span style={{
                                          fontSize: "10px",
                                          opacity: "0.9",
                                          textTransform: "uppercase",
                                          letterSpacing: "0.3px"
                                        }}>
                                          {match.won ? "W" : "L"}
                                        </span>
                                      </div>

                                      {/* Expand/Collapse Arrow */}
                                      <span style={{
                                        fontSize: "16px",
                                        transition: "transform 0.2s ease",
                                        transform: isExpanded ? "rotate(180deg)" : "rotate(0)",
                                        color: "var(--muted)"
                                      }}>
                                        ▼
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {/* Expandable Content */}
                                {isExpanded && (
                                  <div style={{ marginTop: "16px" }}>
                                    {/* Set Scores - Horizontal Layout */}
                                    {(match.setScores || []).length > 0 && (
                                      <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flexWrap: "wrap",
                                        marginBottom: "20px"
                                      }}>
                                      <span style={{
                                        fontSize: "12px",
                                        color: "var(--muted)",
                                        fontWeight: "600",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                      }}>
                                        Sets:
                                      </span>
                                      <div style={{
                                        display: "flex",
                                        gap: "6px",
                                        alignItems: "center"
                                      }}>
                                        {(match.setScores || []).map((s, i) => {
                                          const us = Number(s?.us || 0);
                                          const opp = Number(s?.opp || 0);
                                          const isSetWin = us > opp;
                                          return (
                                            <div key={i} style={{
                                              background: isSetWin ? "var(--success)" : "var(--bg-light)",
                                              color: isSetWin ? "white" : "var(--text)",
                                              padding: "4px 8px",
                                              borderRadius: "6px",
                                              fontSize: "13px",
                                              fontWeight: "600",
                                              fontVariantNumeric: "tabular-nums",
                                              border: isSetWin ? "none" : "1px solid var(--border)"
                                            }}>
                                              {us}–{opp}
                                            </div>
                                          );
                                        })}
                                      </div>
                                      </div>
                                    )}

                                    {/* Stats grid */}
                                <div style={{ 
                                  display: "grid", 
                                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                                  gap: "16px",
                                  fontSize: "14px"
                                }}>
                                  {/* Hitting stats */}
                                  <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    gap: "10px",
                                    background: "var(--bg-light)",
                                    padding: "12px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,0,0,0.05)"
                                  }}>
                                    <div style={{ 
                                      fontWeight: "700", 
                                      color: "var(--primary)", 
                                      fontSize: "11px",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      marginBottom: "4px"
                                    }}>HITTING</div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Kills:</span>
                                      <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--text)" }}>{match.kills || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Attempts:</span>
                                      <span style={{ fontWeight: "500" }}>{match.hitInPlay || match.attackAttempts || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Errors:</span>
                                      <span style={{ fontWeight: "500" }}>{match.hittingErrors || 0}</span>
                                    </div>
                                    <div style={{ 
                                      display: "flex", 
                                      justifyContent: "space-between", 
                                      alignItems: "center",
                                      paddingTop: "6px",
                                      borderTop: "1px solid var(--border)",
                                      marginTop: "4px"
                                    }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Hitting %:</span>
                                      <span style={{ 
                                        fontWeight: "700", 
                                        fontSize: "15px",
                                        color: "var(--primary)"
                                      }}>{hittingPctRow.toFixed(3)}</span>
                                    </div>
                                  </div>

                                  {/* Serving stats */}
                                  <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    gap: "10px",
                                    background: "var(--bg-light)",
                                    padding: "12px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,0,0,0.05)"
                                  }}>
                                    <div style={{ 
                                      fontWeight: "700", 
                                      color: "var(--primary)", 
                                      fontSize: "11px",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      marginBottom: "4px"
                                    }}>SERVING</div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Aces:</span>
                                      <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--text)" }}>{match.aces || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Attempts:</span>
                                      <span style={{ fontWeight: "500" }}>{match.serveAttempts || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Errors:</span>
                                      <span style={{ fontWeight: "500" }}>{match.serviceErrors || 0}</span>
                                    </div>
                                    <div style={{ 
                                      display: "flex", 
                                      justifyContent: "space-between", 
                                      alignItems: "center",
                                      paddingTop: "6px",
                                      borderTop: "1px solid var(--border)",
                                      marginTop: "4px"
                                    }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Srv %:</span>
                                      <span style={{ 
                                        fontWeight: "700", 
                                        fontSize: "15px",
                                        color: "var(--primary)"
                                      }}>{servingPctRow}%</span>
                                    </div>
                                  </div>

                                  {/* Defense stats */}
                                  <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    gap: "10px",
                                    background: "var(--bg-light)",
                                    padding: "12px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,0,0,0.05)"
                                  }}>
                                    <div style={{ 
                                      fontWeight: "700", 
                                      color: "var(--primary)", 
                                      fontSize: "11px",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      marginBottom: "4px"
                                    }}>DEFENSE</div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Digs:</span>
                                      <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--text)" }}>{match.digs || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Dig Errors:</span>
                                      <span style={{ fontWeight: "500" }}>{de}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Blocks:</span>
                                      <span style={{ fontWeight: "500" }}>{match.blocks || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Block Touch:</span>
                                      <span style={{ fontWeight: "500" }}>{match.blockTouch || 0}</span>
                                    </div>
                                    <div style={{ 
                                      display: "flex", 
                                      justifyContent: "space-between", 
                                      alignItems: "center",
                                      paddingTop: "6px",
                                      borderTop: "1px solid var(--border)",
                                      marginTop: "4px"
                                    }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Block Error:</span>
                                      <span style={{ 
                                        fontWeight: "700", 
                                        fontSize: "15px",
                                        color: "var(--primary)"
                                      }}>{match.blockError || 0}</span>
                                    </div>
                                  </div>

                                  {/* Additional stats */}
                                  <div style={{ 
                                    display: "flex", 
                                    flexDirection: "column", 
                                    gap: "10px",
                                    background: "var(--bg-light)",
                                    padding: "12px",
                                    borderRadius: "12px",
                                    border: "1px solid rgba(0,0,0,0.05)"
                                  }}>
                                    <div style={{ 
                                      fontWeight: "700", 
                                      color: "var(--primary)", 
                                      fontSize: "11px",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.5px",
                                      marginBottom: "4px"
                                    }}>OTHER</div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Tips:</span>
                                      <span style={{ fontWeight: "500" }}>{match.tip || 0}</span>
                                    </div>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Assists:</span>
                                      <span style={{ fontWeight: "500" }}>{match.assists || 0}</span>
                                    </div>
                                    <div style={{ 
                                      display: "flex", 
                                      justifyContent: "space-between", 
                                      alignItems: "center",
                                      paddingTop: "6px",
                                      borderTop: "1px solid var(--border)",
                                      marginTop: "4px"
                                    }}>
                                      <span style={{ color: "var(--muted)", fontSize: "13px" }}>Kill %:</span>
                                      <span style={{ 
                                        fontWeight: "700", 
                                        fontSize: "15px",
                                        color: "var(--primary)"
                                      }}>{killPctRow}%</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Action buttons */}
                                <div style={{ 
                                  display: "flex", 
                                  gap: "8px", 
                                  justifyContent: "flex-end",
                                  paddingTop: "12px",
                                  marginTop: "8px",
                                  flexWrap: "wrap"
                                }}>
                                        <button
                                          className="btn btn-primary"
                                          onClick={() => startEditMatch(match)}
                                          style={{
                                            padding: "8px 16px",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            cursor: "pointer",
                                            transition: "all 0.3s ease",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            whiteSpace: "nowrap",
                                            minWidth: "60px"
                                          }}
                                        >
                                          Edit
                                        </button>
                                        {match.notes && (
                                          <button
                                            className="btn btn-primary"
                                            onClick={() => setViewNotesMatch(match)}
                                            style={{
                                              padding: "8px 16px",
                                              fontSize: "13px",
                                              fontWeight: "600",
                                              cursor: "pointer",
                                              transition: "all 0.3s ease",
                                              display: "flex",
                                              alignItems: "center",
                                              justifyContent: "center",
                                              whiteSpace: "nowrap",
                                              minWidth: "60px"
                                            }}
                                          >
                                            Notes
                                          </button>
                                        )}
                                        <button
                                          className="btn btn-danger"
                                          onClick={() => {
                                            if (!isOnline) {
                                              setShowOfflineDeleteModal(true);
                                            } else {
                                              setPendingDeleteMatchId(match.id);
                                            }
                                          }}
                                          style={{
                                            padding: "8px 16px",
                                            fontSize: "13px",
                                            fontWeight: "600",
                                            cursor: "pointer",
                                            transition: "all 0.3s ease",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            whiteSpace: "nowrap",
                                            minWidth: "60px"
                                          }}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                  </div>
                                )}
                              </div>
                                );
                              })}
                      </div>
                    </section>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        {/* TRENDS VIEW */}
        {user && view === "trends" && (
          <section className="card" style={{ marginBottom: "48px" }}>
            <div className="board-head">
              <h2 className="section-title">Trends Analysis</h2>
              <div className="board-actions">
                {trendsSeasonId && trendsPlayerId && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setActiveSeasonId(trendsSeasonId);
                      setView("season-detail");
                    }}
                    style={{
                      marginRight: "8px",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease"
                    }}
                  >
                    View Season
                  </button>
                )}
                <button
                  className="btn btn-muted"
                  onClick={() => setView("dashboard")}
                  style={{
                    padding: "12px 24px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease"
                  }}
                >
                  Back to Dashboard
                </button>
              </div>
            </div>

            {/* Player Selector */}
            <div className="field" style={{ marginBottom: "20px" }}>
              <label className="field-label">Select Player</label>
              <select
                className="input"
                value={trendsPlayerId || ""}
                onChange={(e) => {
                  setTrendsPlayerId(e.target.value || null);
                  setTrendsSeasonId(null); // Clear season when player changes
                }}
              >
                <option value="">All Players</option>
                {players.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Season Selector */}
            <div className="field" style={{ marginBottom: "20px" }}>
              <label className="field-label">Select Season to Analyze</label>
              <select
                className="input"
                value={trendsSeasonId || ""}
                onChange={(e) => setTrendsSeasonId(e.target.value || null)}
                disabled={!trendsPlayerId}
              >
                <option value="">Choose a season...</option>
                {trendsPlayerId && (() => {
                  // Get all matches for the selected player (not filtered by active player)
                  const playerMatches = allMatches.filter(m => m.playerId === trendsPlayerId);
                  
                  const playerSeasons = [...new Set(
                    playerMatches.map(m => m.seasonId)
                  )];
                  
                  return playerSeasons.map(seasonId => {
                    const season = allSeasons.find(s => s.id === seasonId);
                    return season ? (
                      <option key={season.id} value={season.id}>
                        {season.name} {season.year ? `(${season.year})` : ""}
                      </option>
                    ) : null;
                  }).filter(Boolean);
                })()}
              </select>
            </div>

            {!trendsPlayerId ? (
              <div className="muted" style={{ textAlign: "center", padding: "40px" }}>
                Please select a player to view their available seasons and trends analysis.
              </div>
            ) : !trendsSeasonId ? (
              <div className="muted" style={{ textAlign: "center", padding: "40px" }}>
                Please select a season to view trends analysis for {players.find(p => p.id === trendsPlayerId)?.name || 'the selected player'}.
              </div>
            ) : trendsSeasonId && trendsPlayerId && (() => {
              let seasonMatches = allMatches.filter(m => m.seasonId === trendsSeasonId && m.playerId === trendsPlayerId);
              const selectedSeason = allSeasons.find(s => s.id === trendsSeasonId);
              
              if (seasonMatches.length === 0) {
                return (
                  <div className="muted" style={{ textAlign: "center", padding: "40px" }}>
                    {trendsPlayerId 
                      ? `No matches found for ${players.find(p => p.id === trendsPlayerId)?.name || 'selected player'} in this season. Record some matches to see trends!`
                      : "No matches found for this season. Record some matches to see trends!"
                    }
                  </div>
                );
              }

              // Calculate basic stats
              const sortedMatches = seasonMatches.sort((a, b) => new Date(a.matchDate) - new Date(b.matchDate));
              const totalMatches = sortedMatches.length;
              const wins = sortedMatches.filter(m => {
                const scores = m.setScores || [];
                const w = scores.filter(s => (s.us || 0) > (s.opp || 0)).length;
                const l = scores.filter(s => (s.us || 0) < (s.opp || 0)).length;
                return w > l;
              }).length;
              const winRate = totalMatches > 0 ? Math.round((wins / totalMatches) * 100) : 0;

              // Calculate trends - split season into early vs recent matches
              const midPoint = Math.ceil(totalMatches / 2);
              const earlyMatches = sortedMatches.slice(0, midPoint);
              const recentMatches = sortedMatches.slice(midPoint);

                              const calculateStats = (matches) => {
                  const totalKills = matches.reduce((sum, m) => sum + (m.kills || 0), 0);
                  const totalHits = matches.reduce((sum, m) => sum + (m.hitInPlay || m.attackAttempts || 0), 0);
                  const totalHittingErrors = matches.reduce((sum, m) => sum + (m.hittingErrors || 0), 0);
                  const totalTips = matches.reduce((sum, m) => sum + (m.tip || 0), 0);
                  const totalAces = matches.reduce((sum, m) => sum + (m.aces || 0), 0);
                  const totalServes = matches.reduce((sum, m) => sum + (m.serveAttempts || 0), 0);
                  const totalServiceErrors = matches.reduce((sum, m) => sum + (m.serviceErrors || 0), 0);
                  const totalDigs = matches.reduce((sum, m) => sum + (m.digs || 0), 0);
                  const totalDigErrors = matches.reduce((sum, m) => sum + (m.digErrors || 0), 0);
                  const totalBlockTouch = matches.reduce((sum, m) => sum + (m.blockTouch || 0), 0);
                  const totalBlockError = matches.reduce((sum, m) => sum + (m.blockError || 0), 0);
                  const totalAssists = matches.reduce((sum, m) => sum + (m.assists || 0), 0);
                  const totalBlocks = matches.reduce((sum, m) => sum + (m.blocks || 0) + ((m.halfBlock || 0) * 0.5), 0);

                  // Total attempts = kills + errors + hits in play
                  const totalAttempts = totalKills + totalHittingErrors + totalHits;
                  const hittingPct = totalAttempts > 0 ? ((totalKills - totalHittingErrors) / totalAttempts) : 0;
                  const acePct = totalServes > 0 ? (totalAces / totalServes) : 0;
                  const servingPct = totalServes > 0 ? ((totalServes - totalServiceErrors) / totalServes) : 0;
                  const digPct = (totalDigs + totalDigErrors) > 0 ? (totalDigs / (totalDigs + totalDigErrors)) : 0;
                  const assistsPerMatch = matches.length > 0 ? (totalAssists / matches.length) : 0;
                  const blocksPerMatch = matches.length > 0 ? (totalBlocks / matches.length) : 0;

                  return {
                    hittingPct: hittingPct,
                    acePct: acePct,
                    servingPct: servingPct,
                    digPct: digPct,
                    assistsPerMatch: assistsPerMatch,
                    blocksPerMatch: blocksPerMatch,
                    totalTips: totalTips,
                    totalBlockTouch: totalBlockTouch,
                    totalBlockError: totalBlockError,
                    totalMatches: matches.length
                  };
                };

              const earlyStats = calculateStats(earlyMatches);
              const recentStats = calculateStats(recentMatches);

              // Calculate improvement trends
              const hittingTrend = recentStats.hittingPct - earlyStats.hittingPct;
              const aceTrend = recentStats.acePct - earlyStats.acePct;
              const servingTrend = recentStats.servingPct - earlyStats.servingPct;
              const digTrend = recentStats.digPct - earlyStats.digPct;
              const assistsTrend = recentStats.assistsPerMatch - earlyStats.assistsPerMatch;
              const blocksTrend = recentStats.blocksPerMatch - earlyStats.blocksPerMatch;

              // Win streak analysis
              let currentStreak = 0;
              let longestWinStreak = 0;
              let longestLossStreak = 0;
              let tempStreak = 0;
              let isWinning = null;

              sortedMatches.forEach(match => {
                const scores = match.setScores || [];
                const w = scores.filter(s => (s.us || 0) > (s.opp || 0)).length;
                const l = scores.filter(s => (s.us || 0) < (s.opp || 0)).length;
                const isWin = w > l;
                
                if (isWinning === null || isWinning === isWin) {
                  tempStreak++;
                } else {
                  if (isWinning) {
                    longestWinStreak = Math.max(longestWinStreak, tempStreak);
                  } else {
                    longestLossStreak = Math.max(longestLossStreak, tempStreak);
                  }
                  tempStreak = 1;
                }
                isWinning = isWin;
              });
              
              // Check final streak
              if (isWinning) {
                longestWinStreak = Math.max(longestWinStreak, tempStreak);
                currentStreak = tempStreak;
              } else {
                longestLossStreak = Math.max(longestLossStreak, tempStreak);
                currentStreak = -tempStreak;
              }

              return (
                <div>
                  {/* Season Overview */}
                  <div className="card" style={{ marginBottom: "20px" }}>
                    <h3 style={{ margin: "0 0 16px", fontSize: "18px" }}>
                      Season Overview: {selectedSeason?.name}
                      {trendsPlayerId && (
                        <span style={{ fontSize: "14px", fontWeight: "normal", color: "var(--muted)", marginLeft: "8px" }}>
                          • {players.find(p => p.id === trendsPlayerId)?.name || 'Selected Player'}
                        </span>
                      )}
                    </h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "16px" }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold", color: "var(--primary)" }}>{totalMatches}</div>
                        <div style={{ fontSize: "14px", color: "var(--muted)" }}>Total Matches</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold", color: "var(--success)" }}>{wins}</div>
                        <div style={{ fontSize: "14px", color: "var(--muted)" }}>Wins</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold", color: "var(--primary)" }}>{winRate}%</div>
                        <div style={{ fontSize: "14px", color: "var(--muted)" }}>Win Rate</div>
                      </div>
                    </div>
                  </div>

                  {/* Rolling Averages */}
                  <div className="card" style={{ marginBottom: "20px" }}>
                    <h3 style={{ margin: "0 0 16px", fontSize: "18px" }}>Rolling Averages</h3>
                    <div style={{ marginBottom: "16px", fontSize: "14px", color: "var(--muted)", textAlign: "left" }}>
                      See how your performance changes over time with smooth trend lines
                    </div>
                    {(() => {
                      // Calculate rolling averages for each stat
                      const calculateRollingAverage = (matches, statKey, windowSize) => {
                        const results = [];
                        for (let i = windowSize - 1; i < matches.length; i++) {
                          const window = matches.slice(i - windowSize + 1, i + 1);
                          const avg = window.reduce((sum, match) => {
                            let value = 0;
                            if (statKey === 'hittingPct') {
                              const kills = match.kills || 0;
                              const hits = match.hitInPlay || match.attackAttempts || 0;
                              const errors = match.hittingErrors || 0;
                              value = (hits + kills + errors) > 0 ? (kills - errors) / (hits + kills + errors) : 0;
                            } else if (statKey === 'acePct') {
                              const aces = match.aces || 0;
                              const serves = match.serveAttempts || 0;
                              value = serves > 0 ? aces / serves : 0;
                            } else if (statKey === 'servingPct') {
                              const serves = match.serveAttempts || 0;
                              const errors = match.serviceErrors || 0;
                              value = serves > 0 ? (serves - errors) / serves : 0;
                            } else if (statKey === 'digPct') {
                              const digs = match.digs || 0;
                              const errors = match.digErrors || 0;
                              value = (digs + errors) > 0 ? digs / (digs + errors) : 0;
                            } else if (statKey === 'assistsPerMatch') {
                              value = match.assists || 0;
                            } else if (statKey === 'blocksPerMatch') {
                              value = match.blocks || 0;
                            }
                            return sum + value;
                          }, 0) / windowSize;
                          results.push({
                            matchIndex: i,
                            value: avg,
                            matchDate: matches[i].matchDate,
                            opponent: matches[i].opponent
                          });
                        }
                        return results;
                      };

                      const rolling3 = calculateRollingAverage(sortedMatches, 'hittingPct', 3);
                      const rolling5 = calculateRollingAverage(sortedMatches, 'hittingPct', 5);
                      const rolling10 = calculateRollingAverage(sortedMatches, 'hittingPct', 10);

                      return (
                        <div>
                          <div style={{ marginBottom: "8px" }}>
                            <h4 style={{ margin: "0 0 12px", fontSize: "16px", color: "var(--text)" }}>Hitting Percentage Trends</h4>
                            <div className="rolling-averages-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px" }}>
                              <div style={{ textAlign: "center", padding: "12px", background: "var(--bg-light)", borderRadius: "8px" }}>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "var(--primary)" }}>
                                  {rolling3.length > 0 ? (rolling3[rolling3.length - 1].value * 100).toFixed(1) : "N/A"}%
                                </div>
                                <div style={{ fontSize: "12px", color: "var(--muted)" }}>3-Match Average</div>
                                <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: "4px" }}>
                                  {rolling3.length > 0 ? `Last: ${rolling3[rolling3.length - 1].opponent}` : "Need 3+ matches"}
                                </div>
                              </div>
                              <div style={{ textAlign: "center", padding: "12px", background: "var(--bg-light)", borderRadius: "8px" }}>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "var(--primary)" }}>
                                  {rolling5.length > 0 ? (rolling5[rolling5.length - 1].value * 100).toFixed(1) : "N/A"}%
                                </div>
                                <div style={{ fontSize: "12px", color: "var(--muted)" }}>5-Match Average</div>
                                <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: "4px" }}>
                                  {rolling5.length > 0 ? `Last: ${rolling5[rolling5.length - 1].opponent}` : "Need 5+ matches"}
                                </div>
                              </div>
                              <div style={{ textAlign: "center", padding: "12px", background: "var(--bg-light)", borderRadius: "8px" }}>
                                <div style={{ fontSize: "18px", fontWeight: "bold", color: "var(--primary)" }}>
                                  {rolling10.length > 0 ? (rolling10[rolling10.length - 1].value * 100).toFixed(1) : "N/A"}%
                                </div>
                                <div style={{ fontSize: "12px", color: "var(--muted)" }}>10-Match Average</div>
                                <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: "4px" }}>
                                  {rolling10.length > 0 ? `Last: ${rolling10[rolling10.length - 1].opponent}` : "Need 10+ matches"}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Consistency Analysis */}
                  <div className="card" style={{ marginBottom: "20px" }}>
                    <h3 style={{ margin: "0 0 16px", fontSize: "18px" }}>Consistency Analysis</h3>
                    <div style={{ marginBottom: "16px", fontSize: "14px", color: "var(--muted)", textAlign: "left", lineHeight: "1.5" }}>
                      How reliable and predictable is your performance? We analyze the variation in your stats across all matches to give you a consistency score from 0-100. Higher scores mean more reliable performance.
                    </div>
                    {(() => {
                      // Calculate consistency metrics for each stat
                      const calculateConsistency = (matches, statKey) => {
                        const values = matches.map(match => {
                          if (statKey === 'hittingPct') {
                            const kills = match.kills || 0;
                            const hits = match.hitInPlay || match.attackAttempts || 0;
                            const errors = match.hittingErrors || 0;
                            return (hits + kills + errors) > 0 ? (kills - errors) / (hits + kills + errors) : 0;
                          } else if (statKey === 'acePct') {
                            const aces = match.aces || 0;
                            const serves = match.serveAttempts || 0;
                            return serves > 0 ? aces / serves : 0;
                          } else if (statKey === 'servingPct') {
                            const serves = match.serveAttempts || 0;
                            const errors = match.serviceErrors || 0;
                            return serves > 0 ? (serves - errors) / serves : 0;
                          } else if (statKey === 'digPct') {
                            const digs = match.digs || 0;
                            const errors = match.digErrors || 0;
                            return (digs + errors) > 0 ? digs / (digs + errors) : 0;
                          } else if (statKey === 'assistsPerMatch') {
                            return match.assists || 0;
                          } else if (statKey === 'blocksPerMatch') {
                            return match.blocks || 0;
                          }
                          return 0;
                        }).filter(v => !isNaN(v) && isFinite(v));

                        if (values.length === 0) return null;

                        const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
                        const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
                        const stdDev = Math.sqrt(variance);
                        const coefficientOfVariation = mean > 0 ? stdDev / mean : 0;
                        const range = Math.max(...values) - Math.min(...values);
                        
                        // Calculate consistency score (0-100, higher = more consistent)
                        // Lower coefficient of variation = more consistent
                        const consistencyScore = Math.max(0, Math.min(100, 100 - (coefficientOfVariation * 100)));
                        
                        // Categorize consistency
                        let category = "Wild Card";
                        let categoryColor = "var(--danger)";
                        if (consistencyScore >= 80) {
                          category = "Rock Solid";
                          categoryColor = "var(--success)";
                        } else if (consistencyScore >= 60) {
                          category = "Consistent";
                          categoryColor = "var(--primary)";
                        } else if (consistencyScore >= 40) {
                          category = "Variable";
                          categoryColor = "var(--warning)";
                        } else if (consistencyScore >= 20) {
                          category = "Streaky";
                          categoryColor = "var(--muted)";
                        }

                        return {
                          mean,
                          stdDev,
                          coefficientOfVariation,
                          range,
                          consistencyScore,
                          category,
                          categoryColor,
                          best: Math.max(...values),
                          worst: Math.min(...values)
                        };
                      };

                      const hittingConsistency = calculateConsistency(sortedMatches, 'hittingPct');
                      const servingConsistency = calculateConsistency(sortedMatches, 'servingPct');

                      return (
                        <div>
                          <div style={{ display: "grid", gap: "20px" }}>
                            {hittingConsistency && (
                              <div style={{ padding: "20px", background: "var(--bg-light)", borderRadius: "12px", border: `2px solid ${hittingConsistency.categoryColor}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                  <h4 style={{ margin: "0", fontSize: "18px", color: "var(--text)" }}>Hitting Percentage</h4>
                                  <div style={{ 
                                    padding: "6px 12px", 
                                    borderRadius: "20px", 
                                    background: hittingConsistency.categoryColor, 
                                    color: "white", 
                                    fontSize: "11px", 
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {hittingConsistency.category}
                                  </div>
                                </div>
                                
                                <div style={{ marginBottom: "16px" }}>
                                  <div style={{ fontSize: "32px", fontWeight: "bold", color: "var(--primary)", marginBottom: "4px" }}>
                                    {(hittingConsistency.mean * 100).toFixed(1)}%
                                  </div>
                                  <div style={{ fontSize: "14px", color: "var(--muted)" }}>Average Performance</div>
                                </div>
                                
                                <div style={{ marginBottom: "16px" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--text)" }}>Consistency</span>
                                    <span style={{ fontSize: "14px", fontWeight: "bold", color: hittingConsistency.categoryColor }}>
                                      {hittingConsistency.consistencyScore.toFixed(0)}/100
                                    </span>
                                  </div>
                                  <div style={{ 
                                    width: "100%", 
                                    height: "8px", 
                                    background: "var(--border)", 
                                    borderRadius: "4px", 
                                    overflow: "hidden" 
                                  }}>
                                    <div style={{ 
                                      width: `${hittingConsistency.consistencyScore}%`, 
                                      height: "100%", 
                                      background: hittingConsistency.categoryColor, 
                                      borderRadius: "4px",
                                      transition: "width 0.3s ease"
                                    }}></div>
                                  </div>
                                </div>
                                
                                <div style={{ 
                                  padding: "12px", 
                                  background: "var(--bg)", 
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  color: "var(--muted)"
                                }}>
                                  <strong>Range:</strong> {(hittingConsistency.best * 100).toFixed(1)}% - {(hittingConsistency.worst * 100).toFixed(1)}%
                                </div>
                              </div>
                            )}

                            {servingConsistency && (
                              <div style={{ padding: "20px", background: "var(--bg-light)", borderRadius: "12px", border: `2px solid ${servingConsistency.categoryColor}` }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                                  <h4 style={{ margin: "0", fontSize: "18px", color: "var(--text)" }}>Serving Percentage</h4>
                                  <div style={{ 
                                    padding: "6px 12px", 
                                    borderRadius: "20px", 
                                    background: servingConsistency.categoryColor, 
                                    color: "white", 
                                    fontSize: "11px", 
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    whiteSpace: "nowrap"
                                  }}>
                                    {servingConsistency.category}
                                  </div>
                                </div>
                                
                                <div style={{ marginBottom: "16px" }}>
                                  <div style={{ fontSize: "32px", fontWeight: "bold", color: "var(--primary)", marginBottom: "4px" }}>
                                    {(servingConsistency.mean * 100).toFixed(1)}%
                                  </div>
                                  <div style={{ fontSize: "14px", color: "var(--muted)" }}>Average Performance</div>
                                </div>
                                
                                <div style={{ marginBottom: "16px" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                                    <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--text)" }}>Consistency</span>
                                    <span style={{ fontSize: "14px", fontWeight: "bold", color: servingConsistency.categoryColor }}>
                                      {servingConsistency.consistencyScore.toFixed(0)}/100
                                    </span>
                                  </div>
                                  <div style={{ 
                                    width: "100%", 
                                    height: "8px", 
                                    background: "var(--border)", 
                                    borderRadius: "4px", 
                                    overflow: "hidden" 
                                  }}>
                                    <div style={{ 
                                      width: `${servingConsistency.consistencyScore}%`, 
                                      height: "100%", 
                                      background: servingConsistency.categoryColor, 
                                      borderRadius: "4px",
                                      transition: "width 0.3s ease"
                                    }}></div>
                                  </div>
                                </div>
                                
                                <div style={{ 
                                  padding: "12px", 
                                  background: "var(--bg)", 
                                  borderRadius: "8px",
                                  fontSize: "13px",
                                  color: "var(--muted)"
                                }}>
                                  <strong>Range:</strong> {(servingConsistency.best * 100).toFixed(1)}% - {(servingConsistency.worst * 100).toFixed(1)}%
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div style={{ marginTop: "20px", padding: "16px", background: "var(--callout)", borderRadius: "8px", fontSize: "14px", color: "var(--muted)" }}>
                            <div style={{ marginBottom: "8px" }}>
                              <strong style={{ color: "var(--text)", fontSize: "15px" }}>How Consistency is Calculated:</strong>
                            </div>
                            <div style={{ marginBottom: "12px", lineHeight: "1.5" }}>
                              We analyze how much your performance varies from match to match. A lower variation means you're more consistent. 
                              The score is calculated using statistical analysis of your performance across all matches in this season.
                            </div>
                            <div style={{ marginBottom: "8px" }}>
                              <strong style={{ color: "var(--text)" }}>Consistency Categories:</strong>
                            </div>
                            <div style={{ display: "grid", gap: "8px", fontSize: "13px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "var(--success)" }}></div>
                                <span><strong>Rock Solid (80-100):</strong> Very reliable performance, rarely has off days</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "var(--primary)" }}></div>
                                <span><strong>Consistent (60-79):</strong> Generally reliable with occasional ups and downs</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "#f59e0b" }}></div>
                                <span><strong>Variable (40-59):</strong> Somewhat unpredictable, good and bad days mixed</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "var(--muted)" }}></div>
                                <span><strong>Streaky (20-39):</strong> Very unpredictable, hot and cold streaks</span>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                <div style={{ width: "12px", height: "12px", borderRadius: "50%", background: "var(--danger)" }}></div>
                                <span><strong>Wild Card (0-19):</strong> Extremely inconsistent, performance varies wildly</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Performance Trends */}
                  <div className="card" style={{ marginBottom: "20px" }}>
                    <h3 style={{ margin: "0 0 16px", fontSize: "18px" }}>Are You Improving? (Early Season vs Recent Matches)</h3>
                    <div style={{ marginBottom: "16px", fontSize: "14px", color: "var(--muted)", textAlign: "left", lineHeight: "1.5" }}>
                      Comparing your first {earlyMatches.length} matches vs your last {recentMatches.length} matches. This shows if you're getting better, worse, or staying the same throughout the season.
                    </div>
                    <div style={{ display: "grid", gap: "16px" }}>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "16px", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Early Season</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(earlyStats.hittingPct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Hitting %</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color: hittingTrend > 0 ? "var(--success)" : hittingTrend < 0 ? "var(--danger)" : "var(--muted)" }}>
                            {hittingTrend > 0 ? "↗" : hittingTrend < 0 ? "↘" : "→"} {(hittingTrend * 100).toFixed(1)}%
                          </div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Change</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Recent Matches</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(recentStats.hittingPct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Hitting %</div>
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "16px", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Early Season</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(earlyStats.acePct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Ace %</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color: aceTrend > 0 ? "var(--success)" : aceTrend < 0 ? "var(--danger)" : "var(--muted)" }}>
                            {aceTrend > 0 ? "↗" : aceTrend < 0 ? "↘" : "→"} {(aceTrend * 100).toFixed(1)}%
                          </div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Change</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Recent Matches</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(recentStats.acePct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Ace %</div>
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "16px", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Early Season</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(earlyStats.servingPct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Serving %</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color: servingTrend > 0 ? "var(--success)" : servingTrend < 0 ? "var(--danger)" : "var(--muted)" }}>
                            {servingTrend > 0 ? "↗" : servingTrend < 0 ? "↘" : "→"} {(servingTrend * 100).toFixed(1)}%
                          </div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Change</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Recent Matches</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(recentStats.servingPct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Serving %</div>
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "16px", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Early Season</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(earlyStats.digPct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Dig %</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color: digTrend > 0 ? "var(--success)" : digTrend < 0 ? "var(--danger)" : "var(--muted)" }}>
                            {digTrend > 0 ? "↗" : digTrend < 0 ? "↘" : "→"} {(digTrend * 100).toFixed(1)}%
                          </div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Change</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Recent Matches</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{(recentStats.digPct * 100).toFixed(1)}%</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Dig %</div>
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "16px", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Early Season</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{earlyStats.assistsPerMatch.toFixed(1)}</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Assists/Match</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color: assistsTrend > 0 ? "var(--success)" : assistsTrend < 0 ? "var(--danger)" : "var(--muted)" }}>
                            {assistsTrend > 0 ? "↗" : assistsTrend < 0 ? "↘" : "→"} {assistsTrend.toFixed(1)}
                          </div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Change</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Recent Matches</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{recentStats.assistsPerMatch.toFixed(1)}</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Assists/Match</div>
                        </div>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: "16px", alignItems: "center" }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Early Season</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{earlyStats.blocksPerMatch.toFixed(1)}</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Blocks/Match</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "16px", fontWeight: "bold", color: blocksTrend > 0 ? "var(--success)" : blocksTrend < 0 ? "var(--danger)" : "var(--muted)" }}>
                            {blocksTrend > 0 ? "↗" : blocksTrend < 0 ? "↘" : "→"} {blocksTrend.toFixed(1)}
                          </div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Change</div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Recent Matches</div>
                          <div style={{ fontSize: "18px", fontWeight: "bold" }}>{recentStats.blocksPerMatch.toFixed(1)}</div>
                          <div style={{ fontSize: "12px", color: "var(--muted)" }}>Blocks/Match</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginTop: "20px", padding: "16px", background: "var(--callout)", borderRadius: "8px", fontSize: "14px", color: "var(--muted)" }}>
                      <div style={{ marginBottom: "8px" }}>
                        <strong style={{ color: "var(--text)", fontSize: "15px" }}>How to Read the Trends:</strong>
                      </div>
                      <div style={{ marginBottom: "12px", lineHeight: "1.5" }}>
                        We compare your performance in the first half of the season vs the second half to see if you're improving over time.
                      </div>
                      <div style={{ marginBottom: "8px" }}>
                        <strong style={{ color: "var(--text)" }}>Arrow Meanings:</strong>
                      </div>
                      <div style={{ display: "grid", gap: "8px", fontSize: "13px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ fontSize: "18px", color: "var(--success)" }}>↗</div>
                          <span><strong>Green Arrow:</strong> You're improving! Your recent performance is better than early season</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ fontSize: "18px", color: "var(--danger)" }}>↘</div>
                          <span><strong>Pink Arrow:</strong> You're declining. Recent performance is worse than early season</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <div style={{ fontSize: "18px", color: "var(--muted)" }}>→</div>
                          <span><strong>Gray Arrow:</strong> No significant change. You're performing about the same</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Win Streak Analysis */}
                  <div className="card">
                    <h3 style={{ margin: "0 0 16px", fontSize: "18px" }}>Win Streak Analysis</h3>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: "16px" }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold", color: currentStreak > 0 ? "var(--success)" : currentStreak < 0 ? "var(--danger)" : "var(--muted)" }}>
                          {currentStreak > 0 ? `+${currentStreak}` : currentStreak}
                        </div>
                        <div style={{ fontSize: "14px", color: "var(--muted)" }}>Current Streak</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold", color: "var(--success)" }}>{longestWinStreak}</div>
                        <div style={{ fontSize: "14px", color: "var(--muted)" }}>Longest Win Streak</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: "24px", fontWeight: "bold", color: "var(--danger)" }}>{longestLossStreak}</div>
                        <div style={{ fontSize: "14px", color: "var(--muted)" }}>Longest Loss Streak</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </section>
        )}

        {/* DASHBOARD */}
        {user && view === "dashboard" && (
          <div className="dashboard-container" style={{ maxWidth: "1200px", margin: "0 auto" }}>
            {/* Sync Status Indicator */}
            <div style={{ marginBottom: "16px" }}>
              <SyncStatusIndicator 
                syncStatus={syncStatus} 
                onSyncClick={() => {
                  updateSyncStatus({ isSyncing: true });
                  OfflineManager.processSyncQueue().then(() => {
                    updateSyncStatus({ isSyncing: false, lastSync: new Date(), syncError: null });
                    updatePendingCount();
                  }).catch((error) => {
                    updateSyncStatus({ isSyncing: false, syncError: error.message });
                  });
                }}
              />
            </div>

            {/* Welcome Section */}
            <section style={{ marginBottom: "32px", textAlign: "center" }}>
              <h1 style={{ 
                fontSize: "28px", 
                fontWeight: "700", 
                margin: "0 0 8px 0", 
                color: "#000000"
              }}>
                Volleyball Stats Dashboard
              </h1>
              <p style={{ 
                fontSize: "16px", 
                color: "var(--muted)", 
                margin: "0",
                fontWeight: "400"
              }}>
                Track your performance and analyze your game
              </p>
            </section>

            {/* Add Player by Code - For parent users without shared players */}
            {user && initialUserDataLoaded && teams !== undefined && !teams?.length && (!user.sharedPlayers || user.sharedPlayers.length === 0) && (
              <section style={{ marginBottom: "40px" }}>
                <div className="card" style={{
                  padding: "40px",
                  textAlign: "center",
                  background: 'linear-gradient(135deg, #2E5AAC 0%, #1E4A9C 100%)',
                  color: 'white',
                  borderRadius: '12px',
                  border: 'none'
                }}>
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏐</div>
                  <h2 style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    margin: "0 0 12px 0",
                    color: 'white'
                  }}>
                    Welcome to VBall Stats!
                  </h2>
                  <p style={{
                    fontSize: "16px",
                    margin: "0 0 24px 0",
                    opacity: 0.9,
                    maxWidth: '500px',
                    marginLeft: 'auto',
                    marginRight: 'auto'
                  }}>
                    Get started by entering a player code provided by your coach to view their stats.
                  </p>
                  <button
                    onClick={() => setShowAddPlayerCodeModal(true)}
                    style={{
                      background: 'white',
                      color: '#2E5AAC',
                      border: 'none',
                      padding: '14px 28px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                  >
                    + Add Player by Code
                  </button>
                </div>
              </section>
            )}

            {/* Shared by Coach Section - For parent users */}
            {!isCoach && (
              <section style={{ marginBottom: "40px" }}>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px"
                }}>
                  <h2 style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    margin: "0",
                    color: "var(--text)"
                  }}>
                    Shared by Coach
                  </h2>
                  <button
                    onClick={() => setShowAddPlayerCodeModal(true)}
                    className="btn btn-muted"
                    style={{ fontSize: "14px", padding: "8px 16px" }}
                  >
                    + Add Player Code
                  </button>
                </div>

                {user?.sharedPlayers && user.sharedPlayers.length > 0 ? (
                  <>
                {/* Desktop List View */}
                <div className="seasons-list-desktop">
                  {user.sharedPlayers.map((sharedPlayer) => {
                    const hasNewStats = sharedPlayerNotifications[sharedPlayer.playerId];
                    return (
                      <div
                        key={sharedPlayer.playerId}
                        className="season-list-item"
                        onClick={() => {
                          setActivePlayerId(sharedPlayer.playerId);
                          setView("shared-player-stats");
                        }}
                        style={{ position: 'relative' }}
                      >
                        <div className="season-list-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div>
                              <div className="season-list-name">
                                {sharedPlayer.playerName}
                                {sharedPlayer.playerNumber && (
                                  <span style={{
                                    fontSize: "14px",
                                    color: "var(--muted)",
                                    marginLeft: "8px",
                                    fontWeight: "500"
                                  }}>
                                    #{sharedPlayer.playerNumber}
                                  </span>
                                )}
                                {hasNewStats && (
                                  <span style={{
                                    display: 'inline-block',
                                    marginLeft: '12px',
                                    padding: '4px 10px',
                                    background: '#007AFF',
                                    color: 'white',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    borderRadius: '12px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                  }}>
                                    New Stats
                                  </span>
                                )}
                              </div>
                              <div className="season-list-year">
                                {sharedPlayer.teamName}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Mobile Card View */}
                <div className="seasons-cards-mobile" style={{ display: "grid", gap: "12px" }}>
                  {user.sharedPlayers.map((sharedPlayer) => {
                    const hasNewStats = sharedPlayerNotifications[sharedPlayer.playerId];
                    return (
                      <div
                        key={sharedPlayer.playerId}
                        className="card"
                        onClick={() => {
                          setActivePlayerId(sharedPlayer.playerId);
                          setView("shared-player-stats");
                        }}
                        style={{
                          padding: "16px",
                          cursor: "pointer",
                          transition: "transform 0.15s ease, box-shadow 0.15s ease",
                          position: "relative"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.01)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "var(--text)",
                            marginBottom: "4px",
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px"
                          }}>
                            <span>
                              {sharedPlayer.playerName}
                              {sharedPlayer.playerNumber && (
                                <span style={{
                                  fontSize: "14px",
                                  color: "var(--muted)",
                                  marginLeft: "8px",
                                  fontWeight: "500"
                                }}>
                                  #{sharedPlayer.playerNumber}
                                </span>
                              )}
                            </span>
                            {hasNewStats && (
                              <span style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                background: '#007AFF',
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: '700',
                                borderRadius: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                              }}>
                                New Stats
                              </span>
                            )}
                          </div>
                          <div style={{
                            fontSize: "14px",
                            color: "var(--muted)"
                          }}>
                            {sharedPlayer.teamName}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                  </>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px',
                    color: 'var(--muted)',
                    background: 'var(--bg-light)',
                    borderRadius: '8px',
                    border: '2px dashed var(--border)'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>👨‍🏫</div>
                    <h3 style={{ marginBottom: '8px', color: 'var(--text)', fontSize: '18px', fontWeight: '600' }}>No shared players yet</h3>
                    <p style={{ fontSize: '14px', marginBottom: '0' }}>Click "+ Add Player Code" to enter a code provided by your coach</p>
                  </div>
                )}
              </section>
            )}

            {/* Players Section */}
            <section style={{ marginBottom: "40px" }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "20px"
              }}>
                <h2 style={{
                  fontSize: "24px",
                  fontWeight: "600",
                  margin: "0",
                  color: "var(--text)"
                }}>
                  My Players
                </h2>
                <button
                  className="btn btn-primary dashboard-add-player-btn"
                  onClick={() => {
                    if (!canAddPlayer(isPro, players.length, isCoach)) {
                      setUpgradeModalFeature('player');
                      setUpgradeModalOpen(true);
                      return;
                    }
                    setActivePlayerId(null);
                    setPlayerEditMode("create");
                    setView("player-setup");
                  }}
                >
                  Add Player
                </button>
              </div>

              {players.length === 0 ? (
                <div style={{
                  background: "var(--bg-light)",
                  border: "2px dashed var(--border)",
                  borderRadius: "12px",
                  padding: "40px 20px",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏐</div>
                  <h3 style={{ 
                    fontSize: "18px", 
                    fontWeight: "600", 
                    margin: "0 0 8px 0", 
                    color: "var(--text)" 
                  }}>
                    No players yet
                  </h3>
                  <p style={{ 
                    fontSize: "12px", 
                    color: "var(--muted)", 
                    margin: "0" 
                  }}>
                    Add your first player to start tracking stats
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop List View */}
                  <div className="seasons-list-desktop">
                    {players.map((p) => (
                      <div
                        key={p.id}
                        className="season-list-item"
                        onClick={async () => {
                          await changeActivePlayer(p.id);
                          setView("player-dashboard");
                        }}
                      >
                        <div className="season-list-info">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {p.photoUrl && (
                              <div style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "8px",
                                overflow: "hidden",
                                flexShrink: 0,
                                border: "1px solid var(--border)"
                              }}>
                                <img
                                  src={p.photoUrl}
                                  alt={p.name}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover"
                                  }}
                                />
                              </div>
                            )}
                            <div>
                              <div className="season-list-name">
                                {p.name}
                                {p.number && (
                                  <span style={{
                                    fontSize: "14px",
                                    color: "var(--muted)",
                                    marginLeft: "8px",
                                    fontWeight: "500"
                                  }}>
                                    #{p.number}
                                  </span>
                                )}
                              </div>
                              <div className="season-list-year">
                                {p.position ?
                                  (p.position === 'OH' ? 'Outside Hitter' :
                                   p.position === 'MB' ? 'Middle Blocker' :
                                   p.position === 'OPP' ? 'Opposite' :
                                   p.position === 'S' ? 'Setter' :
                                   p.position === 'L' ? 'Libero' :
                                   p.position) : "Position not set"}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActivePlayerId(p.id);
                              setPlayerEditMode("edit");
                              setView("player-setup");
                            }}
                            className="season-list-edit-btn"
                            title="Edit player"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isOnline) {
                                setShowOfflineDeleteModal(true);
                              } else {
                                setPendingDeletePlayerId(p.id);
                              }
                            }}
                            className="season-list-edit-btn"
                            style={{
                              background: '#FEE2E2'
                            }}
                            title="Delete player"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#FCA5A5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FEE2E2';
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile Card View */}
                  <div className="seasons-cards-mobile" style={{ display: "grid", gap: "12px" }}>
                    {players.map((p) => (
                      <div
                        key={p.id}
                        className="card"
                        onClick={async () => {
                          await changeActivePlayer(p.id);
                          setView("player-dashboard");
                        }}
                        style={{
                          padding: "16px",
                          cursor: "pointer",
                          transition: "transform 0.15s ease, box-shadow 0.15s ease"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.01)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '';
                        }}
                      >
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                          {p.photoUrl && (
                            <div style={{
                              width: "60px",
                              height: "60px",
                              borderRadius: "8px",
                              overflow: "hidden",
                              flexShrink: 0,
                              border: "1px solid var(--border)"
                            }}>
                              <img
                                src={p.photoUrl}
                                alt={p.name}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover"
                                }}
                              />
                            </div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: "18px",
                              fontWeight: "600",
                              letterSpacing: "-0.02em",
                              color: "var(--text)",
                              marginBottom: "4px"
                            }}>
                              {p.name}
                              {p.number && (
                                <span style={{
                                  fontSize: "14px",
                                  color: "var(--muted)",
                                  marginLeft: "8px",
                                  fontWeight: "500"
                                }}>
                                  #{p.number}
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: "13px",
                              color: "var(--muted)"
                            }}>
                              {p.position ?
                                (p.position === 'OH' ? 'Outside Hitter' :
                                 p.position === 'MB' ? 'Middle Blocker' :
                                 p.position === 'OPP' ? 'Opposite' :
                                 p.position === 'S' ? 'Setter' :
                                 p.position === 'L' ? 'Libero' :
                                 p.position) : "Position not set"}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActivePlayerId(p.id);
                                setPlayerEditMode("edit");
                                setView("player-setup");
                              }}
                              style={{
                                width: '40px',
                                height: '40px',
                                background: '#F2F2F7',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#E5E5EA';
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#F2F2F7';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title="Edit player"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!isOnline) {
                                  setShowOfflineDeleteModal(true);
                                } else {
                                  setPendingDeletePlayerId(p.id);
                                }
                              }}
                              style={{
                                width: '40px',
                                height: '40px',
                                background: '#FEE2E2',
                                border: 'none',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                fontSize: '18px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.15s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#FCA5A5';
                                e.currentTarget.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = '#FEE2E2';
                                e.currentTarget.style.transform = 'scale(1)';
                              }}
                              title="Delete player"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>

          </div>
        )}

        {/* PLAYER DASHBOARD (seasons for selected player) */}
        {user && view === "player-dashboard" && (
          <div style={{ maxWidth: "1200px", margin: "0 auto", paddingBottom: "40px" }}>
            {/* Breadcrumb */}
            <div className="breadcrumb-nav" style={{ marginBottom: "24px" }}>
              <button
                onClick={() => setView("dashboard")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px"
                }}
              >
                ← Players
              </button>
            </div>

            {/* Player Header */}
            <section style={{ marginBottom: "32px", textAlign: "center" }}>
              <h1 style={{
                fontSize: "28px",
                fontWeight: "700",
                margin: "0 0 8px 0",
                color: "#000000"
              }}>
                {players.find(p => p.id === activePlayerId)?.name || "Player"}
              </h1>
              <p style={{
                fontSize: "16px",
                color: "var(--muted)",
                margin: "0",
                fontWeight: "400"
              }}>
                Select a season to track your performance
              </p>
            </section>

            {/* Seasons Section */}
            <section className="dashboard-seasons-section" style={{ marginBottom: "48px" }}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px"
              }}>
                <h2 style={{
                  fontSize: "20px",
                  fontWeight: "600",
                  margin: "0",
                  color: "var(--text)"
                }}>
                  Seasons
                </h2>
                <button
                  onClick={() => {
                    if (!canAddSeason(isPro, seasons.length, isCoach)) {
                      setUpgradeModalFeature('season');
                      setUpgradeModalOpen(true);
                      return;
                    }
                    setView("season-setup");
                  }}
                  className="btn btn-primary"
                  style={{
                    width: '44px',
                    height: '44px',
                    minWidth: '44px',
                    minHeight: '44px',
                    borderRadius: '10px',
                    fontWeight: '400',
                    fontSize: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    lineHeight: 1
                  }}
                  title="Add season"
                >
                  +
                </button>
              </div>

              {seasons.length === 0 ? (
                <div className="card" style={{
                  background: "var(--bg-light)",
                  border: "2px dashed var(--border)",
                  padding: "40px 20px",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "48px", marginBottom: "16px" }}>📅</div>
                  <h3 style={{
                    fontSize: "18px",
                    fontWeight: "600",
                    margin: "0 0 8px 0",
                    color: "var(--text)"
                  }}>
                    No seasons yet
                  </h3>
                  <p style={{
                    fontSize: "13px",
                    color: "var(--muted)",
                    margin: "0 0 16px 0"
                  }}>
                    Create a season to start recording matches
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      if (!canAddSeason(isPro, seasons.length, isCoach)) {
                        setUpgradeModalFeature('season');
                        setUpgradeModalOpen(true);
                        return;
                      }
                      setView("season-setup");
                    }}
                  >
                    Create Your First Season
                  </button>
                </div>
              ) : (
                <>
                  {/* Desktop List View */}
                  <div className="seasons-list-desktop">
                    {seasons.map((s) => (
                      <div
                        key={s.id}
                        className="season-list-item"
                        onClick={async () => {
                          await changeActiveSeason(s.id);
                          setView("season-detail");
                        }}
                      >
                        <div className="season-list-info">
                          <div className="season-list-name">{s.name}</div>
                          {s.year && <div className="season-list-year">{s.year}</div>}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveSeasonId(s.id);
                              openSeasonEdit(s.id);
                            }}
                            className="season-list-edit-btn"
                            title="Edit season"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSeasonToDelete({
                                playerId: activePlayerId,
                                seasonId: s.id,
                                seasonName: s.name
                              });
                              setGateModal("deleteSeason");
                            }}
                            className="season-list-edit-btn"
                            style={{
                              background: '#FEE2E2'
                            }}
                            title="Delete season"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#FCA5A5';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FEE2E2';
                            }}
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Mobile Card View */}
                  <div className="seasons-cards-mobile" style={{ display: "grid", gap: "12px" }}>
                    {seasons.map((s) => (
                    <div
                      key={s.id}
                      className="card"
                      onClick={async () => {
                        await changeActiveSeason(s.id);
                        setView("season-detail");
                      }}
                      style={{
                        padding: "16px",
                        cursor: "pointer",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease"
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.01)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = '';
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{
                            fontSize: "18px",
                            fontWeight: "600",
                            letterSpacing: "-0.02em",
                            color: "var(--text)",
                            marginBottom: "4px"
                          }}>
                            {s.name}
                          </div>
                          {s.year && (
                            <div style={{
                              fontSize: "13px",
                              color: "var(--muted)"
                            }}>
                              {s.year}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveSeasonId(s.id);
                              openSeasonEdit(s.id);
                            }}
                            style={{
                              width: '40px',
                              height: '40px',
                              background: '#F2F2F7',
                              border: 'none',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#E5E5EA';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#F2F2F7';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Edit season"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSeasonToDelete({
                                playerId: activePlayerId,
                                seasonId: s.id,
                                seasonName: s.name
                              });
                              setGateModal("deleteSeason");
                            }}
                            style={{
                              width: '40px',
                              height: '40px',
                              background: '#FEE2E2',
                              border: 'none',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '18px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#FCA5A5';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#FEE2E2';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                            title="Delete season"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}
            </section>
          </div>
        )}

        {/* SEASON DETAIL (matches for selected season) */}
        {user && view === "season-detail" && (
          <div style={{
            maxWidth: "1200px",
            margin: "0 auto",
            minHeight: "100vh"
          }}>
            {/* Breadcrumb */}
            <div className="breadcrumb-nav" style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => setView("dashboard")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0"
                }}
              >
                Players
              </button>
              <span style={{ color: "var(--muted)" }}>›</span>
              <button
                onClick={() => setView("player-dashboard")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0"
                }}
              >
                {players.find(p => p.id === activePlayerId)?.name || "Player"}
              </button>
              <span style={{ color: "var(--muted)" }}>›</span>
              <span style={{ fontSize: "16px", color: "var(--text)" }}>
                {allSeasons.find(s => s.id === activeSeasonId)?.name || "Season"}
              </span>
            </div>

            {/* Sync Status Indicator */}
            <div style={{ marginBottom: "16px" }}>
              <SyncStatusIndicator
                syncStatus={syncStatus}
                onSyncClick={() => {
                  updateSyncStatus({ isSyncing: true });
                  OfflineManager.processSyncQueue().then(() => {
                    updateSyncStatus({ isSyncing: false, lastSync: new Date(), syncError: null });
                    updatePendingCount();
                  }).catch((error) => {
                    updateSyncStatus({ isSyncing: false, syncError: error.message });
                  });
                }}
              />
            </div>

            {/* Header Section - Mobile Only */}
            <section className="season-detail-header-mobile" style={{ marginBottom: "32px", textAlign: "center" }}>
              <h1 style={{
                fontSize: "28px",
                fontWeight: "700",
                margin: "0 0 8px 0",
                color: "#000000"
              }}>
                {allSeasons.find(s => s.id === activeSeasonId)?.name || "Season"}
              </h1>
              <p style={{
                fontSize: "16px",
                color: "var(--muted)",
                margin: "0",
                fontWeight: "400"
              }}>
                Track your performance and analyze your matches
              </p>
            </section>

            {/* Main Content Container */}
            <div className="season-detail-main-content">

            {/* Actions Section - Mobile Only */}
            <section className="season-detail-actions-mobile" style={{ marginBottom: "40px" }}>
              {/* Primary Action - Record Match */}
              <button
                className="btn btn-primary"
                onClick={recordMatchGate}
                style={{
                  width: "100%",
                  padding: "18px",
                  fontSize: "18px",
                  fontWeight: "600",
                  marginBottom: "16px"
                }}
              >
                Record Match
              </button>

              {/* Secondary Actions */}
              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                <button
                  onClick={() => openSeasonEdit(activeSeasonId)}
                  style={{
                    width: '70px',
                    height: '70px',
                    background: '#F2F2F7',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    gap: '4px',
                    padding: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#E5E5EA';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F2F2F7';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Edit season"
                >
                  <span style={{ fontSize: '24px' }}>✏️</span>
                  <span style={{ fontSize: '11px', color: 'var(--text)', fontWeight: '500', textAlign: 'center', lineHeight: '1.2' }}>Edit Season</span>
                </button>

                <button
                  onClick={() => {
                    const season = allSeasons.find(s => s.id === activeSeasonId);
                    setSeasonToDelete({
                      playerId: activePlayerId,
                      seasonId: activeSeasonId,
                      seasonName: season?.name || "Season"
                    });
                    setGateModal("deleteSeason");
                  }}
                  style={{
                    width: '70px',
                    height: '70px',
                    background: '#FEE2E2',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    gap: '4px',
                    padding: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FCA5A5';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#FEE2E2';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Delete season"
                >
                  <span style={{ fontSize: '24px' }}>🗑️</span>
                  <span style={{ fontSize: '11px', color: 'var(--text)', fontWeight: '500', textAlign: 'center', lineHeight: '1.2' }}>Delete</span>
                </button>

                <button
                  onClick={() => setView("match-history")}
                  disabled={matches.length === 0}
                  style={{
                    width: '70px',
                    height: '70px',
                    background: '#F2F2F7',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: matches.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    opacity: matches.length === 0 ? 0.5 : 1,
                    gap: '4px',
                    padding: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (matches.length > 0) {
                      e.currentTarget.style.background = '#E5E5EA';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F2F2F7';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="View match history"
                >
                  <span style={{ fontSize: '24px' }}>📋</span>
                  <span style={{ fontSize: '11px', color: 'var(--text)', fontWeight: '500' }}>History</span>
                </button>

                <button
                  onClick={exportSeasonCsv}
                  disabled={matches.length === 0}
                  style={{
                    width: '70px',
                    height: '70px',
                    background: '#F2F2F7',
                    border: 'none',
                    borderRadius: '12px',
                    cursor: matches.length === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease',
                    opacity: matches.length === 0 ? 0.5 : 1,
                    gap: '4px',
                    padding: '8px'
                  }}
                  onMouseEnter={(e) => {
                    if (matches.length > 0) {
                      e.currentTarget.style.background = '#E5E5EA';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#F2F2F7';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Export CSV"
                >
                  <span style={{ fontSize: '24px' }}>📊</span>
                  <span style={{ fontSize: '11px', color: 'var(--text)', fontWeight: '500' }}>Export</span>
                </button>
              </div>
            </section>

            {/* Season Statistics Section */}
            <section style={{ marginBottom: "40px" }}>
              <h2 style={{ 
                fontSize: "24px", 
                fontWeight: "600", 
                margin: "0 0 20px 0", 
                color: "var(--text)" 
              }}>
                Season Statistics
              </h2>
              
              <div
                className="totals-box"
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: "12px",
                  padding: "20px",
                    background: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                }}
              >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  marginBottom: 8,
                }}
              >
                <div style={{ fontWeight: 700 }}>Season Totals</div>
                    </div>

              {/* Mobile: Cards */}
              <div className="season-stats-cards">
                {/* Overview Cards */}
                <div style={{ display: "grid", gap: "16px", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", marginBottom: "24px" }}>
                  <VisualStatCard label="Matches Played" value={seasonTotals.matchesCount} color="#667eea" />
                  <VisualStatCard label="Sets Won" value={seasonTotals.setWins} color="#43e97b" />
                  <VisualStatCard label="Sets Lost" value={seasonTotals.setLosses} color="#f5576c" />
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                {/* Offensive Stats */}
                <div>
                  <div
                    onClick={() => setShowOffensiveStats(!showOffensiveStats)}
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#667eea",
                      marginBottom: showOffensiveStats ? "16px" : "0",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      userSelect: "none"
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "20px" }}>⚡</span>
                      Offensive
                    </div>
                    <span style={{ fontSize: "16px", transition: "transform 0.2s ease", transform: showOffensiveStats ? "rotate(90deg)" : "rotate(0)", color: "#000" }}>▶</span>
                  </div>
                  {showOffensiveStats && (
                  <div style={{ display: "grid", gap: "16px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                    <VisualStatCard label="Kills" value={seasonTotals.kills} color="#667eea" />
                    <VisualStatCard label="Hit in Play" value={seasonTotals.hitInPlay} color="#667eea" />
                    <VisualStatCard label="Hitting Errors" value={seasonTotals.hittingErrors} color="#FF6B6B" />
                    <VisualStatCard label="Tips" value={seasonTotals.tip} color="#667eea" />
                    <VisualStatCard
                      label="Hitting %"
                      value={seasonTotals.hittingPct.toFixed(3)}
                      isPercentage
                      percentValue={Math.min(seasonTotals.hittingPct * 100, 100)}
                      color="#4ECDC4"
                      tooltip={
                        <div style={{ textAlign: "left" }}>
                          <div style={{ marginBottom: "12px", fontSize: "15px", fontWeight: "600" }}>Formula:</div>
                          <div style={{ marginBottom: "16px", fontSize: "14px", opacity: 0.9 }}>
                            (Kills - Errors) / Total Attempts
                          </div>
                          <div style={{ marginBottom: "12px", fontSize: "15px", fontWeight: "600" }}>Breakdown:</div>
                          <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                            <span style={{ opacity: 0.8 }}>Kills:</span> <span style={{ fontWeight: "600" }}>{seasonTotals.kills}</span>
                          </div>
                          <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                            <span style={{ opacity: 0.8 }}>Errors:</span> <span style={{ fontWeight: "600" }}>{seasonTotals.hittingErrors}</span>
                          </div>
                          <div style={{ marginBottom: "8px", fontSize: "14px" }}>
                            <span style={{ opacity: 0.8 }}>Hit in Play:</span> <span style={{ fontWeight: "600" }}>{seasonTotals.hitInPlay}</span>
                          </div>
                          <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", marginTop: "12px", paddingTop: "12px" }}>
                            <div style={{ fontSize: "14px", marginBottom: "4px" }}>
                              ({seasonTotals.kills} - {seasonTotals.hittingErrors}) / ({seasonTotals.kills} + {seasonTotals.hittingErrors} + {seasonTotals.hitInPlay})
                            </div>
                            <div style={{ fontSize: "16px", fontWeight: "600", color: "#4ECDC4" }}>
                              = {seasonTotals.hittingPct.toFixed(3)}
                            </div>
                          </div>
                        </div>
                      }
                    />
                    <VisualStatCard
                      label="Kill %"
                      value={`${seasonTotals.killPct}%`}
                      isPercentage
                      percentValue={seasonTotals.killPct}
                      color="#4ECDC4"
                    />
                  </div>
                  )}
                </div>

                {/* Serving Stats */}
                <div>
                  <div
                    onClick={() => setShowServingStats(!showServingStats)}
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#764ba2",
                      marginBottom: showServingStats ? "16px" : "0",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      userSelect: "none"
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "20px" }}>🏐</span>
                      Serving
                    </div>
                    <span style={{ fontSize: "16px", transition: "transform 0.2s ease", transform: showServingStats ? "rotate(90deg)" : "rotate(0)", color: "#000" }}>▶</span>
                  </div>
                  {showServingStats && (
                  <div style={{ display: "grid", gap: "16px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                    <VisualStatCard label="Aces" value={seasonTotals.aces} color="#764ba2" />
                    <VisualStatCard label="Serve Attempts" value={seasonTotals.serveAttempts} color="#764ba2" />
                    <VisualStatCard label="Service Errors" value={seasonTotals.serviceErrors} color="#FF6B6B" />
                    <VisualStatCard
                      label="Serving %"
                      value={`${seasonTotals.servingPct}%`}
                      isPercentage
                      percentValue={seasonTotals.servingPct}
                      color="#4ECDC4"
                    />
                    <VisualStatCard
                      label="Ace %"
                      value={`${seasonTotals.acePct}%`}
                      isPercentage
                      percentValue={seasonTotals.acePct}
                      color="#4ECDC4"
                    />
                  </div>
                  )}
                </div>

                {/* Defensive & Other Stats */}
                <div>
                  <div
                    onClick={() => setShowDefensiveStats(!showDefensiveStats)}
                    style={{
                      fontSize: "14px",
                      fontWeight: "700",
                      color: "#F39C12",
                      marginBottom: showDefensiveStats ? "16px" : "0",
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      userSelect: "none"
                    }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "20px" }}>🛡️</span>
                      Defensive & Other
                    </div>
                    <span style={{ fontSize: "16px", transition: "transform 0.2s ease", transform: showDefensiveStats ? "rotate(90deg)" : "rotate(0)", color: "#000" }}>▶</span>
                  </div>
                  {showDefensiveStats && (
                  <div style={{ display: "grid", gap: "16px", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
                    <VisualStatCard label="Digs" value={seasonTotals.digs} color="#F39C12" />
                    <VisualStatCard label="Blocks" value={seasonTotals.sets} color="#F39C12" />
                    <VisualStatCard label="Block Touch" value={seasonTotals.blockTouch} color="#F39C12" />
                    <VisualStatCard label="Block Errors" value={seasonTotals.blockError} color="#FF6B6B" />
                    <VisualStatCard label="Assists" value={seasonTotals.assists} color="#F39C12" />
                    <VisualStatCard label="Sets (W-L)" value={`${seasonTotals.setWins}-${seasonTotals.setLosses}`} color="#667eea" />
                  </div>
                  )}
                  </div>
                </div>
              </div>
              {/* End Mobile Cards */}

              {/* Desktop: Table Layout */}
              <div className="season-stats-table">
                <table className="stats-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Statistic</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Overview */}
                    <tr className="category-row">
                      <td colSpan="3">Overview</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Matches Played</td>
                      <td>{seasonTotals.matchesCount}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Sets Won</td>
                      <td>{seasonTotals.setWins}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Sets Lost</td>
                      <td>{seasonTotals.setLosses}</td>
                    </tr>

                    {/* Offensive */}
                    <tr className="category-row">
                      <td colSpan="3">⚡ Offensive</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Kills</td>
                      <td>{seasonTotals.kills}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Hit in Play</td>
                      <td>{seasonTotals.hitInPlay}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Hitting Errors</td>
                      <td>{seasonTotals.hittingErrors}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Tips</td>
                      <td>{seasonTotals.tip}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Hitting %</td>
                      <td>{seasonTotals.hittingPct.toFixed(3)}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Kill %</td>
                      <td>{seasonTotals.killPct}%</td>
                    </tr>

                    {/* Serving */}
                    <tr className="category-row">
                      <td colSpan="3">🏐 Serving</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Aces</td>
                      <td>{seasonTotals.aces}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Serve Attempts</td>
                      <td>{seasonTotals.serveAttempts}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Service Errors</td>
                      <td>{seasonTotals.serviceErrors}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Serving %</td>
                      <td>{seasonTotals.servingPct}%</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Ace %</td>
                      <td>{seasonTotals.acePct}%</td>
                    </tr>

                    {/* Defensive & Other */}
                    <tr className="category-row">
                      <td colSpan="3">🛡️ Defensive & Other</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Digs</td>
                      <td>{seasonTotals.digs}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Blocks</td>
                      <td>{seasonTotals.sets}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Block Touch</td>
                      <td>{seasonTotals.blockTouch}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Block Errors</td>
                      <td>{seasonTotals.blockError}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Assists</td>
                      <td>{seasonTotals.assists}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>Sets (W-L)</td>
                      <td>{seasonTotals.setWins}-{seasonTotals.setLosses}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* End Desktop Tables */}

              </div>
              {/* End totals-box */}
            </section>

            {/* Tournaments Section - Only show if feature is enabled */}
            {FEATURES.TOURNAMENTS && (
              <section style={{ 
                background: "#fff", 
                border: "1px solid var(--border)", 
                borderRadius: "12px", 
                padding: "24px", 
                marginBottom: "40px",
                minHeight: "80px"
              }}>
                {/* Mobile Header - Collapsible */}
                <div
                  className="tournaments-mobile-header"
                  onClick={() => setShowTournaments(!showTournaments)}
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: showTournaments ? "20px" : "0",
                    minHeight: "32px"
                  }}
                >
                  <h2
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      margin: "0",
                      color: "var(--text)",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    Tournaments
                    <span style={{
                      fontSize: "16px",
                      fontWeight: "600",
                      color: "var(--text)",
                      transition: "all 0.2s ease"
                    }}>
                      {showTournaments ? "▼" : "▶"}
                    </span>
                  </h2>
                  {showTournaments && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTournamentForm({ name: "", date: "", location: "" });
                        setView("tournament-create");
                      }}
                      className="btn btn-primary"
                      style={{
                        width: '44px',
                        height: '44px',
                        minWidth: '44px',
                        minHeight: '44px',
                        padding: 0,
                        fontSize: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '10px',
                        flexShrink: 0,
                        lineHeight: 1
                      }}
                      title="Create tournament"
                    >
                      +
                    </button>
                  )}
                </div>

                {/* Desktop Header - Always Visible */}
                <div className="tournaments-desktop-header" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                  <h2 style={{ fontSize: "24px", fontWeight: "600", margin: "0", color: "var(--text)" }}>
                    Tournaments
                  </h2>
                  <button
                    onClick={() => {
                      setTournamentForm({ name: "", date: "", location: "" });
                      setView("tournament-create");
                    }}
                    className="btn btn-primary"
                    style={{ width: "36px", height: "36px", minWidth: "36px", minHeight: "36px", padding: "0", fontSize: "20px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "var(--radius-md)", flexShrink: 0, lineHeight: 1 }}
                    title="Create tournament"
                  >
                    +
                  </button>
                </div>

                {/* Mobile Cards */}
                {showTournaments && (
                  <div className="tournaments-cards-mobile">
                {tournaments.length === 0 ? (
                  <div style={{
                    background: "var(--bg-light)",
                    border: "2px dashed var(--border)",
                    borderRadius: "12px",
                    padding: "40px 20px",
                    textAlign: "center"
                  }}>
                    <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏆</div>
                    <h3 style={{ 
                      fontSize: "18px", 
                      fontWeight: "600", 
                      margin: "0 0 8px 0", 
                      color: "var(--text)" 
                    }}>
                      No tournaments yet
                    </h3>
                    <p style={{ 
                      fontSize: "12px", 
                      color: "var(--muted)", 
                      margin: "0" 
                    }}>
                      Create one to group related matches together
                    </p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {tournaments.map((tournament) => {
                      // Calculate tournament stats
                      const stats = calculateTournamentStats(tournament.id);

                      return (
                        <div
                          key={tournament.id}
                          className="card"
                          onClick={() => {
                            setActiveTournamentId(tournament.id);
                            setView("tournament-detail");
                            window.scrollTo(0, 0);
                          }}
                          style={{
                            padding: '16px',
                            cursor: 'pointer',
                            transition: 'transform 0.15s ease, box-shadow 0.15s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.01)';
                            e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ marginBottom: '8px' }}>
                            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '4px', color: 'var(--text)', letterSpacing: '-0.02em' }}>
                              {tournament.name}
                            </div>
                            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                              {(tournament.startDate || tournament.date) && (
                                <>
                                  {tournament.startDate
                                    ? new Date(tournament.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                    : new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                  }
                                  {tournament.endDate && tournament.endDate !== tournament.startDate && (
                                    <span> - {new Date(tournament.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                  )}
                                </>
                              )}
                              {(tournament.startDate || tournament.date) && tournament.location && ' • '}
                              {tournament.location}
                            </div>
                          </div>

                          {/* Stats */}
                          {stats.matchesCount > 0 && (
                            <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                              {stats.matchesCount} match{stats.matchesCount === 1 ? "" : "es"}
                              {(stats.setWins > 0 || stats.setLosses > 0) && (
                                <span> • {stats.setWins}-{stats.setLosses} sets</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                  </div>
                )}

                {/* Desktop Table */}
                <div className="tournaments-table-desktop">
                  {tournaments.length === 0 ? (
                    <div style={{
                      background: "var(--bg-light)",
                      border: "2px dashed var(--border)",
                      borderRadius: "12px",
                      padding: "40px 20px",
                      textAlign: "center"
                    }}>
                      <div style={{ fontSize: "48px", marginBottom: "16px" }}>🏆</div>
                      <h3 style={{
                        fontSize: "18px",
                        fontWeight: "600",
                        margin: "0 0 8px 0",
                        color: "var(--text)"
                      }}>
                        No tournaments yet
                      </h3>
                      <p style={{
                        fontSize: "14px",
                        color: "var(--muted)",
                        margin: "0"
                      }}>
                        Create one to group related matches together
                      </p>
                    </div>
                  ) : (
                    <table className="tournaments-table">
                      <thead>
                        <tr>
                          <th>Tournament Name</th>
                          <th>Date Range</th>
                          <th>Location</th>
                          <th>Matches</th>
                          <th>Sets Record</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tournaments.map((tournament) => {
                          const stats = calculateTournamentStats(tournament.id);

                          return (
                            <tr
                              key={tournament.id}
                              onClick={() => {
                                setActiveTournamentId(tournament.id);
                                setView("tournament-detail");
                                window.scrollTo(0, 0);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{tournament.name}</td>
                              <td>
                                {(tournament.startDate || tournament.date) && (
                                  <>
                                    {tournament.startDate
                                      ? new Date(tournament.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                      : new Date(tournament.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                    }
                                    {tournament.endDate && tournament.endDate !== tournament.startDate && (
                                      <span> - {new Date(tournament.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                    )}
                                  </>
                                )}
                              </td>
                              <td>{tournament.location || '-'}</td>
                              <td>{stats.matchesCount}</td>
                              <td>{stats.setWins}-{stats.setLosses}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
            </section>
            )}

            </div>
            {/* End Main Content */}
          </div>
        )}

        {/* MATCH HISTORY VIEW */}
        {user && view === "match-history" && activePlayerId && activeSeasonId && (
          <div style={{
            maxWidth: "1200px",
            margin: "0 auto",
            minHeight: "100vh"
          }}>
            {/* Breadcrumb */}
            <div className="breadcrumb-nav" style={{ marginBottom: "24px", display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                onClick={() => setView("dashboard")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0"
                }}
              >
                Players
              </button>
              <span style={{ color: "var(--muted)" }}>›</span>
              <button
                onClick={() => setView("player-dashboard")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0"
                }}
              >
                {players.find(p => p.id === activePlayerId)?.name || "Player"}
              </button>
              <span style={{ color: "var(--muted)" }}>›</span>
              <button
                onClick={() => setView("season-detail")}
                style={{
                  background: "none",
                  border: "none",
                  color: "#007AFF",
                  fontSize: "16px",
                  cursor: "pointer",
                  padding: "0"
                }}
              >
                {allSeasons.find(s => s.id === activeSeasonId)?.name || "Season"}
              </button>
              <span style={{ color: "var(--muted)" }}>›</span>
              <span style={{ fontSize: "16px", color: "var(--text)" }}>
                Match History
              </span>
            </div>

            {/* Header */}
            <div style={{
              textAlign: "center",
              marginBottom: "32px"
            }}>
              <h1 style={{
                fontSize: "28px",
                fontWeight: "700",
                margin: "0 0 8px 0",
                color: "#000000"
              }}>
                Match History
              </h1>
              <p style={{
                fontSize: "16px",
                color: "var(--muted)",
                margin: "0",
                fontWeight: "400"
              }}>
                View and manage all matches for this season
              </p>
            </div>

            {/* Match History Section */}
            <section style={{ 
              background: "#fff", 
              borderRadius: "12px", 
              marginBottom: "40px",
              padding: "24px"
            }}>
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "flex-end", 
                marginBottom: "20px" 
              }}>
                  <span className="muted" style={{ fontSize: "12px" }}>
                    {matches.length} match{matches.length !== 1 ? 'es' : ''}
                  </span>
              </div>

            {/* Desktop Table View */}
            <div className="match-history-table-container">
              <table className="match-history-table">
                <thead>
                  <tr>
                    <th style={{ width: "40px" }}></th>
                    <th onClick={() => setDateSortDir((d) => (d === "asc" ? "desc" : "asc"))} style={{ cursor: "pointer" }}>
                      Date {dateSortDir === "asc" ? "▲" : "▼"}
                    </th>
                    <th>Opponent</th>
                    <th>Result</th>
                    <th>Score</th>
                    <th>Hitting %</th>
                    <th>Serving %</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.length === 0 && (
                    <tr>
                      <td colSpan="7" style={{ textAlign: "center", padding: "40px", color: "var(--muted)" }}>
                        No matches found for this season.
                      </td>
                    </tr>
                  )}
                  {sortedMatches.map((m) => {
                    const aa = Number(m.hitInPlay || m.attackAttempts || 0);
                    const k  = Number(m.kills || 0);
                    const he = Number(m.hittingErrors || 0);
                    const sa = Number(m.serveAttempts || 0);
                    const se = Number(m.serviceErrors || 0);
                    const ac = Number(m.aces || 0);
                    const hittingPctRow = (aa + k + he) ? ((k - he) / (aa + k + he) * 100).toFixed(1) : "0.0";
                    const servingPct = sa ? ((sa - se) / sa * 100).toFixed(1) : "0.0";
                    const { w, l } = getSetWL(m.setScores || []);
                    const isMatchWin = w > l;
                    const isExpanded = expandedMatchIds.has(m.id);

                    return (
                      <React.Fragment key={m.id}>
                        <tr className="match-history-row">
                          <td>
                            <button
                              className="expand-btn"
                              onClick={() => toggleExpandedRow(m.id)}
                              title={isExpanded ? "Collapse" : "Expand"}
                            >
                              {isExpanded ? "▼" : "▶"}
                            </button>
                          </td>
                          <td>{m.matchDate || "-"}</td>
                          <td style={{ fontWeight: "600" }}>{m.opponent || "Unknown"}</td>
                          <td>
                            <span className={`result-badge ${isMatchWin ? 'win' : 'loss'}`}>
                              {isMatchWin ? "W" : "L"}
                            </span>
                          </td>
                          <td>{w}-{l}</td>
                          <td>{hittingPctRow}%</td>
                          <td>{servingPct}%</td>
                          <td>
                          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                            {(m.isPublic === true || m.isPublic === 'true') && m.shareToken && (
                              <button
                                className="table-action-btn share-btn"
                                onClick={() => {
                                  const shareUrl = `${window.location.origin}/match/${m.shareToken}`;
                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(shareUrl).then(() => {
                                      setShowShareModal(true);
                                      setTimeout(() => setShowShareModal(false), 3000);
                                    }).catch(() => {
                                      alert('Failed to copy link. Please copy manually: ' + shareUrl);
                                    });
                                  }
                                }}
                                title="Copy share link"
                              >
                                🔗
                              </button>
                            )}
                            <button
                              className="table-action-btn edit-btn"
                              onClick={() => startEditMatch(m)}
                              title="Edit match"
                            >
                              ✏️
                            </button>
                            <button
                              className="table-action-btn delete-btn"
                              onClick={() => {
                                if (!isOnline) {
                                  setShowOfflineDeleteModal(true);
                                } else {
                                  setPendingDeleteMatchId(m.id);
                                }
                              }}
                              title="Delete match"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded row with full stats */}
                      {isExpanded && (
                        <tr className="expanded-row">
                          <td colSpan="8">
                            <div className="expanded-content">
                              <div className="expanded-stats-grid">
                                {/* Hitting Stats */}
                                <div className="stat-section">
                                  <h4>Hitting</h4>
                                  <div className="stat-item">
                                    <span>Kills:</span>
                                    <strong>{k}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Errors:</span>
                                    <strong>{he}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Attempts:</span>
                                    <strong>{aa}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Hitting %:</span>
                                    <strong>{hittingPctRow}%</strong>
                                  </div>
                                </div>

                                {/* Serving Stats */}
                                <div className="stat-section">
                                  <h4>Serving</h4>
                                  <div className="stat-item">
                                    <span>Aces:</span>
                                    <strong>{ac}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Errors:</span>
                                    <strong>{se}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Attempts:</span>
                                    <strong>{sa}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Serving %:</span>
                                    <strong>{servingPct}%</strong>
                                  </div>
                                </div>

                                {/* Defense Stats */}
                                <div className="stat-section">
                                  <h4>Defense</h4>
                                  <div className="stat-item">
                                    <span>Digs:</span>
                                    <strong>{m.digs || 0}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Blocks:</span>
                                    <strong>{((Number(m.sets || 0)) + (Number(m.halfBlock || 0) * 0.5)).toFixed(1)}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Block Assists:</span>
                                    <strong>{m.blockAssists || 0}</strong>
                                  </div>
                                  {((m.digErrors || 0) > 0) && (
                                    <div className="stat-item">
                                      <span>Dig Errors:</span>
                                      <strong>{m.digErrors || 0}</strong>
                                    </div>
                                  )}
                                  {((m.blockError || m.blockErrors || 0) > 0) && (
                                    <div className="stat-item">
                                      <span>Block Errors:</span>
                                      <strong>{m.blockError || m.blockErrors || 0}</strong>
                                    </div>
                                  )}
                                </div>

                                {/* Other Stats */}
                                <div className="stat-section">
                                  <h4>Other</h4>
                                  <div className="stat-item">
                                    <span>Tips:</span>
                                    <strong>{m.tip || 0}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>Assists:</span>
                                    <strong>{m.assists || 0}</strong>
                                  </div>
                                  <div className="stat-item">
                                    <span>SR Touches:</span>
                                    <strong>{m.srTouches || 0}</strong>
                                  </div>
                                </div>
                              </div>

                              {/* Set Scores */}
                              {m.setScores && m.setScores.length > 0 && (
                                <div className="set-scores-section">
                                  <h4>Set Scores</h4>
                                  <div className="set-scores-list">
                                    {m.setScores.map((score, idx) => (
                                      <div key={idx} className="set-score-item">
                                        Set {idx + 1}: <strong>{score.us}-{score.opp}</strong>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="match-history-cards" style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {/* Sort button */}
              <div style={{ 
                display: "flex", 
                justifyContent: "flex-end", 
                marginBottom: "12px" 
              }}>
                      <button
                        onClick={() => setDateSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                        aria-label={`Sort by date ${dateSortDir === "asc" ? "descending" : "ascending"}`}
                        style={{
                    background: "var(--bg-light)",
                    border: "1px solid var(--border)",
                    borderRadius: "6px",
                    padding: "8px 12px",
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                    gap: "6px",
                    fontSize: "12px",
                    color: "var(--text)"
                        }}
                      >
                  <span>Sort by Date</span>
                        <span>{dateSortDir === "asc" ? "▲" : "▼"}</span>
                      </button>
              </div>
                  {matches.length === 0 && (
                <div style={{ 
                  textAlign: "center", 
                  padding: "40px", 
                  color: "var(--muted)",
                  background: "var(--bg-light)",
                  borderRadius: "8px",
                  border: "1px solid var(--border)"
                }}>
                        No matches found for this season.
                </div>
                  )}
                  {sortedMatches.map((m) => {
                    const sa = Number(m.serveAttempts || 0);
                    const se = Number(m.serviceErrors || 0);
                    // const ac = Number(m.aces || 0);
                    const aa = Number(m.hitInPlay || m.attackAttempts || 0);
                    const k  = Number(m.kills || 0);
                    const he = Number(m.hittingErrors || 0);
                    const servingPctRow = sa ? Math.round(((sa - Math.max(0, se)) / sa) * 100) : 0;
                    // const acePctRow     = sa ? Math.round((Math.max(0, ac) / sa) * 100) : 0;

                    const hittingPctRow = (aa + k + he) ? (k - he) / (aa + k + he) : 0;
                    const killPctRow = (aa + k + he) ? Math.round((k / (aa + k + he)) * 100) : 0;
                    const { w, l } = getSetWL(m.setScores || []);
                    const scores = m.setScores || [];
                    const isMatchWin = w > l;

                    return (
                  <div key={m.id} style={{
                    background: "white",
                    borderRadius: "16px",
                    padding: "24px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    overflow: "hidden",
                    marginBottom: "16px"
                  }}>
                    {/* Win/Loss indicator */}
                    <div style={{
                      position: "absolute",
                      top: "0",
                      right: "0",
                      width: "0",
                      height: "0",
                      borderLeft: "20px solid transparent",
                      borderTop: `20px solid ${isMatchWin ? "var(--success)" : "var(--danger)"}`,
                      borderRadius: "0 12px 0 0"
                    }}></div>

                    {/* Header row with date, opponent, and result */}
                    <div style={{ marginTop: "8px" }}>
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "space-between", 
                        alignItems: "center", 
                        marginBottom: "8px",
                        flexWrap: "wrap",
                        gap: "8px"
                      }}>
                        <div style={{ 
                          fontWeight: "700", 
                          color: "var(--text)", 
                          fontSize: "18px"
                        }}>
                          {m.matchDate || "-"}
                        </div>
                        
                        {/* Match Result Badge - Inline */}
                        <div style={{ 
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}>
                          {/* Public indicator */}
                          {(m.isPublic === true || m.isPublic === 'true') && m.shareToken && (
                            <div style={{
                              background: "#22c55e",
                              color: "white",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              fontSize: "12px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px"
                            }}>
                              <span>🌐</span>
                              <span>Public</span>
                            </div>
                          )}

                          {/* Match Result Badge */}
                        <div style={{ 
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          background: isMatchWin ? "var(--success)" : "var(--danger)",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          fontSize: "12px",
                          fontWeight: "600"
                        }}>
                          <span>{w}-{l}</span>
                          <span style={{ 
                            fontSize: "10px", 
                            opacity: "0.9",
                            textTransform: "uppercase",
                            letterSpacing: "0.3px"
                          }}>
                            {isMatchWin ? "W" : "L"}
                          </span>
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ 
                        fontSize: "16px", 
                        color: "var(--muted)",
                        fontWeight: "500",
                        marginBottom: "8px"
                      }}>
                        vs {m.opponent || "-"}
                      </div>
                      
                      {/* Set Scores - Horizontal Layout */}
                      {scores.length > 0 && (
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexWrap: "wrap"
                        }}>
                          <span style={{
                            fontSize: "12px",
                            color: "var(--muted)",
                            fontWeight: "600",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px"
                          }}>
                            Sets:
                          </span>
                          <div style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center"
                          }}>
                              {scores.map((s, i) => {
                                const us = Number(s?.us || 0);
                                const opp = Number(s?.opp || 0);
                              const isSetWin = us > opp;
                                return (
                                <div key={i} style={{
                                  background: isSetWin ? "var(--success)" : "var(--bg-light)",
                                  color: isSetWin ? "white" : "var(--text)",
                                  padding: "4px 8px",
                                  borderRadius: "6px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  fontVariantNumeric: "tabular-nums",
                                  border: isSetWin ? "none" : "1px solid var(--border)"
                                }}>
                                    {us}–{opp}
                                </div>
                                );
                              })}
                            </div>
                        </div>
                      )}
                    </div>

                    {/* Stats grid */}
                    <div style={{ 
                      display: "grid", 
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
                      gap: "16px",
                      fontSize: "14px"
                    }}>
                      {/* Hitting stats */}
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: "10px",
                        background: "var(--bg-light)",
                        padding: "12px",
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.05)"
                      }}>
                        <div style={{ 
                          fontWeight: "700", 
                          color: "var(--primary)", 
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "4px"
                        }}>HITTING</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Kills:</span>
                          <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--text)" }}>{m.kills ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Hit in Play:</span>
                          <span style={{ fontWeight: "500" }}>{(m.hitInPlay || m.attackAttempts) ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Errors:</span>
                          <span style={{ fontWeight: "500" }}>{m.hittingErrors ?? 0}</span>
                        </div>
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          paddingTop: "6px",
                          marginTop: "4px"
                        }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Hitting %:</span>
                          <span style={{ 
                            fontWeight: "700", 
                            fontSize: "15px",
                            color: "var(--primary)"
                          }}>{hittingPctRow.toFixed(3)}</span>
                        </div>
                      </div>

                      {/* Serving stats */}
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: "10px",
                        background: "var(--bg-light)",
                        padding: "12px",
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.05)"
                      }}>
                        <div style={{ 
                          fontWeight: "700", 
                          color: "var(--primary)", 
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "4px"
                        }}>SERVING</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Aces:</span>
                          <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--text)" }}>{m.aces ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Attempts:</span>
                          <span style={{ fontWeight: "500" }}>{m.serveAttempts ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Errors:</span>
                          <span style={{ fontWeight: "500" }}>{m.serviceErrors ?? 0}</span>
                        </div>
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          paddingTop: "6px",
                          marginTop: "4px"
                        }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Srv %:</span>
                          <span style={{ 
                            fontWeight: "700", 
                            fontSize: "15px",
                            color: "var(--primary)"
                          }}>{servingPctRow}%</span>
                        </div>
                      </div>

                      {/* Defense stats */}
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: "10px",
                        background: "var(--bg-light)",
                        padding: "12px",
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.05)"
                      }}>
                        <div style={{ 
                          fontWeight: "700", 
                          color: "var(--primary)", 
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "4px"
                        }}>DEFENSE</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Digs:</span>
                          <span style={{ fontWeight: "700", fontSize: "15px", color: "var(--text)" }}>{m.digs ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Dig Errors:</span>
                          <span style={{ fontWeight: "500" }}>{m.digErrors ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Blocks:</span>
                          <span style={{ fontWeight: "500" }}>{m.blocks ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Block Touch:</span>
                          <span style={{ fontWeight: "500" }}>{m.blockTouch ?? 0}</span>
                        </div>
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          paddingTop: "6px",
                          marginTop: "4px"
                        }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Block Error:</span>
                          <span style={{ 
                            fontWeight: "700", 
                            fontSize: "15px",
                            color: "var(--primary)"
                          }}>{m.blockError ?? 0}</span>
                        </div>
                      </div>

                      {/* Additional stats */}
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column", 
                        gap: "10px",
                        background: "var(--bg-light)",
                        padding: "12px",
                        borderRadius: "12px",
                        border: "1px solid rgba(0,0,0,0.05)"
                      }}>
                        <div style={{ 
                          fontWeight: "700", 
                          color: "var(--primary)", 
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "4px"
                        }}>OTHER</div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Tips:</span>
                          <span style={{ fontWeight: "500" }}>{m.tip ?? 0}</span>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Assists:</span>
                          <span style={{ fontWeight: "500" }}>{m.assists ?? 0}</span>
                        </div>
                        <div style={{ 
                          display: "flex", 
                          justifyContent: "space-between", 
                          alignItems: "center",
                          paddingTop: "6px",
                          marginTop: "4px"
                        }}>
                          <span style={{ color: "var(--muted)", fontSize: "13px" }}>Kill %:</span>
                          <span style={{ 
                            fontWeight: "700", 
                            fontSize: "15px",
                            color: "var(--primary)"
                          }}>{killPctRow}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Match Media */}
                    {m.matchMedia && m.matchMedia.length > 0 && (
                      <div style={{
                        background: "var(--bg-light)",
                        borderRadius: "12px",
                        padding: "16px",
                        border: "1px solid rgba(0,0,0,0.05)",
                        marginTop: "16px"
                      }}>
                        <div style={{
                          fontWeight: "700",
                          color: "var(--primary)",
                          fontSize: "11px",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          marginBottom: "12px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px"
                        }}>
                          📸 MEDIA ({m.matchMedia.length})
                        </div>
                        
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                          gap: "12px"
                        }}>
                          {m.matchMedia.map((media, index) => (
                            <div key={index} style={{
                              position: "relative",
                              background: "#f8f9fa",
                              borderRadius: "8px",
                              overflow: "hidden",
                              border: "1px solid var(--border)",
                              transition: "transform 0.2s ease, box-shadow 0.2s ease",
                              cursor: "pointer"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "scale(1.02)";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.boxShadow = "none";
                            }}
                            onClick={() => {
                              if (media.type === 'image') {
                                // Open lightbox for images
                                const imageMedia = m.matchMedia.filter(med => med.type === 'image');
                                const imageIndex = imageMedia.findIndex(img => img.url === media.url);
                                setLightboxImages(imageMedia);
                                setLightboxIndex(imageIndex);
                                setLightboxOpen(true);
                              }
                            }}>
                              {media.type === 'image' ? (
                                <img
                                  src={media.url}
                                  alt={media.name}
                                  style={{
                                    width: "100%",
                                    height: "80px",
                                    objectFit: "cover",
                                    display: "block"
                                  }}
                                />
                              ) : (
                                <div style={{
                                  width: "100%",
                                  height: "80px",
                                  background: "var(--primary)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: "white",
                                  fontSize: "24px"
                                }}>
                                  🎥
                                </div>
                              )}
                              
                              {/* Media type badge */}
                              <div style={{
                                position: "absolute",
                                top: "4px",
                                right: "4px",
                                background: "rgba(0,0,0,0.7)",
                                color: "white",
                                padding: "2px 6px",
                                borderRadius: "4px",
                                fontSize: "10px",
                                fontWeight: "600",
                                textTransform: "uppercase"
                              }}>
                                IMG
                              </div>
                              
                              {/* File name */}
                              <div style={{
                                padding: "8px",
                                fontSize: "11px",
                                color: "var(--text)",
                                fontWeight: "500",
                                textAlign: "center",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                              }}>
                                {media.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div style={{ 
                      display: "flex", 
                      gap: "8px", 
                      justifyContent: "flex-end",
                      paddingTop: "12px",
                      borderTop: "2px solid var(--border)",
                      marginTop: "8px",
                      flexWrap: "wrap"
                    }}>
                            {/* Share button */}
                            {(m.isPublic === true || m.isPublic === 'true') && m.shareToken && (
                              <button
                                className="btn btn-primary"
                                onClick={() => {
                                  const shareUrl = `${window.location.origin}/match/${m.shareToken}`;

                                  // GA: Track share link copy
                                  if (window.gtag) {
                                    window.gtag("event", "share_link_copied", {
                                      event_category: "Match Sharing",
                                      event_label: m.playerName || 'Unknown Player',
                                      custom_parameters: {
                                        match_share_token: m.shareToken,
                                        player_name: m.playerName || 'Unknown Player',
                                        opponent: m.opponent || 'Unknown Opponent',
                                        match_date: m.matchDate || 'Unknown Date'
                                      }
                                    });
                                  }

                                  // Try modern clipboard API first, fallback to older method
                                  if (navigator.clipboard && navigator.clipboard.writeText) {
                                    navigator.clipboard.writeText(shareUrl).then(() => {
                                      setShowShareModal(true);
                                      setTimeout(() => setShowShareModal(false), 3000);
                                    }).catch(() => {
                                      alert('Failed to copy link. Please copy manually: ' + shareUrl);
                                    });
                                  } else {
                                    // Fallback for browsers that don't support clipboard API
                                    const textArea = document.createElement('textarea');
                                    textArea.value = shareUrl;
                                    textArea.style.position = 'fixed';
                                    textArea.style.left = '-999999px';
                                    document.body.appendChild(textArea);
                                    textArea.focus();
                                    textArea.select();
                                    try {
                                      document.execCommand('copy');
                                      setShowShareModal(true);
                                      setTimeout(() => setShowShareModal(false), 3000);
                                    } catch (err) {
                                      alert('Failed to copy link. Please copy manually: ' + shareUrl);
                                    }
                                    document.body.removeChild(textArea);
                                  }
                                }}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  whiteSpace: "nowrap",
                                  minWidth: "60px",
                                  gap: "4px"
                                }}
                              >
                                📤 Share
                              </button>
                            )}
                            
                            <button
                              className="btn btn-primary"
                              onClick={() => startEditMatch(m)}
                              style={{
                                padding: "8px 16px",
                                fontSize: "13px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                whiteSpace: "nowrap",
                                minWidth: "60px"
                              }}
                            >
                              Edit
                            </button>
                            {m.notes && (
                              <button
                                className="btn btn-primary"
                                onClick={() => setViewNotesMatch(m)}
                                style={{
                                  padding: "8px 16px",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  transition: "all 0.3s ease",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  whiteSpace: "nowrap",
                                  minWidth: "60px"
                                }}
                              >
                                Notes
                              </button>
                            )}
                            <button
                              className="btn btn-danger"
                              onClick={() => {
                                if (!isOnline) {
                                  setShowOfflineDeleteModal(true);
                                } else {
                                  setPendingDeleteMatchId(m.id);
                                }
                              }}
                              style={{
                                padding: "8px 16px",
                                fontSize: "13px",
                                fontWeight: "600",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                whiteSpace: "nowrap",
                                minWidth: "60px"
                              }}
                            >
                              Delete
                            </button>
                          </div>
                  </div>
                    );
                  })}
            </div>
            </section>
          </div>
        )}

        {/* RECORD MATCH */}
        {user && activePlayerId && activeSeasonId && view === "record" && (
          <div style={{ maxWidth: "1800px", margin: "0 auto" }}>
            {/* Toggle Button - Draggable on right side when closed */}
            {!isFloatingWidgetExpanded && (
              <button
                type="button"
                onClick={(e) => {
                  if (!isDraggingScoreboard) {
                    setIsFloatingWidgetExpanded(true);
                  }
                }}
                onTouchStart={(e) => {
                  const touch = e.touches[0];
                  setDragStartY(touch.clientY);
                  setIsDraggingScoreboard(false);
                }}
                onTouchMove={(e) => {
                  if (dragStartY === null) return;
                  const touch = e.touches[0];
                  const distance = Math.abs(touch.clientY - dragStartY);

                  // Only start dragging if moved more than 10px
                  if (distance > 10) {
                    setIsDraggingScoreboard(true);
                    const windowHeight = window.innerHeight;
                    const newY = (touch.clientY / windowHeight) * 100;
                    setScoreboardButtonY(Math.max(10, Math.min(90, newY)));
                    e.preventDefault();
                  }
                }}
                onTouchEnd={(e) => {
                  const wasDragging = isDraggingScoreboard;
                  setDragStartY(null);
                  setIsDraggingScoreboard(false);

                  // If wasn't dragging, allow the click to happen
                  if (!wasDragging) {
                    setIsFloatingWidgetExpanded(true);
                  }
                }}
                onMouseDown={(e) => {
                  setDragStartY(e.clientY);
                  setIsDraggingScoreboard(false);
                }}
                onMouseMove={(e) => {
                  if (e.buttons === 1 && dragStartY !== null) {
                    const distance = Math.abs(e.clientY - dragStartY);

                    if (distance > 10) {
                      setIsDraggingScoreboard(true);
                      const windowHeight = window.innerHeight;
                      const newY = (e.clientY / windowHeight) * 100;
                      setScoreboardButtonY(Math.max(10, Math.min(90, newY)));
                    }
                  }
                }}
                onMouseUp={() => {
                  setDragStartY(null);
                  setTimeout(() => setIsDraggingScoreboard(false), 100);
                }}
                onMouseLeave={() => {
                  setDragStartY(null);
                  setTimeout(() => setIsDraggingScoreboard(false), 100);
                }}
                style={{
                  position: 'fixed',
                  right: 0,
                  top: `${scoreboardButtonY}%`,
                  transform: 'translateY(-50%)',
                  background: 'var(--primary)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px 0 0 8px',
                  padding: '16px 12px',
                  fontSize: '12px',
                  fontWeight: '900',
                  cursor: isDraggingScoreboard ? 'grabbing' : 'grab',
                  boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.2)',
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed',
                  letterSpacing: '1px',
                  zIndex: 999,
                  touchAction: 'none',
                  userSelect: 'none'
                }}
              >
                {isDraggingScoreboard ? '⋮⋮' : 'SCOREBOARD'}
              </button>
            )}

            {/* Slide-out Scoreboard Widget */}
            <div
              className="floating-set-score-widget"
              style={{
                position: "fixed",
                right: isFloatingWidgetExpanded ? 0 : '-100%',
                top: `${scoreboardButtonY}%`,
                transform: 'translateY(-50%)',
                height: 'auto',
                maxHeight: '80vh',
                width: '100%',
                maxWidth: '400px',
                zIndex: 1000,
                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                border: "2px solid #dee2e6",
                borderLeft: "3px solid var(--primary)",
                borderRadius: "12px 0 0 12px",
                padding: "12px",
                boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
                transition: 'right 0.3s ease',
                overflowY: 'auto',
                fontFamily: "'Courier New', monospace"
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "12px",
                paddingBottom: "8px",
                borderBottom: "2px solid var(--primary)",
                transition: "all 0.3s ease"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "50%",
                    background: "var(--primary)",
                    boxShadow: "0 0 12px rgba(46, 90, 172, 0.6)"
                  }}></div>
                  <h3 style={{
                    fontSize: "16px",
                    fontWeight: "900",
                    margin: "0",
                    color: "#333333",
                    letterSpacing: "1px",
                    textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)"
                  }}>
                    SCOREBOARD
                  </h3>
                  {data.setScores && data.setScores.length > 0 && (
                    <div style={{
                      background: "var(--primary)",
                      color: "#ffffff",
                      borderRadius: "8px",
                      padding: "4px 8px",
                      fontSize: "12px",
                      fontWeight: "900",
                      minWidth: "24px",
                      textAlign: "center",
                      boxShadow: "0 2px 4px rgba(46, 90, 172, 0.3)"
                    }}>
                      {data.setScores.length}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <button
                    type="button"
                    onClick={addSetScore}
                    style={{
                      background: "var(--primary)",
                      color: "#ffffff",
                      border: "1px solid var(--primary)",
                      borderRadius: "6px",
                      padding: "4px 6px",
                      fontSize: "9px",
                      fontWeight: "900",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 4px rgba(46, 90, 172, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                      letterSpacing: "0.5px",
                      textShadow: "0 1px 1px rgba(0, 0, 0, 0.3)"
                    }}
                  >
                    + ADD SET
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsFloatingWidgetExpanded(false)}
                    style={{
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      color: "#ffffff",
                      border: "2px solid #dc2626",
                      borderRadius: "6px",
                      padding: "8px",
                      fontSize: "18px",
                      fontWeight: "900",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      width: "44px",
                      height: "44px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)"
                    }}
                    title="Close"
                  >
                    ×
                  </button>
                </div>
              </div>

              <>
                {(!data.setScores || data.setScores.length === 0) ? (
                    <div style={{
                      textAlign: "center",
                      padding: "20px 12px",
                      color: "#666666",
                      fontSize: "12px",
                      background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                      borderRadius: "8px",
                      border: "2px dashed #dee2e6",
                      fontFamily: "'Courier New', monospace"
                    }}>
                      <div style={{ fontSize: "24px", marginBottom: "8px", color: "#667eea" }}>🏐</div>
                      <div style={{ fontWeight: "700", letterSpacing: "0.5px" }}>NO SETS RECORDED</div>
                    </div>
                  ) : (
                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {(data.setScores || []).map((s, i) => {
                    const usNum = Number(s.us || 0);
                    const oppNum = Number(s.opp || 0);
                    const isWin = usNum > oppNum;
                    const isSetComplete = usNum > 0 || oppNum > 0;
                    const isCollapsed = collapsedSets.has(i);
                    
                    // Show collapsed set as compact summary
                    if (isCollapsed) {
                      return (
                        <div key={i} style={{
                          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                          border: "1px solid #dee2e6",
                          borderRadius: "6px",
                          margin: "2px 0",
                          padding: "6px 8px",
                          transition: "all 0.2s ease",
                          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
                          cursor: "pointer"
                        }}
                        onClick={() => {
                          const newCollapsedSets = new Set(collapsedSets);
                          newCollapsedSets.delete(i);
                          setCollapsedSets(newCollapsedSets);
                        }}
                        title="Click to expand set"
                        >
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            fontSize: "12px",
                            fontWeight: "900",
                            color: "#333333",
                            fontFamily: "'Courier New', monospace",
                            letterSpacing: "0.5px"
                          }}>
                            <span>SET {i + 1}</span>
                            <span>
                              <span style={{ color: isSetComplete && isWin ? "#22c55e" : "#333333" }}>{usNum}</span>
                              <span style={{ color: "#666666", margin: "0 4px" }}>-</span>
                              <span style={{ color: isSetComplete && !isWin && oppNum > usNum ? "#ef4444" : "#333333" }}>{oppNum}</span>
                            </span>
                            <span style={{ fontSize: "14px", color: "#667eea" }}>▼</span>
                          </div>
                        </div>
                      );
                    }
                    
                    // Show expanded set with full controls
                    return (
                      <div key={i} style={{
                        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        margin: "4px 0",
                        padding: "8px",
                        transition: "all 0.2s ease",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)"
                      }}>
                        {/* Set Title */}
                        <div style={{
                          fontSize: "11px",
                          fontWeight: "900",
                          color: "#333333",
                          textAlign: "center",
                          marginBottom: "6px",
                          letterSpacing: "0.5px",
                          textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)",
                          fontFamily: "'Courier New', monospace"
                        }}>
                          SET {i + 1}
                        </div>
                        
                        {/* Score Controls */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          justifyContent: "center",
                          fontFamily: "'Courier New', monospace"
                        }}>
                          {/* Us Score - Clickable */}
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            {usNum > 0 && (
                              <button
                                type="button"
                                onClick={() => updateSetScore(i, "us", Math.max(0, usNum - 1))}
                                style={{
                                  background: "linear-gradient(135deg, #666 0%, #888 100%)",
                                  color: "#ffffff",
                                  border: "1px solid #888",
                                  borderRadius: "4px",
                                  width: "28px",
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  fontWeight: "900",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.3)"
                                }}
                                title="Decrease score"
                              >
                                −
                              </button>
                            )}
                            <div 
                              onClick={() => updateSetScore(i, "us", Math.min(99, usNum + 1))}
                              style={{
                                fontSize: "24px",
                                fontWeight: "900",
                                color: isSetComplete && isWin ? "#22c55e" : "#333333",
                                minWidth: "60px",
                                textAlign: "center",
                                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                border: "2px solid #dee2e6",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                textShadow: isSetComplete && isWin ? "0 0 8px rgba(34, 197, 94, 0.6)" : "none",
                                fontFamily: "'Courier New', monospace",
                                letterSpacing: "2px",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                userSelect: "none",
                                position: "relative"
                              }}
                              onMouseDown={(e) => {
                                e.target.style.transform = "scale(0.95)";
                                e.target.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.2)";
                              }}
                              onMouseUp={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "scale(1.05)";
                                e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                              }}
                            >
                              {usNum}
                            </div>
                          </div>
                          
                          <div style={{ 
                            color: "#667eea", 
                            fontSize: "12px", 
                            fontWeight: "900",
                            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                            borderRadius: "6px",
                            padding: "4px 8px",
                            letterSpacing: "1px",
                            border: "1px solid #dee2e6",
                            textShadow: "0 1px 2px rgba(255, 255, 255, 0.8)",
                            fontFamily: "'Courier New', monospace"
                          }}>VS</div>
                          
                          {/* Opponent Score - Clickable */}
                          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                            {oppNum > 0 && (
                              <button
                                type="button"
                                onClick={() => updateSetScore(i, "opp", Math.max(0, oppNum - 1))}
                                style={{
                                  background: "linear-gradient(135deg, #666 0%, #888 100%)",
                                  color: "#ffffff",
                                  border: "1px solid #888",
                                  borderRadius: "4px",
                                  width: "28px",
                                  height: "28px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "14px",
                                  fontWeight: "900",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.3)"
                                }}
                                title="Decrease score"
                              >
                                −
                              </button>
                            )}
                            <div 
                              onClick={() => updateSetScore(i, "opp", Math.min(99, oppNum + 1))}
                              style={{
                                fontSize: "24px",
                                fontWeight: "900",
                                color: isSetComplete && !isWin && oppNum > usNum ? "#ef4444" : "#333333",
                                minWidth: "60px",
                                textAlign: "center",
                                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                borderRadius: "8px",
                                padding: "12px 16px",
                                border: "2px solid #dee2e6",
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                textShadow: isSetComplete && !isWin && oppNum > usNum ? "0 0 8px rgba(239, 68, 68, 0.6)" : "none",
                                fontFamily: "'Courier New', monospace",
                                letterSpacing: "2px",
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                userSelect: "none",
                                position: "relative"
                              }}
                              onMouseDown={(e) => {
                                e.target.style.transform = "scale(0.95)";
                                e.target.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.2)";
                              }}
                              onMouseUp={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                                e.target.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.1)";
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "scale(1.05)";
                                e.target.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.15)";
                              }}
                            >
                              {oppNum}
                            </div>
                          </div>
                        </div>
                        
                        <div style={{ marginTop: "8px" }}>
                          <button
                            type="button"
                            onClick={() => removeSetScore(i)}
                            style={{
                              width: "100%",
                              background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                              color: "#ffffff",
                              border: "none",
                              borderRadius: "4px",
                              padding: "6px 8px",
                              fontSize: "9px",
                              fontWeight: "900",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              letterSpacing: "0.5px",
                              textShadow: "0 1px 1px rgba(0, 0, 0, 0.2)"
                            }}
                            title="Delete set"
                          >
                            DELETE SET
                          </button>
                        </div>
                      </div>
                    );
                  })}
                    </div>
                  )}
              </>
            </div>

            {/* Sync Status Indicator */}
            <div style={{ marginBottom: "16px" }}>
              <SyncStatusIndicator 
                syncStatus={syncStatus} 
                onSyncClick={() => {
                  updateSyncStatus({ isSyncing: true });
                  OfflineManager.processSyncQueue().then(() => {
                    updateSyncStatus({ isSyncing: false, lastSync: new Date(), syncError: null });
                    updatePendingCount();
                  }).catch((error) => {
                    updateSyncStatus({ isSyncing: false, syncError: error.message });
                  });
                }}
              />
            </div>

            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: "32px" }}>
              <h1 style={{ 
                fontSize: "28px", 
                fontWeight: "700", 
                margin: "0 0 8px 0", 
                color: "#000000"
              }}>
                {editingMatchId ? "Edit Match" : "Record Match"}
              </h1>
              <p style={{ 
                fontSize: "16px", 
                color: "var(--muted)", 
                margin: "0",
                fontWeight: "400"
              }}>
                {players.find((p) => p.id === activePlayerId)?.name || "Player"} • {seasons.find((s) => s.id === activeSeasonId)?.name || "Season"}
              </p>
            </div>

            {/* Player/Season Selectors */}
            <PlayerSeasonSelectors
              isPlayerSeasonExpanded={isPlayerSeasonExpanded}
              setIsPlayerSeasonExpanded={setIsPlayerSeasonExpanded}
              players={players}
              seasons={seasons}
              activePlayerId={activePlayerId}
              activeSeasonId={activeSeasonId}
              changeActivePlayer={changeActivePlayer}
              changeActiveSeason={changeActiveSeason}
            />

            {/* Match Details */}
            <MatchDetailsForm
              isMatchDetailsExpanded={isMatchDetailsExpanded}
              setIsMatchDetailsExpanded={setIsMatchDetailsExpanded}
              data={data}
              setData={setData}
              onField={onField}
              onOpponentChange={onOpponentChange}
              generateShareToken={generateShareToken}
              showNotice={showNotice}
              tournaments={tournaments}
              FEATURES={FEATURES}
            />

            {/* Match Media */}
            <MatchMediaUpload
              isMatchMediaExpanded={isMatchMediaExpanded}
              setIsMatchMediaExpanded={setIsMatchMediaExpanded}
              data={data}
              setData={setData}
              handleMatchMediaUpload={handleMatchMediaUpload}
            />

            {/* Stats Section - Tabbed Interface */}
            <section style={{ 
              background: "#fff", 
              border: "1px solid var(--border)", 
              borderRadius: "12px", 
              padding: "20px", 
              marginBottom: "24px" 
            }}>
              <h2 style={{ 
                fontSize: "18px", 
                fontWeight: "600", 
                margin: "0 0 16px 0", 
                color: "var(--text)" 
              }}>
                Match Statistics
              </h2>
              
              {/* Tab Navigation */}
              <div className="tab-navigation" style={{ 
                display: "flex", 
                gap: "4px", 
                marginBottom: "0px",
                borderBottom: "2px solid var(--border)",
                paddingBottom: "0px",
                overflowX: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none"
              }}>
                <button
                  onClick={() => setActiveStatsTab("offense")}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px 8px 0 0",
                    border: "none",
                    background: activeStatsTab === "offense" ? 'var(--primary)' : 'transparent',
                    color: activeStatsTab === "offense" ? 'white' : '#666',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Offense
                </button>
                <button
                  onClick={() => setActiveStatsTab("defense")}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px 8px 0 0",
                    border: "none",
                    background: activeStatsTab === "defense" ? 'var(--primary)' : 'transparent',
                    color: activeStatsTab === "defense" ? 'white' : '#666',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Defense
                </button>
                <button
                  onClick={() => setActiveStatsTab("serving")}
                  style={{
                    padding: "12px 16px",
                    borderRadius: "8px 8px 0 0",
                    border: "none",
                    background: activeStatsTab === "serving" ? 'var(--primary)' : 'transparent',
                    color: activeStatsTab === "serving" ? 'white' : '#666',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    flexShrink: 0,
                    whiteSpace: 'nowrap'
                  }}
                >
                  Serving
                </button>
              </div>
              
              {/* Tab Content */}
              {activeStatsTab === "offense" && (
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
                  gap: "8px",
                  marginTop: "20px"
                }}>
                  {[
                    { key: "kills", label: "Kills" },
                    { key: "hitInPlay", label: "Hit in Play" },
                    { key: "hittingErrors", label: "Hit Errors" },
                    { key: "tip", label: "Tips" },
                    { key: "assists", label: "Assists" }
                  ].map((s) => (
                    <div key={s.key} style={{
                      background: "var(--bg-light)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: "11px", 
                          fontWeight: "500", 
                          color: "var(--text)",
                          marginBottom: "1px"
                        }}>
                          {s.label}
                        </div>
                        <div style={{ 
                          fontSize: "18px", 
                          fontWeight: "700", 
                          color: "var(--primary)" 
                        }}>
                          {data[s.key] ?? 0}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => dec(s.key)}
                          style={{
                            background: "var(--danger)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          onClick={() => inc(s.key)}
                          style={{
                            background: "var(--primary)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          +
                        </button>
                    </div>
                  </div>
                ))}
                </div>
              )}

              {activeStatsTab === "defense" && (
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
                  gap: "8px",
                  marginTop: "20px"
                }}>
                  {[
                    { key: "digs", label: "Digs" },
                    { key: "digErrors", label: "Dig Errors" },
                    { key: "sets", label: "Blocks" },
                    { key: "halfBlock", label: "Half Block" },
                    { key: "blockTouch", label: "Block Touch" },
                    { key: "blockError", label: "Block Error" },
                    { key: "srTouches", label: "SR Touches" }
                  ].map((s) => (
                    <div key={s.key} style={{
                      background: "var(--bg-light)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}>
                <div>
                        <div style={{ 
                          fontSize: "11px", 
                          fontWeight: "500", 
                          color: "var(--text)",
                          marginBottom: "1px"
                        }}>
                          {s.label}
                        </div>
                        <div style={{ 
                          fontSize: "18px", 
                          fontWeight: "700", 
                          color: "var(--primary)" 
                        }}>
                          {data[s.key] ?? 0}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => dec(s.key)}
                          style={{
                            background: "var(--danger)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          onClick={() => inc(s.key)}
                          style={{
                            background: "var(--primary)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeStatsTab === "serving" && (
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
                  gap: "8px",
                  marginTop: "20px"
                }}>
                  {[
                    { key: "serveAttempts", label: "Serve Att." },
                    { key: "aces", label: "Aces" },
                    { key: "serviceErrors", label: "Serve Errors" }
                  ].map((s) => (
                    <div key={s.key} style={{
                      background: "var(--bg-light)",
                      border: "1px solid var(--border)",
                      borderRadius: "8px",
                      padding: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: "11px", 
                          fontWeight: "500", 
                          color: "var(--text)",
                          marginBottom: "1px"
                        }}>
                          {s.label}
                        </div>
                        <div style={{ 
                          fontSize: "18px", 
                          fontWeight: "700", 
                          color: "var(--primary)" 
                        }}>
                          {data[s.key] ?? 0}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                        <button
                          type="button"
                          onClick={() => dec(s.key)}
                          style={{
                            background: "var(--danger)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          −
                        </button>
                        <button
                          type="button"
                          onClick={() => inc(s.key)}
                          style={{
                            background: "var(--primary)",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            width: "32px",
                            height: "32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            cursor: "pointer"
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Live Stats Summary */}
              <div style={{ 
                marginTop: "20px", 
                padding: "16px", 
                background: "var(--bg-light)", 
                borderRadius: "8px",
                border: "1px solid var(--border)"
              }}>
                <h3 style={{ 
                  fontSize: "12px", 
                  fontWeight: "600", 
                  margin: "0 0 8px 0", 
                  color: "var(--text)" 
                }}>
                  Live Stats Summary
                </h3>
                <div style={{ 
                  display: "grid", 
                  gridTemplateColumns: "repeat(2, 1fr)", 
                  gap: "8px"
                }}
                className="live-stats-grid">
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Hitting %</div>
                    <div style={{ fontSize: "18px", fontWeight: "600", color: "var(--text)" }}>
  {(() => {
    const hits = Number(data.hitInPlay || data.attackAttempts || 0);
                        const k = Number(data.kills || 0);
                        const he = Number(data.hittingErrors || 0);
    const totalAttempts = hits + k + he;
    if (!totalAttempts) return "0.000";
                        const v = (k - he) / totalAttempts;
                        return v.toFixed(3);
  })()}
</div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Kill %</div>
                    <div style={{ fontSize: "18px", fontWeight: "600", color: "var(--text)" }}>
                  {(() => {
                    const hits = Number(data.hitInPlay || data.attackAttempts || 0);
                        const k = Number(data.kills || 0);
                        const he = Number(data.hittingErrors || 0);
                    const totalAttempts = hits + k + he;
                    if (!totalAttempts) return "0%";
                        const v = (k / totalAttempts) * 100;
                    return Math.round(v) + "%";
                  })()}
                </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Serving %</div>
                    <div style={{ fontSize: "18px", fontWeight: "600", color: "var(--text)" }}>
                  {(() => {
                    const att = Number(data.serveAttempts || 0);
                    const err = Number(data.serviceErrors || 0);
                    if (!att) return "0%";
                    const inCount = Math.max(0, Math.min(att, att - err));
                    return Math.round((inCount / att) * 100) + "%";
                  })()}
                </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "12px", color: "var(--muted)", marginBottom: "4px" }}>Ace %</div>
                    <div style={{ fontSize: "18px", fontWeight: "600", color: "var(--text)" }}>
                  {(() => {
                    const att = Number(data.serveAttempts || 0);
                    const ac = Number(data.aces || 0);
                    if (!att) return "0%";
                    const a = Math.max(0, Math.min(att, ac));
                    return Math.round((a / att) * 100) + "%";
                  })()}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Set Scores */}
            <section style={{ 
              background: "#fff", 
              border: "1px solid var(--border)", 
              borderRadius: "12px", 
              padding: "24px", 
              marginBottom: "24px" 
            }}>
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between", 
                marginBottom: isSetScoresExpanded ? "16px" : "0",
                cursor: "pointer"
              }}
              onClick={() => setIsSetScoresExpanded(!isSetScoresExpanded)}
              >
                <h2 style={{ 
                  fontSize: "20px", 
                  fontWeight: "600", 
                  margin: "0", 
                  color: "var(--text)" 
                }}>
                  Set Scores
                </h2>
                <button 
                  type="button" 
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--muted)",
                    cursor: "pointer",
                    fontSize: "16px",
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease",
                    transform: isSetScoresExpanded ? "rotate(180deg)" : "rotate(0deg)"
                  }}
                  title={isSetScoresExpanded ? "Collapse" : "Expand"}
                >
                  ▼
                </button>
  </div>
              
              {isSetScoresExpanded && (
                <>

              {(!data.setScores || data.setScores.length === 0) ? (
                <div style={{
                  background: "var(--bg-light)",
                  border: "2px dashed var(--border)",
                  borderRadius: "8px",
                  padding: "32px 20px",
                  textAlign: "center"
                }}>
                  <div style={{ fontSize: "32px", marginBottom: "12px" }}>🏐</div>
                  <p style={{ 
                    fontSize: "12px", 
                    color: "var(--muted)", 
                    margin: "0" 
                  }}>
                    No set scores yet — click "Add Set" to start recording
                  </p>
    </div>
              ) : (
                <div style={{ 
                  display: "grid", 
                  gap: "16px", 
                  gridTemplateColumns: "1fr",
                  width: "100%"
                }}>
    {(data.setScores || []).map((s, i) => {
      const usNum = Number(s.us || 0);
      const oppNum = Number(s.opp || 0);
      const isWin = usNum > oppNum;
      const isSetComplete = usNum > 0 || oppNum > 0;

      return (
                      <div key={i} className="set-score-card" style={{
                        padding: "20px",
                        background: isSetComplete ? (isWin ? "var(--success-light)" : "var(--bg-light)") : "var(--bg-light)",
                        border: `2px solid ${isSetComplete ? (isWin ? "var(--success)" : "var(--border)") : "var(--border)"}`,
                        borderRadius: "12px",
                        transition: "all 0.2s ease",
                        position: "relative"
                      }}>
                        {/* Set Header */}
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          marginBottom: "20px"
                        }}>
                          <h3 style={{ 
                            fontSize: "20px", 
                            fontWeight: "700", 
                            color: "var(--text)",
                            margin: "0"
                          }}>
                            Set {i + 1}
                          </h3>
                          <button 
                            className="btn" 
                            type="button" 
                            onClick={() => removeSetScore(i)}
                            style={{ 
                              padding: "4px 8px", 
                              fontSize: "12px",
                              background: "transparent",
                              color: "var(--muted)",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              transition: "all 0.2s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "var(--danger-light)";
                              e.target.style.color = "var(--danger)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "transparent";
                              e.target.style.color = "var(--muted)";
                            }}
                            title="Remove this set"
                          >
                            ×
                          </button>
                        </div>

                        {/* Score Controls */}
                        <div style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                          gap: "24px"
                        }}>
                          {/* Us Score */}
                          <div style={{ textAlign: "center" }}>
                            <div style={{ 
                              fontSize: "12px", 
                              color: "var(--muted)", 
                              marginBottom: "8px",
                              fontWeight: "500",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}>
                              Us
                            </div>
                            <div className="set-score-buttons" style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "center",
                              gap: "16px"
                            }}>
                              <button
                                className="set-score-button"
                                type="button"
                                onClick={() => updateSetScore(i, "us", Math.max(0, usNum - 1))}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  border: "1px solid var(--border)",
                                  background: "white",
                                  color: "var(--text)",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  outline: "none"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.borderColor = "var(--primary)";
                                  e.target.style.background = "var(--primary-light)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.borderColor = "var(--border)";
                                  e.target.style.background = "white";
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = "var(--primary)";
                                  e.target.style.background = "var(--primary-light)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = "var(--border)";
                                  e.target.style.background = "white";
                                }}
                                onMouseDown={(e) => {
                                  e.target.style.borderColor = "var(--primary)";
                                  e.target.style.background = "var(--primary-light)";
                                  e.target.style.transform = "scale(0.95)";
                                }}
                                onMouseUp={(e) => {
                                  e.target.style.borderColor = "var(--border)";
                                  e.target.style.background = "white";
                                  e.target.style.transform = "scale(1)";
                                }}
                              >
                                −
                              </button>
                              <div className="set-score-display" style={{
                                fontSize: "28px",
                                fontWeight: "700",
                                color: "var(--text)",
                                minWidth: "40px",
                                textAlign: "center"
                              }}>
                                {usNum}
                              </div>
                              <button
                                className="set-score-button"
                                type="button"
                                onClick={() => updateSetScore(i, "us", Math.min(99, usNum + 1))}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  border: "1px solid #3b82f6",
                                  background: "#3b82f6",
                                  color: "white",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  outline: "none",
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#1d4ed8";
                                  e.target.style.borderColor = "#1d4ed8";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#3b82f6";
                                  e.target.style.borderColor = "#3b82f6";
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "#1d4ed8";
                                  e.target.style.borderColor = "#1d4ed8";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "#3b82f6";
                                  e.target.style.borderColor = "#3b82f6";
                                }}
                                onMouseDown={(e) => {
                                  e.target.style.background = "#1d4ed8";
                                  e.target.style.borderColor = "#1d4ed8";
                                  e.target.style.transform = "scale(0.95)";
                                }}
                                onMouseUp={(e) => {
                                  e.target.style.background = "#3b82f6";
                                  e.target.style.borderColor = "#3b82f6";
                                  e.target.style.transform = "scale(1)";
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>

                          {/* Opponent Score */}
                          <div style={{ textAlign: "center" }}>
                            <div style={{ 
                              fontSize: "12px", 
                              color: "var(--muted)", 
                              marginBottom: "8px",
                              fontWeight: "500",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}>
                              Opponent
                            </div>
                            <div className="set-score-buttons" style={{ 
                              display: "flex", 
                              alignItems: "center", 
                              justifyContent: "center",
                              gap: "16px"
                            }}>
                              <button
                                className="set-score-button"
                                type="button"
                                onClick={() => updateSetScore(i, "opp", Math.max(0, oppNum - 1))}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  border: "1px solid var(--border)",
                                  background: "white",
                                  color: "var(--text)",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  outline: "none"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.borderColor = "var(--danger)";
                                  e.target.style.background = "var(--danger-light)";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.borderColor = "var(--border)";
                                  e.target.style.background = "white";
                                }}
                                onFocus={(e) => {
                                  e.target.style.borderColor = "var(--danger)";
                                  e.target.style.background = "var(--danger-light)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.borderColor = "var(--border)";
                                  e.target.style.background = "white";
                                }}
                                onMouseDown={(e) => {
                                  e.target.style.borderColor = "var(--danger)";
                                  e.target.style.background = "var(--danger-light)";
                                  e.target.style.transform = "scale(0.95)";
                                }}
                                onMouseUp={(e) => {
                                  e.target.style.borderColor = "var(--border)";
                                  e.target.style.background = "white";
                                  e.target.style.transform = "scale(1)";
                                }}
                              >
                                −
                              </button>
                              <div className="set-score-display" style={{
                                fontSize: "28px",
                                fontWeight: "700",
                                color: "var(--text)",
                                minWidth: "40px",
                                textAlign: "center"
                              }}>
                                {oppNum}
                              </div>
                              <button
                                className="set-score-button"
                                type="button"
                                onClick={() => updateSetScore(i, "opp", Math.min(99, oppNum + 1))}
                                style={{
                                  width: "36px",
                                  height: "36px",
                                  borderRadius: "50%",
                                  border: "1px solid #ef4444",
                                  background: "#ef4444",
                                  color: "white",
                                  fontSize: "14px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  transition: "all 0.2s ease",
                                  outline: "none",
                                  userSelect: "none",
                                  WebkitUserSelect: "none",
                                  MozUserSelect: "none",
                                  msUserSelect: "none"
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background = "#dc2626";
                                  e.target.style.borderColor = "#dc2626";
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background = "#ef4444";
                                  e.target.style.borderColor = "#ef4444";
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "#dc2626";
                                  e.target.style.borderColor = "#dc2626";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "#ef4444";
                                  e.target.style.borderColor = "#ef4444";
                                }}
                                onMouseDown={(e) => {
                                  e.target.style.background = "#dc2626";
                                  e.target.style.borderColor = "#dc2626";
                                  e.target.style.transform = "scale(0.95)";
                                }}
                                onMouseUp={(e) => {
                                  e.target.style.background = "#ef4444";
                                  e.target.style.borderColor = "#ef4444";
                                  e.target.style.transform = "scale(1)";
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
        </div>
      );
    })}
  </div>
              )}

  {!!(data.setScores && data.setScores.length) && (
                <div style={{ 
                  marginTop: "16px", 
                  padding: "12px", 
                  background: "var(--bg-light)", 
                  borderRadius: "6px",
                  textAlign: "center"
                }}>
                  <span style={{ fontSize: "14px", color: "var(--muted)" }}>
      Sets W-L for this match:{" "}
                    <strong style={{ color: "var(--text)" }}>
      {(() => {
        const { w, l } = getSetWL(data.setScores);
        return `${w}-${l}`;
      })()}
                    </strong>
                  </span>
    </div>
  )}

              {/* Add Set Button at Bottom */}
              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginTop: "20px" 
              }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={addSetScore}
                  style={{
                    padding: "12px 24px",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    whiteSpace: "nowrap"
                  }}
                >
                  Add Set
                </button>
              </div>
                </>
              )}
</section>

            {/* Match Notes */}
            <section style={{ 
              background: "#fff", 
              border: "1px solid var(--border)", 
              borderRadius: "12px", 
              padding: "24px", 
              marginBottom: "24px" 
            }}>
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: isMatchNotesExpanded ? "12px" : "0",
                cursor: "pointer"
              }}
              onClick={() => setIsMatchNotesExpanded(!isMatchNotesExpanded)}
              >
              <h2 style={{ 
                fontSize: "20px", 
                fontWeight: "600", 
                  margin: "0", 
                color: "var(--text)" 
              }}>
                Match Notes
              </h2>
                <button
                  type="button"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--muted)",
                    cursor: "pointer",
                    fontSize: "16px",
                    padding: "4px",
                    borderRadius: "4px",
                    transition: "all 0.2s ease",
                    transform: isMatchNotesExpanded ? "rotate(180deg)" : "rotate(0deg)"
                  }}
                  title={isMatchNotesExpanded ? "Collapse" : "Expand"}
                >
                  ▼
                </button>
              </div>
              
              {isMatchNotesExpanded && (
                <>
              <p style={{ 
                fontSize: "14px", 
                color: "var(--muted)", 
                margin: "0 0 16px 0",
                lineHeight: "1.5"
              }}>
                Add any additional notes about this match (optional)
              </p>
              <textarea
                className="input"
                value={data.notes || ""}
                onChange={(e) => setData((d) => ({ ...d, notes: e.target.value }))}
                placeholder="e.g., Great comeback in the 3rd set! Sarah had an amazing serving run..."
                rows={4}
                style={{ 
                  width: "100%",
                  resize: 'vertical',
                  minHeight: '100px'
                }}
              />
                </>
              )}
            </section>

            {/* Actions */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "1fr 1fr", 
              gap: "12px", 
              marginTop: "32px",
              maxWidth: "600px",
              margin: "32px auto 48px"
            }}>
              <button
                type="button"
                onClick={() => {
                  if (!isOnline) {
                    setShowOfflineDeleteModal(true);
                  } else {
                    setConfirmClearStatsOpen(true);
                  }
                }}
                className="btn btn-danger"
                style={{
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%"
                }}
              >
                Clear Stats
              </button>

              <button
                className="btn btn-primary"
                onClick={saveMatch}
                style={{
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%"
                }}
              >
                {editingMatchId ? "Update Match" : "Save Match"}
              </button>

              <button
                className="btn btn-primary"
                onClick={() => {
                  const { w: wlW, l: wlL } = getSetWL(data.setScores || []);
                  const setScoresStr = formatSetScores(data.setScores || []);
                  const headers = [
                    "Player Name",
                    "Match Date",
                    "Opponent",
                    ...STAT_FIELDS.map((s) => s.label),
                    "Sets W-L",
                    "Set Scores",
                    "Notes",
                  ];
                  const values = [
                    escapeCsv(data.playerName),
                    escapeCsv(data.matchDate),
                    escapeCsv(data.opponent),
                    ...STAT_FIELDS.map((s) => String(data[s.key] ?? 0)),
                    `${wlW}-${wlL}`,
                    escapeCsv(setScoresStr),
                    escapeCsv(data.notes || ""),
                  ];
                  const csvOne = [headers.join(","), values.join(",")].join("\n");
                  const blob = new Blob([csvOne], { type: "text/csv;charset=utf-8;" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  const filename = `vball-stats-${(data.playerName || "player").replace(/\s+/g, "_")}-${
                    data.matchDate || "date"
                  }.csv`;
                  a.download = filename;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}
                style={{
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%"
                }}
              >
                Export CSV
              </button>

              <button
                className="btn btn-danger"
                onClick={() => {
                  if (!data.email) {
                    showNotice("error", "Please enter an email address first.");
                    return;
                  }
                  window.location.href = buildMailto({ data, csv });
                }}
                style={{
                  padding: "16px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%"
                }}
              >
                Email Stats
              </button>
            </div>

            {/* Cancel Edit Button - Outside Grid */}
            {editingMatchId && (
              <div style={{ 
                display: "flex", 
                justifyContent: "center", 
                marginTop: "16px",
                marginBottom: "48px" 
              }}>
              <button
                  className="btn btn-muted"
                onClick={() => {
                    setEditingMatchId(null);
                    setView("season-detail");
                  }}
                  style={{
                  padding: "16px 32px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px"
                  }}
                >
                  ❌ Cancel Edit
              </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Static-style Footer */}
      <SiteFooter />

      {/* Position info modal */}
      {showPosInfo && (
        <div
          className="modal-backdrop"
          onClick={() => setShowPosInfo(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div
              className="modal-title"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>Volleyball Positions</div>
              <button
                className="btn"
                onClick={() => setShowPosInfo(false)}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <ul style={{ lineHeight: 1.65, margin: 0, paddingLeft: "1.1rem" }}>
                <li><code>OH</code> — Outside Hitter</li>
                <li><code>MB</code> — Middle Blocker/Hitter </li>
                <li><code>OPP</code> — Opposite/Right Hitter</li>
                <li><code>S</code> — Setter</li>
                <li><code>L</code> — Libero</li>
                <li><code>DS</code> — Defensive Specialist (Backrow)</li>
                <li><strong>Utility</strong> — Multi-role / fills where needed</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Pending Changes Modal */}
      {showPendingChanges && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowPendingChanges(false)}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '24px 32px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700' }}>
                Pending Changes ({offlineDataCount})
              </h2>
              <button
                onClick={() => {
                  setShowPendingChanges(false);
                  setPendingChangesError("");
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '28px',
                  cursor: 'pointer',
                  color: 'var(--muted)',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '24px 32px'
            }}>
              {pendingChangesError && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  color: '#dc2626',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  fontSize: '14px',
                  border: '1px solid #fecaca'
                }}>
                  {pendingChangesError}
                </div>
              )}
              <p style={{ margin: '0 0 20px 0', color: 'var(--muted)', lineHeight: '1.6' }}>
                You have {offlineDataCount} change{offlineDataCount !== 1 ? 's' : ''} waiting to sync. Review and sync individual changes or sync all at once.
              </p>

              <div style={{ marginBottom: '20px' }}>
                {(() => {
                  const queue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
                  return queue.map((item, index) => {
                    // Extract opponent name for team matches
                    let opponentName = '';
                    if (item.operation === 'addTeamMatch' && item.data?.data?.opponent) {
                      opponentName = ` vs ${item.data.data.opponent}`;
                    }

                    return (
                    <div key={item.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      marginBottom: '12px',
                      border: '1px solid var(--border)'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px', color: 'var(--text)' }}>
                          {item.operation === 'saveMatch' && 'New Match'}
                          {item.operation === 'updateMatch' && 'Updated Match'}
                          {item.operation === 'deleteMatch' && 'Deleted Match'}
                          {item.operation === 'addTeamMatch' && `New Team Match${opponentName}`}
                          {item.operation === 'deleteTeamMatch' && 'Deleted Team Match'}
                          {item.operation === 'savePlayer' && 'New Player'}
                          {item.operation === 'updatePlayer' && 'Updated Player'}
                          {item.operation === 'deletePlayer' && 'Deleted Player'}
                          {item.operation === 'saveSeason' && 'New Season'}
                          {item.operation === 'updateSeason' && 'Updated Season'}
                          {item.operation === 'deleteSeason' && 'Deleted Season'}
                          {item.operation === 'saveTournament' && 'New Tournament'}
                          {item.operation === 'updateTournament' && 'Updated Tournament'}
                          {item.operation === 'deleteTournament' && 'Deleted Tournament'}
                          {item.operation === 'updateUserPrefs' && 'Settings Change (Local Only)'}
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                          {new Date(item.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <button
                          style={{
                            padding: '8px 16px',
                            background: isOnline ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' : '#e9ecef',
                            color: isOnline ? 'white' : '#adb5bd',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: isOnline ? 'pointer' : 'not-allowed',
                            fontWeight: '600',
                            fontSize: '13px',
                            boxShadow: isOnline ? '0 2px 8px rgba(67, 233, 123, 0.2)' : 'none'
                          }}
                          onClick={async () => {
                            // Check if we're online before attempting sync
                            if (!isOnline) {
                              setPendingChangesError("Switch to online mode to sync changes.");
                              return;
                            }
                            
                            try {
                              await OfflineManager.executeOperation(item.operation, item.data);
                              // Remove from queue
                              const currentQueue = OfflineManager.loadFromLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE) || [];
                              const updatedQueue = currentQueue.filter(q => q.id !== item.id);
                              OfflineManager.saveToLocal(OFFLINE_STORAGE_KEYS.SYNC_QUEUE, updatedQueue);
                              setOfflineDataCount(updatedQueue.length);
                              if (updatedQueue.length === 0) {
                                setShowPendingChanges(false);
                              }
                              setPendingChangesError("");
                              
                              // Refresh matches data after successful sync
                              if (view === "season-detail") {
                                await loadMatches();
                              }
                              
                              showNotice("success", "Change synced successfully!");
                            } catch (error) {
                              console.error('Failed to sync item:', error);
                              showNotice("error", "Failed to sync change. Please try again.");
                            }
                          }}
                        >
                          Sync Now
                        </button>
                    </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div style={{
              padding: '20px 32px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowPendingChanges(false)}
                style={{
                  padding: '12px 24px',
                  background: '#f8f9fa',
                  color: '#333',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Review Later
              </button>
              <button
                onClick={async () => {
                  if (!isOnline) {
                    setPendingChangesError("You must be online to sync changes.");
                    return;
                  }

                  try {
                    await OfflineManager.processSyncQueue();
                    setOfflineDataCount(0);
                    setShowPendingChanges(false);
                    setPendingChangesError("");

                    if (view === "season-detail") {
                      await loadMatches();
                    }

                    showNotice("success", "All changes synced successfully!");
                  } catch (error) {
                    console.error('Failed to sync all changes:', error);
                    showNotice("error", "Some changes failed to sync. Please try again.");
                  }
                }}
                style={{
                  padding: '12px 24px',
                  background: isOnline ? 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' : '#e9ecef',
                  color: isOnline ? 'white' : '#adb5bd',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: isOnline ? 'pointer' : 'not-allowed',
                  fontWeight: '600',
                  fontSize: '14px',
                  boxShadow: isOnline ? '0 4px 12px rgba(67, 233, 123, 0.3)' : 'none'
                }}
              >
                Sync All Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Mode Info Modal */}
      {showOfflineInfo && (
        <div
          className="modal-backdrop"
          onClick={() => setShowOfflineInfo(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div
              className="modal-title"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>🏐 Offline Mode <span style={{ fontStyle: "italic", fontWeight: "400", fontSize: "14px", color: "var(--muted)" }}>beta</span></div>
              <button
                className="btn"
                onClick={() => setShowOfflineInfo(false)}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body" style={{
              maxHeight: "70vh",
              overflowY: "auto",
              padding: "20px"
            }}>
              <div style={{ marginBottom: "8px" }}>
                <h3 style={{ margin: "0 0 12px 0", color: "var(--primary)" }}>What is Offline Mode?</h3>
                <p style={{ margin: "0 0 16px 0", lineHeight: "1.5" }}>
                  VBall Stats works even when you don't have internet! Record matches, view stats, 
                  and manage your data completely offline.
                </p>
              </div>

              <div style={{ marginBottom: "8px" }}>
                <h4 style={{ margin: "0 0 8px 0", color: "var(--primary)" }}>✅ What Works Offline:</h4>
                <ul style={{ margin: "0 0 16px 0", paddingLeft: "20px", lineHeight: "1.6" }}>
                  <li>Record new matches</li>
                  <li>Edit existing matches</li>
                  <li>View all your data (players, seasons, matches)</li>
                  <li>Generate CSV exports</li>
                  <li>Calculate all statistics</li>
                  <li>Use the Trends analysis</li>
                </ul>
              </div>

              <div style={{ marginBottom: "8px" }}>
                <h4 style={{ margin: "0 0 8px 0", color: "var(--primary)" }}>🔄 How Sync Works:</h4>
                <ul style={{ margin: "0 0 16px 0", paddingLeft: "20px", lineHeight: "1.6" }}>
                  <li>Changes are saved locally when offline</li>
                  <li><strong>Toggle back to online mode</strong> to sync your changes</li>
                  <li><strong>Pending Count:</strong> Shows how many changes are waiting to sync (e.g., "3 pending" means 3 operations queued)</li>
                  <li>Review and approve each change before syncing</li>
                </ul>
              </div>

              <div style={{ 
                backgroundColor: "var(--primary)", 
                padding: "12px", 
                borderRadius: "8px",
                marginBottom: "16px"
              }}>
                <h4 style={{ margin: "0 0 8px 0", color: "white", fontWeight: "600", fontSize: "16px" }}>💡 Pro Tips:</h4>
                <ul style={{ margin: "0", paddingLeft: "20px", lineHeight: "1.6", color: "white" }}>
                  <li>Great for tournaments with spotty internet connections</li>
                  <li>Your data is saved locally so you can work without internet</li>
                  <li>Take your time reviewing changes before they sync to your account</li>
                </ul>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Match History Abbreviations & Formulas modal */}
      {showStatsInfo && (
        <div
          className="modal-backdrop"
          onClick={() => setShowStatsInfo(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div
              className="modal-title"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>Stat Abbreviations & Formulas</div>
              <button
                className="btn"
                onClick={() => setShowStatsInfo(false)}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <ul style={{ lineHeight: 1.65, margin: 0, paddingLeft: "1.1rem" }}>
                <li><code>HA</code> — Hit Attempts</li>
                <li><code>HE</code> — Hitting Errors</li>{/* NEW */}
                <li><code>Tips</code> — Tips</li>{/* NEW */}
                <li><code>SA</code> — Serve Attempts</li>
                <li><code>SE</code> — Serve Errors</li>
                <li><code>DE</code> — Dig Errors</li>{/* NEW */}
                <li><code>BT</code> — Block Touch</li>{/* NEW */}
                <li><code>BE</code> — Block Error</li>{/* NEW */}
              </ul>

              <div style={{ marginTop: 20, fontWeight: 600, color: "var(--primary)" }}>Block Statistics Explained</div>
              <ul style={{ lineHeight: 1.65, margin: "8px 0 0 0", paddingLeft: "1.1rem" }}>
                <li><strong>Block Touch (BT):</strong> When a blocker makes contact with the ball, redirecting the attack to keep the ball in play, but does not result in a point for either team.</li>
                <li><strong>Block Assist:</strong> When more than one player goes up for a block, resulting in a point. A block assist is given to each player regardless of who touched the ball. For team block stats: count single blocks + half of the block assists.</li>
                <li><strong>Block Error (BE):</strong> When a block attempt results in a point for the other team (e.g., block goes out of bounds, or the blocker is in the net).</li>
              </ul>

              <div style={{ marginTop: 20, fontWeight: 600 }}>Percentages</div>
<ul style={{ lineHeight: 1.65, margin: 0, paddingLeft: "1.1rem" }}>
  <li>
    <code>Hitting %</code> (efficiency) = (<em>Kills</em> − <em>Hitting Err</em>) ÷ (<em>Hits</em> + <em>Kills</em> + <em>Hitting Err</em>) <em>(shown to three decimals)</em>
  </li>
  <li>
    <code>Kill %</code> = <em>Kills</em> ÷ (<em>Hits</em> + <em>Kills</em> + <em>Hitting Err</em>) × 100
  </li>

  <li>
    <code>Srv %</code> (a.k.a. <code>Serving %</code>) = (<em>Serve Att</em> − <em>Serve Err</em>) ÷ <em>Serve Att</em> × 100
  </li>
  <li>
    <code>Ace %</code> = <em>Aces</em> ÷ <em>Serve Att</em> × 100
  </li>
</ul>


              <p className="muted" style={{ marginTop: 8 }}>
                All percentages are rounded to the nearest whole number based on your entries.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Match Save Success Modal */}
      {showMatchSaveModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowMatchSaveModal(false)}
          role="dialog"
          aria-modal="true"
          style={{
            background: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "32px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)"
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
            <h3 style={{ margin: "0 0 12px 0" }}>Match Saved!</h3>
            {wasOfflineMatchSave && (
              <p style={{ color: "var(--muted)", fontSize: "14px", marginBottom: "24px" }}>
                Match queued for sync. It will appear in match history after you sync your pending changes when online.
              </p>
            )}
            {wasOfflineMatchSave ? (
              <button
                onClick={() => {
                  setShowMatchSaveModal(false);
                  setWasOfflineMatchSave(false);
                  // Reset form to empty state
                  setData({ ...EMPTY_DATA, playerName: players.find(p => p.id === activePlayerId)?.name || "" });
                  setEditingMatchId(null);
                }}
                className="btn btn-primary"
                style={{ width: "100%" }}
              >
                Record Another Match
              </button>
            ) : (
              <button
                onClick={() => {
                  setShowMatchSaveModal(false);
                  setWasOfflineMatchSave(false);
                }}
                className="btn btn-primary"
                style={{ width: "100%" }}
              >
                Done
              </button>
            )}
          </div>
        </div>
      )}

      {/* Share Success Modal */}
      {showShareModal && (
        <div
          className="modal-backdrop"
          onClick={() => setShowShareModal(false)}
          role="dialog"
          aria-modal="true"
          style={{
            background: "rgba(0, 0, 0, 0.5)",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <div
            className="modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#ffffff",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "400px",
              width: "90%",
              textAlign: "center",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)"
            }}
          >
            <div style={{
              fontSize: "48px",
              marginBottom: "16px"
            }}>
              ✅
            </div>
            <h3 style={{
              fontSize: "20px",
              fontWeight: "600",
              color: "var(--text)",
              margin: "0 0 8px 0"
            }}>
              Link Copied!
            </h3>
            <p style={{
              fontSize: "14px",
              color: "var(--muted)",
              margin: "0 0 20px 0",
              lineHeight: "1.5"
            }}>
              The share link has been copied to your clipboard. You can now paste it anywhere to share this match.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowShareModal(false)}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "all 0.3s ease"
              }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Delete Player Modal */}
      {pendingDeletePlayerId && (
        <div className="modal-backdrop" onClick={() => setPendingDeletePlayerId(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Delete player?</div>
            <div className="modal-message">
              This will permanently remove the player and <strong>all seasons and matches</strong> tied to them. This
              cannot be undone.
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setPendingDeletePlayerId(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  const id = pendingDeletePlayerId;
                  setPendingDeletePlayerId(null);
                  await deletePlayer(id);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* NEW: Delete Match Modal */}
      {pendingDeleteMatchId && (
        <div className="modal-backdrop" onClick={() => setPendingDeleteMatchId(null)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Delete match?</div>
            <div className="modal-message">
              This will permanently remove this match. <strong>This cannot be undone.</strong>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => setPendingDeleteMatchId(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  const id = pendingDeleteMatchId;
                  setPendingDeleteMatchId(null);
                  await removeMatch(id);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Tournament Modal */}
      {pendingDeleteTournamentId && (
        <div className="modal-backdrop" onClick={() => setPendingDeleteTournamentId(null)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Delete tournament?</div>
            <div className="modal-message">
              This will permanently remove this tournament and <strong>all matches</strong> associated with it. <strong>This cannot be undone.</strong>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setPendingDeleteTournamentId(null)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={async () => {
                  console.log('Delete tournament button clicked');
                  const id = pendingDeleteTournamentId;
                  console.log('Tournament ID to delete:', id);
                  setPendingDeleteTournamentId(null);
                  await deleteTournament(id);
                  setView("season-detail");
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Notes Modal */}
      {viewNotesMatch && (
        <div className="modal-backdrop" onClick={() => setViewNotesMatch(null)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">
              Match Notes
              <div style={{ fontSize: "14px", fontWeight: "normal", color: "var(--muted)", marginTop: "4px" }}>
                {viewNotesMatch.matchDate || "No date"} • {viewNotesMatch.opponent || "No opponent"}
              </div>
            </div>
            <div className="modal-message" style={{ 
              textAlign: "left", 
              maxHeight: "400px", 
              overflowY: "auto", 
              padding: "16px", 
              backgroundColor: "var(--bg-secondary)", 
              borderRadius: "8px"
            }}>
              {editingNotes ? (
                <textarea
                  className="input"
                  value={notesEditText}
                  onChange={(e) => setNotesEditText(e.target.value)}
                  placeholder="Enter match notes..."
                  style={{
                    width: "100%",
                    minHeight: "120px",
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: "12px",
                    lineHeight: "1.4"
                  }}
                />
              ) : (
                <div style={{ whiteSpace: "pre-wrap" }}>
                  {viewNotesMatch.notes}
                </div>
              )}
            </div>
            <div className="modal-actions">
              {editingNotes ? (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={async () => {
                      try {
                        // Update the match with new notes
                        const matchRef = doc(
                          db,
                          "users",
                          user.uid,
                          "players",
                          viewNotesMatch.playerId,
                          "seasons",
                          viewNotesMatch.seasonId,
                          "matches",
                          viewNotesMatch.id
                        );
                        await updateDoc(matchRef, { notes: notesEditText });

                        // Update local state
                        setMatches(prev => prev.map(m =>
                          m.id === viewNotesMatch.id
                            ? { ...m, notes: notesEditText }
                            : m
                        ));

                        // Update the viewNotesMatch state
                        setViewNotesMatch(prev => ({ ...prev, notes: notesEditText }));

                        setEditingNotes(false);
                        showNotice("success", "Notes updated successfully!");
                      } catch (err) {
                        showNotice("error", err?.message || "Could not update notes.");
                      }
                    }}
                    style={{
                      marginRight: "8px",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingNotes(false);
                      setNotesEditText("");
                    }}
                    style={{
                      marginRight: "8px",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setNotesEditText(viewNotesMatch.notes || "");
                      setEditingNotes(true);
                    }}
                    style={{
                      marginRight: "8px",
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Edit Notes
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setViewNotesMatch(null)}
                    style={{
                      padding: "12px 24px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      whiteSpace: "nowrap"
                    }}
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NEW: Clear Stats Modal */}
      {confirmClearStatsOpen && (
        <div className="modal-backdrop" onClick={() => setConfirmClearStatsOpen(false)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Clear stats?</div>
            <div className="modal-message">
              This will clear all fields and stats for the current match entry. <strong>This cannot be undone.</strong>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setConfirmClearStatsOpen(false)}>
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  actuallyClearStats();
                  setConfirmClearStatsOpen(false);
                }}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Offline Delete Modal */}
      {showOfflineDeleteModal && (
        <div className="modal-backdrop" onClick={() => setShowOfflineDeleteModal(false)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-title">Cannot Delete While Offline</div>
            <div className="modal-message">
              You cannot delete data while in offline mode. 
              Please switch to online mode to delete players, matches, or clear stats.
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={() => setShowOfflineDeleteModal(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Record gate modal */}
      {gateModal && (
        <div className="modal-backdrop" onClick={() => setGateModal(null)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title" style={{ marginTop: 0 }}>
              {gateModal === "needPlayer" ? "Add a player first" :
               gateModal === "needSeason" ? "Add a season first" :
               gateModal === "needDate" ? "Match Date Required" :
               gateModal === "needOpponent" ? "Opponent Name Required" :
               gateModal === "deleteSeason" ? "Delete Season?" :
               gateModal === "deleteLineup" ? "Delete Lineup?" : "Validation Error"}
            </h3>
            <p className="modal-body">
              {gateModal === "needPlayer" ? "To record a match, you need at least one player." :
               gateModal === "needSeason" ? "To record a match, you need at least one season for the selected player." :
               gateModal === "needDate" ? "Please enter a match date before saving." :
               gateModal === "needOpponent" ? "Please enter an opponent name before saving." :
               gateModal === "deleteSeason" ? `Are you sure you want to delete "${seasonToDelete?.seasonName}"? This will permanently delete all matches and stats for this season. This action cannot be undone.` :
               gateModal === "deleteLineup" ? `Are you sure you want to delete "${lineupToDelete?.lineupName}"? This action cannot be undone.` : ""}
            </p>
            <div className="modal-actions">
              {gateModal === "deleteSeason" ? (
                <>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteSeason}
                  >
                    Delete Season
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setGateModal(null);
                      setSeasonToDelete(null);
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : gateModal === "deleteLineup" ? (
                <>
                  <button
                    className="btn btn-danger"
                    onClick={handleDeleteLineup}
                  >
                    Delete Lineup
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setGateModal(null);
                      setLineupToDelete(null);
                    }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {(gateModal === "needPlayer" || gateModal === "needSeason") && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setGateModal(null);
                        if (gateModal === "needPlayer") {
                          setActivePlayerId(null);
                          setPlayerEditMode("create");
                          setView("player-setup");
                        } else {
                          setView("season-setup");
                        }
                      }}
                    >
                      {gateModal === "needPlayer" ? "Add Player" : "Add Season"}
                    </button>
                  )}
                  <button
                    className="btn btn-primary"
                    onClick={() => setGateModal(null)}
                  >
                    Cancel
                  </button>
                  {gateModal === "needPlayer" && user?.sharedPlayers?.length > 0 && (
                    <p style={{
                      fontSize: '13px',
                      color: 'var(--muted)',
                      margin: '12px 0 0 0',
                      textAlign: 'center',
                      lineHeight: '1.4'
                    }}>
                      Note: You can only record matches for players you create. Shared players are managed by your coach.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Add Player by Code Modal */}
      {showAddPlayerCodeModal && (
        <div className="modal-backdrop" onClick={() => setShowAddPlayerCodeModal(false)} role="dialog" aria-modal="true">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title" style={{ marginTop: 0 }}>
              Add Player by Code
            </h3>
            <p className="modal-body" style={{ marginBottom: '20px', color: '#6B7280' }}>
              Enter the player code provided by your coach to access their stats.
            </p>
            <input
              type="text"
              value={playerCode}
              onChange={(e) => setPlayerCode(e.target.value.toUpperCase())}
              placeholder="VB-ALEX-1234"
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                fontFamily: 'monospace',
                letterSpacing: '1px',
                border: '2px solid #E5E7EB',
                borderRadius: '8px',
                marginBottom: '20px',
                textAlign: 'center',
                fontWeight: '600'
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddPlayerByCode();
                }
              }}
            />
            <div className="modal-actions">
              <button
                className="btn btn-primary"
                onClick={handleAddPlayerByCode}
                disabled={!playerCode.trim()}
              >
                Add Player
              </button>
              <button
                className="btn btn-muted"
                onClick={() => {
                  setShowAddPlayerCodeModal(false);
                  setPlayerCode('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </Suspense>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />

      {/* Upgrade Modal */}
      <UpgradePromptModal
        isOpen={upgradeModalOpen}
        onClose={() => setUpgradeModalOpen(false)}
        user={user}
        feature={upgradeModalFeature}
      />

      {/* Team Selection Modal */}
      {showTeamSelectionModal && (
        <TeamSelectionModal
          teams={teams}
          onSelectTeam={handleTeamSelection}
          onClose={() => setShowTeamSelectionModal(false)}
        />
      )}

    </div>
  );
}

/* Visual Stat Card with Progress Bar */
function VisualStatCard({ label, value, color, isPercentage, percentValue, tooltip }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <>
    <div
      style={{
        border: "1px solid #e8e8e8",
        borderRadius: "12px",
        padding: "20px",
        background: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
        transition: "all 0.2s ease",
        position: "relative"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.04)";
      }}
    >
      <div style={{
        fontSize: "13px",
        fontWeight: "500",
        color: "#6b7280",
        marginBottom: "8px",
        letterSpacing: "0.3px",
        display: "flex",
        alignItems: "center",
        gap: "6px"
      }}>
        <span>{label}</span>
        {tooltip && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip(!showTooltip);
            }}
            style={{
              background: color,
              border: "none",
              cursor: "pointer",
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "11px",
              fontWeight: "700",
              opacity: 0.7,
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.transform = "scale(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.7";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="See calculation"
          >
            ?
          </button>
        )}
      </div>
      <div style={{
        fontSize: "32px",
        fontWeight: "800",
        color: color,
        marginBottom: isPercentage ? "12px" : "0",
        fontVariantNumeric: "tabular-nums"
      }}>
        {value}
      </div>
      {isPercentage && (
        <div style={{
          width: "100%",
          height: "8px",
          background: "#e8e8e8",
          borderRadius: "4px",
          overflow: "hidden",
          position: "relative"
        }}>
          <div style={{
            width: `${Math.min(percentValue, 100)}%`,
            height: "100%",
            background: `linear-gradient(90deg, ${color}, ${color}dd)`,
            borderRadius: "4px",
            transition: "width 0.6s ease"
          }} />
        </div>
      )}
    </div>

    {tooltip && showTooltip && (
      <div
        className="modal-backdrop"
        onClick={() => setShowTooltip(false)}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <div
            className="modal-title"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>How is this calculated?</div>
            <button
              className="btn"
              onClick={() => setShowTooltip(false)}
              aria-label="Close"
              title="Close"
            >
              ×
            </button>
          </div>

          <div className="modal-body">
            {tooltip}
          </div>
        </div>
      </div>
    )}
  </>
  );
}

// Lightbox Modal Component
function LightboxModal({ isOpen, onClose, images, currentIndex, onNavigate }) {
  if (!isOpen || !images || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === images.length - 1;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && !isFirst) onNavigate(currentIndex - 1);
    if (e.key === 'ArrowRight' && !isLast) onNavigate(currentIndex + 1);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px"
      }}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: "20px",
          right: "20px",
          background: "rgba(255, 255, 255, 0.2)",
          border: "none",
          borderRadius: "50%",
          width: "40px",
          height: "40px",
          color: "white",
          fontSize: "20px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000
        }}
      >
        ×
      </button>

      {/* Navigation Arrows */}
      {images.length > 1 && (
        <>
          {!isFirst && (
            <button
              onClick={() => onNavigate(currentIndex - 1)}
              style={{
                position: "absolute",
                left: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255, 255, 255, 0.2)",
                border: "none",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10000
              }}
            >
              ‹
            </button>
          )}
          {!isLast && (
            <button
              onClick={() => onNavigate(currentIndex + 1)}
              style={{
                position: "absolute",
                right: "20px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255, 255, 255, 0.2)",
                border: "none",
                borderRadius: "50%",
                width: "50px",
                height: "50px",
                color: "white",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10000
              }}
            >
              ›
            </button>
          )}
        </>
      )}

      {/* Image */}
      <div style={{
        maxWidth: "70vw",
        maxHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 0"
      }}>
        <img
          src={currentImage.url}
          alt={currentImage.name}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            borderRadius: "8px",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)"
          }}
        />
      </div>

      {/* Image Counter */}
      {images.length > 1 && (
        <div style={{
          position: "absolute",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(255, 255, 255, 0.2)",
          color: "white",
          padding: "8px 16px",
          borderRadius: "20px",
          fontSize: "14px",
          fontWeight: "500"
        }}>
          {currentIndex + 1} / {images.length}
        </div>
      )}

      {/* Image Name */}
      <div style={{
        position: "absolute",
        bottom: "60px",
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(255, 255, 255, 0.2)",
        color: "white",
        padding: "8px 16px",
        borderRadius: "20px",
        fontSize: "14px",
        fontWeight: "500",
        textAlign: "center",
        maxWidth: "80vw"
      }}>
        {currentImage.name}
      </div>
    </div>
  );
}

// Update Social Media Meta Tags
function updateSocialMetaTags(match, imageDataUrl) {
  const scores = match.setScores || [];
  const { w, l } = getSetWL(scores);
  const isWin = w > l;
  const playerName = match.playerName || 'Player';
  const opponent = match.opponent || 'Opponent';
  const matchDate = match.matchDate || 'Unknown Date';
  
  // Create dynamic title
  const title = `${playerName} ${isWin ? 'Wins' : 'Loses'} vs ${opponent} - Volleyball Match Stats`;
  
  // Create dynamic description
  const teamName = match.playerTeam || 'Team';
  const description = `🏐 ${teamName} ${isWin ? 'won' : 'lost'} ${w}-${l} vs ${opponent} on ${matchDate}. View ${playerName}'s full match statistics including kills, aces, digs, and more!`;
  
  // Update document title
  document.title = title;
  
  // Update or create meta tags
  const updateMetaTag = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`) || 
               document.querySelector(`meta[name="${property}"]`);
    
    if (!meta) {
      meta = document.createElement('meta');
      if (property.startsWith('og:') || property.startsWith('twitter:')) {
        meta.setAttribute('property', property);
      } else {
        meta.setAttribute('name', property);
      }
      document.head.appendChild(meta);
    }
    
    meta.setAttribute('content', content);
  };
  
  // Update basic meta tags
  updateMetaTag('description', description);
  
  // Update Open Graph tags
  updateMetaTag('og:title', title);
  updateMetaTag('og:description', description);
  updateMetaTag('og:image', imageDataUrl);
  updateMetaTag('og:url', window.location.href);
  updateMetaTag('og:type', 'article');
  
  // Update Twitter Card tags
  updateMetaTag('twitter:title', title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', imageDataUrl);
  updateMetaTag('twitter:card', 'summary_large_image');
}

// Image Cache Management
const imageCache = {
  // Cache storage key
  CACHE_KEY: 'vballstats_social_images',
  // Cache expiration time (24 hours)
  CACHE_EXPIRY: 24 * 60 * 60 * 1000,
  
  // Generate cache key for a match
  generateKey(match) {
    const keyData = {
      playerName: match.playerName,
      playerTeam: match.playerTeam,
      opponent: match.opponent,
      matchDate: match.matchDate,
      setScores: match.setScores,
      shareToken: match.shareToken
    };
    return btoa(JSON.stringify(keyData));
  },
  
  // Get cached image
  get(key) {
    try {
      const cached = localStorage.getItem(`${this.CACHE_KEY}_${key}`);
      if (!cached) return null;
      
      const { dataUrl, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - timestamp > this.CACHE_EXPIRY) {
        this.remove(key);
        return null;
      }
      
      return dataUrl;
    } catch (error) {
      console.warn('Error reading image cache:', error);
      return null;
    }
  },
  
  // Set cached image
  set(key, dataUrl) {
    try {
      const cacheData = {
        dataUrl,
        timestamp: Date.now()
      };
      localStorage.setItem(`${this.CACHE_KEY}_${key}`, JSON.stringify(cacheData));
    } catch (error) {
      console.warn('Error saving image cache:', error);
    }
  },
  
  // Remove cached image
  remove(key) {
    try {
      localStorage.removeItem(`${this.CACHE_KEY}_${key}`);
    } catch (error) {
      console.warn('Error removing image cache:', error);
    }
  },
  
  // Clean up expired cache entries
  cleanup() {
    try {
      const keys = Object.keys(localStorage);
      const now = Date.now();
      
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_KEY)) {
          try {
            const cached = localStorage.getItem(key);
            if (cached) {
              const { timestamp } = JSON.parse(cached);
              if (now - timestamp > this.CACHE_EXPIRY) {
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            // Remove corrupted entries
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.warn('Error cleaning up image cache:', error);
    }
  }
};

// Dynamic Social Media Image Generator with Caching
function generateSocialMediaImage(match, playerName) {
  return new Promise(async (resolve) => {
    // Generate cache key
    const cacheKey = imageCache.generateKey(match);
    
    // Check cache first
    const cachedImage = imageCache.get(cacheKey);
    if (cachedImage) {
      console.log('Using cached social media image');
      resolve(cachedImage);
      return;
    }
    
    console.log('Generating new social media image');
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions for social media (1200x630px)
    canvas.width = 1200;
    canvas.height = 630;
    
    // Calculate match result
    const scores = match.setScores || [];
    const { w, l } = getSetWL(scores);
    const isWin = w > l;
    
    // Blue to purple gradient background
    const gradient = ctx.createLinearGradient(0, 0, 1200, 630);
    gradient.addColorStop(0, '#3b82f6'); // Blue
    gradient.addColorStop(0.2, '#2563eb');
    gradient.addColorStop(0.4, '#1d4ed8');
    gradient.addColorStop(0.6, '#7c3aed'); // Purple
    gradient.addColorStop(0.8, '#6d28d9');
    gradient.addColorStop(1, '#5b21b6'); // Dark purple
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 1200, 630);
    
    // Add subtle overlay for better text readability
    ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
    ctx.fillRect(0, 0, 1200, 630);
    
    // Player photo section (if available)
    let playerPhotoLoaded = false;
    if (match.photoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise((resolveImg, rejectImg) => {
          img.onload = () => {
            // Draw circular player photo with glow effect and high quality scaling
            ctx.save();
            ctx.shadowColor = 'rgba(255, 255, 255, 0.4)';
            ctx.shadowBlur = 25;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.beginPath();
            ctx.arc(600, 160, 65, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, 535, 95, 130, 130);
            ctx.restore();
            
            // Add white border
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.arc(600, 160, 65, 0, Math.PI * 2);
            ctx.stroke();
            
            playerPhotoLoaded = true;
            resolveImg();
          };
          img.onerror = () => {
            // Fallback: Draw default avatar
            drawDefaultAvatar(ctx, 600, 160, 65);
            playerPhotoLoaded = true;
            resolveImg();
          };
          img.src = match.photoUrl;
        });
      } catch (err) {
        console.log('Could not load player photo:', err);
        // Fallback: Draw default avatar
        drawDefaultAvatar(ctx, 600, 160, 65);
        playerPhotoLoaded = true;
      }
    } else {
      // No photo URL provided, draw default avatar
      drawDefaultAvatar(ctx, 600, 160, 65);
      playerPhotoLoaded = true;
    }
    
    // Helper function to draw default avatar
    function drawDefaultAvatar(ctx, x, y, radius) {
      ctx.save();
      
      // Draw circular background
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw white border
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.stroke();
      
      // Draw volleyball icon
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = `${radius * 0.8}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🏐', x, y);
      
      ctx.restore();
    }
    
    // Match date - top center
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = '20px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(match.matchDate || 'Unknown Date', 600, 40);
    
    // Player name - centered with more spacing
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 42px Inter, sans-serif';
    ctx.fillText(playerName || 'Player', 600, playerPhotoLoaded ? 280 : 160);
    
    // Team vs Opponent - much larger with block font
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 56px Arial Black, sans-serif'; // Block font
    ctx.textAlign = 'center';
    
    // Split the text to make it span wider
    const teamText = `${match.playerTeam || 'Team'}`;
    const vsText = 'vs';
    const opponentText = `${match.opponent || 'Opponent'}`;
    
    // Calculate positions for wider layout
    const centerX = 600;
    const teamX = centerX - 220;
    const vsX = centerX;
    const opponentX = centerX + 220;
    const textY = playerPhotoLoaded ? 360 : 240; // More space after player name
    
    // Draw each part separately for better spacing
    ctx.fillText(teamText, teamX, textY);
    ctx.font = 'bold 40px Arial Black, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.fillText(vsText, vsX, textY);
    ctx.font = 'bold 56px Arial Black, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(opponentText, opponentX, textY);
    
    // Win/Loss result - muted display with much more spacing
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '24px Inter, sans-serif';
    ctx.fillText(isWin ? 'Win' : 'Loss', 600, playerPhotoLoaded ? 450 : 330);
    
    // Set scores without box background
    if (scores.length > 0) {
      const scoreY = playerPhotoLoaded ? 500 : 380;
      
      // Set scores text
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 24px Inter, sans-serif';
      ctx.textAlign = 'center';
      
      const scoreText = scores.map(score => `${score.us}-${score.opp}`).join(' | ');
      ctx.fillText(scoreText, 600, scoreY);
    }
    
    // VBALLSTAT logo and domain in white badge
    try {
      const logoImg = new Image();
      logoImg.crossOrigin = 'anonymous';
      await new Promise((resolveLogo, rejectLogo) => {
        logoImg.onload = () => {
          // Draw white badge background with rounded corners
          const badgeX = 30;
          const badgeY = 550;
          const badgeWidth = 200;
          const badgeHeight = 60;
          const radius = 12;
          
          // Badge background
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.moveTo(badgeX + radius, badgeY);
          ctx.lineTo(badgeX + badgeWidth - radius, badgeY);
          ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + radius);
          ctx.lineTo(badgeX + badgeWidth, badgeY + badgeHeight - radius);
          ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX + badgeWidth - radius, badgeY + badgeHeight);
          ctx.lineTo(badgeX + radius, badgeY + badgeHeight);
          ctx.quadraticCurveTo(badgeX, badgeY + badgeHeight, badgeX, badgeY + badgeHeight - radius);
          ctx.lineTo(badgeX, badgeY + radius);
          ctx.quadraticCurveTo(badgeX, badgeY, badgeX + radius, badgeY);
          ctx.closePath();
          ctx.fill();
          
          // Badge border
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
          ctx.lineWidth = 1;
          ctx.stroke();
          
          // Draw logo image inside badge with high quality scaling
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(logoImg, badgeX + 10, badgeY + 10, 40, 40);
          
          // Add text next to logo inside badge
          ctx.fillStyle = '#1f2937';
          ctx.font = 'bold 16px Inter, sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText('VBALLSTAT', badgeX + 60, badgeY + 25);
          
          ctx.fillStyle = '#6b7280';
          ctx.font = '12px Inter, sans-serif';
          ctx.fillText('VBALLSTAT.COM', badgeX + 60, badgeY + 42);
          
          resolveLogo();
        };
        logoImg.onerror = () => {
          // Fallback badge with text if logo fails to load
          const badgeX = 30;
          const badgeY = 550;
          const badgeWidth = 180;
          const badgeHeight = 60;
          const radius = 12;
          
          // Badge background
          ctx.fillStyle = '#ffffff';
          ctx.beginPath();
          ctx.moveTo(badgeX + radius, badgeY);
          ctx.lineTo(badgeX + badgeWidth - radius, badgeY);
          ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + radius);
          ctx.lineTo(badgeX + badgeWidth, badgeY + badgeHeight - radius);
          ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX + badgeWidth - radius, badgeY + badgeHeight);
          ctx.lineTo(badgeX + radius, badgeY + badgeHeight);
          ctx.quadraticCurveTo(badgeX, badgeY + badgeHeight, badgeX, badgeY + badgeHeight - radius);
          ctx.lineTo(badgeX, badgeY + radius);
          ctx.quadraticCurveTo(badgeX, badgeY, badgeX + radius, badgeY);
          ctx.closePath();
          ctx.fill();
          
          // Badge border
          ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
          ctx.lineWidth = 1;
          ctx.stroke();
          
          // Text inside badge
          ctx.fillStyle = '#1f2937';
          ctx.font = 'bold 16px Inter, sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText('🏐 VBALLSTAT', badgeX + 15, badgeY + 25);
          
          ctx.fillStyle = '#6b7280';
          ctx.font = '12px Inter, sans-serif';
          ctx.fillText('VBALLSTAT.COM', badgeX + 15, badgeY + 42);
          
          resolveLogo();
        };
        logoImg.src = '/Vballstat-Logo.png';
      });
    } catch (err) {
      // Fallback badge with text if logo fails to load
      const badgeX = 30;
      const badgeY = 550;
      const badgeWidth = 180;
      const badgeHeight = 60;
      const radius = 12;
      
      // Badge background
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.moveTo(badgeX + radius, badgeY);
      ctx.lineTo(badgeX + badgeWidth - radius, badgeY);
      ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + radius);
      ctx.lineTo(badgeX + badgeWidth, badgeY + badgeHeight - radius);
      ctx.quadraticCurveTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX + badgeWidth - radius, badgeY + badgeHeight);
      ctx.lineTo(badgeX + radius, badgeY + badgeHeight);
      ctx.quadraticCurveTo(badgeX, badgeY + badgeHeight, badgeX, badgeY + badgeHeight - radius);
      ctx.lineTo(badgeX, badgeY + radius);
      ctx.quadraticCurveTo(badgeX, badgeY, badgeX + radius, badgeY);
      ctx.closePath();
      ctx.fill();
      
      // Badge border
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Text inside badge
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 16px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('🏐 VBALLSTAT', badgeX + 15, badgeY + 25);
      
      ctx.fillStyle = '#6b7280';
      ctx.font = '12px Inter, sans-serif';
      ctx.fillText('VBALLSTAT.COM', badgeX + 15, badgeY + 42);
    }
    
    // Convert to optimized data URL
    const dataURL = canvas.toDataURL('image/jpeg', 0.85); // JPEG with 85% quality for smaller file size
    
    // Cache the generated image
    imageCache.set(cacheKey, dataURL);
    
    // Clean up old cache entries periodically
    if (Math.random() < 0.1) { // 10% chance to cleanup
      imageCache.cleanup();
    }
    
    resolve(dataURL);
  });
}

// Public Match View Component
function PublicMatchView({ shareToken }) {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // GA: Track public match views
  useEffect(() => {
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: `VBall Stats — Public Match`,
        page_path: `/match/${shareToken}`,
        custom_parameters: {
          match_share_token: shareToken
        }
      });
    }
  }, [shareToken]);

  useEffect(() => {
    const findPublicMatch = async () => {
      try {
        setLoading(true);
        
        // Query Firebase to find a match with this shareToken
        const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
        const { db } = await import('./firebase');
        
        // For now, let's try a simpler approach - create a flat collection for public matches
        // This avoids the complex nested query and security rule issues
        const publicMatchesRef = collection(db, 'public_matches');
        const q = query(publicMatchesRef, where('shareToken', '==', shareToken), limit(1));
        const querySnapshot = await getDocs(q);
        
        let foundMatch = null;
        
        if (!querySnapshot.empty) {
          foundMatch = querySnapshot.docs[0].data();
        }
        
        if (foundMatch) {
          setMatch(foundMatch);
          
          // GA: Track successful match load
          if (window.gtag) {
            window.gtag("event", "public_match_viewed", {
              event_category: "Match Sharing",
              event_label: foundMatch.playerName || 'Unknown Player',
              custom_parameters: {
                match_share_token: shareToken,
                player_name: foundMatch.playerName || 'Unknown Player',
                opponent: foundMatch.opponent || 'Unknown Opponent',
                match_date: foundMatch.matchDate || 'Unknown Date'
              }
            });
          }
          
          // Generate social media image and update meta tags for social sharing
          try {
            const imageDataUrl = await generateSocialMediaImage(foundMatch, foundMatch.playerName || 'Player');
            updateSocialMetaTags(foundMatch, imageDataUrl);
          } catch (err) {
            console.error('Error generating social image:', err);
          }
        } else {
          setMatch(null);
          setError("Match not found or no longer public");
          
          // GA: Track failed match load
          if (window.gtag) {
            window.gtag("event", "public_match_not_found", {
              event_category: "Match Sharing",
              event_label: "Match Not Found",
              custom_parameters: {
                match_share_token: shareToken
              }
            });
          }
        }
      } catch (err) {
        console.error('Error loading public match:', err);
        setError("Error loading match");
        
        // GA: Track error loading match
        if (window.gtag) {
          window.gtag("event", "public_match_error", {
            event_category: "Match Sharing",
            event_label: "Load Error",
            custom_parameters: {
              match_share_token: shareToken,
              error_message: err.message || 'Unknown error'
            }
          });
        }
      } finally {
        setLoading(false);
      }
    };

    findPublicMatch();
  }, [shareToken]);

  if (loading) {
    return (
      <div style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        fontSize: "18px",
        color: "var(--text)"
      }}>
        Loading match...
      </div>
    );
  }

  if (error || !match) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        padding: "20px",
        textAlign: "center"
      }}>
        <h1 style={{ color: "var(--text)", marginBottom: "16px" }}>
          Match Not Found
        </h1>
        <p style={{ color: "var(--muted)", marginBottom: "24px" }}>
          This match may have been removed or is no longer public.
        </p>
        <a 
          href="/" 
          style={{
            padding: "12px 24px",
            background: "var(--primary)",
            color: "white",
            textDecoration: "none",
            borderRadius: "8px",
            fontWeight: "600"
          }}
        >
          Go to Home
        </a>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: "800px",
      margin: "0 auto",
      padding: "20px",
      fontFamily: "system-ui, -apple-system, sans-serif"
    }}>
      {/* Site Header */}
      <div style={{
        textAlign: "center",
        marginBottom: "32px",
        padding: "24px",
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
        borderRadius: "16px",
        border: "1px solid var(--border)",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)"
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          marginBottom: "8px"
        }}>
          <img 
            src="/Vballstat-Logo.png" 
            alt="VballStat Logo" 
            style={{
              height: "40px",
              width: "auto"
            }}
          />
          <h1 style={{
            fontSize: "28px",
            fontWeight: "700",
            margin: "0",
            color: "#000000"
          }}>
            VballStat
          </h1>
        </div>
        <p style={{
          fontSize: "14px",
          color: "var(--muted)",
          margin: "0",
          fontWeight: "500"
        }}>
          Volleyball Statistics & Match Tracking
        </p>
      </div>

      {/* Match Header */}
      <div style={{ 
        textAlign: "center", 
        marginBottom: "32px",
        background: "linear-gradient(135deg, var(--primary), var(--accent))",
        borderRadius: "16px",
        padding: "32px 24px",
        color: "white",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)"
      }}>
        {/* Player Photo */}
        {match.photoUrl && (
          <div style={{ marginBottom: "20px" }}>
            <img 
              src={match.photoUrl} 
              alt={match.playerName}
              style={{
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                objectFit: "cover",
                border: "4px solid rgba(255, 255, 255, 0.3)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)"
              }}
            />
          </div>
        )}
        
        <h1 style={{ 
          fontSize: "32px", 
          fontWeight: "700", 
          margin: "0 0 16px 0", 
          color: "white",
          textShadow: "0 2px 4px rgba(0, 0, 0, 0.2)"
        }}>
          Match Results
        </h1>
        
        {/* Team vs Opponent Info */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: "24px",
          marginBottom: "16px",
          flexWrap: "wrap"
        }}>
          <div style={{
            background: "rgba(255, 255, 255, 0.2)",
            padding: "16px 20px",
            borderRadius: "12px",
            backdropFilter: "blur(10px)"
          }}>
            <div style={{ fontSize: "18px", fontWeight: "600" }}>
              {match.playerTeam || "Team"}
            </div>
          </div>
          
          <div style={{ fontSize: "24px", fontWeight: "600" }}>VS</div>
          
          <div style={{
            background: "rgba(255, 255, 255, 0.2)",
            padding: "12px 20px",
            borderRadius: "12px",
            backdropFilter: "blur(10px)"
          }}>
            <div style={{ fontSize: "18px", fontWeight: "600" }}>
              {match.opponent}
            </div>
          </div>
        </div>
        
        {/* Match Details */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "32px",
          fontSize: "14px",
          opacity: "0.9",
          flexWrap: "wrap"
        }}>
          <div>
            <strong>Date:</strong> {match.matchDate}
          </div>
        </div>
      </div>

      {/* Match Stats */}
      <div style={{
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: "16px",
        padding: "32px",
        marginBottom: "24px",
        boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)"
      }}>
        {/* Header with Download Button */}
        <div style={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px"
      }}>
        <h2 style={{ 
          fontSize: "24px", 
          fontWeight: "700", 
            margin: "0", 
            color: "#000000"
        }}>
          {match?.playerName || 'Player'}'s Stats
        </h2>
        
        </div>
        
        {/* Clean Stats Table */}
        <div style={{
          background: "#ffffff",
              borderRadius: "12px",
          border: "1px solid var(--border)",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)"
            }}>
          {/* Table Header */}
              <div style={{ 
            background: "#f8f9fa",
            padding: "16px 20px",
            borderBottom: "2px solid var(--border)"
          }}>
            <div style={{ fontSize: "16px", fontWeight: "600", color: "var(--text)", textAlign: "center" }}>
              Match Statistics
              </div>
              </div>

          {/* Stats Table */}
          <div style={{ padding: "0" }}>
            {/* Offensive Stats Section */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f3f4" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--muted)", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Offensive Stats
            </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Kills</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.kills || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Hits</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.hitInPlay || match.attackAttempts || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Hitting Errors</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.hittingErrors || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Tips</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.tip || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "600" }}>Hitting %</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.hittingPct || 0}%</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "600" }}>Kill %</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.killPct || 0}%</span>
                </div>
              </div>
            </div>

            {/* Serving Stats Section */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f3f4" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--muted)", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Serving Stats
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Aces</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.aces || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Serve Attempts</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.serveAttempts || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Service Errors</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.serviceErrors || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "600" }}>Serving %</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.servingPct || 0}%</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "600" }}>Ace %</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.acePct || 0}%</span>
                </div>
              </div>
            </div>

            {/* Defensive Stats Section */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f3f4" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--muted)", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Defensive Stats
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Digs</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.digs || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Dig Errors</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.digErrors || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Blocks</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{(Number(match.sets || 0) + Number(match.halfBlock || 0) * 0.5) || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Block Touch</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.blockTouch || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Block Error</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.blockError || 0}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                  <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Assists</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{match.assists || 0}</span>
                </div>
              </div>
            </div>

            {/* Match Summary Section */}
            <div style={{ padding: "16px 20px" }}>
              <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--muted)", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Match Summary
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" }}>
                {(() => {
                  const { w, l } = getSetWL(match.setScores || []);
                  return (
                    <>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                        <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Sets Won</span>
                        <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{w}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px" }}>
                        <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "500" }}>Sets Lost</span>
                        <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{l}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: "#f8f9fa", borderRadius: "8px", border: "1px solid var(--border)" }}>
                        <span style={{ fontSize: "13px", color: "var(--text)", fontWeight: "600" }}>Set Record</span>
                        <span style={{ fontSize: "16px", fontWeight: "700", color: "var(--text)" }}>{w}-{l}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Set Scores */}
      {match.setScores && match.setScores.length > 0 && (
        <div style={{
          background: "#fff",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          padding: "32px",
          marginBottom: "24px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)"
        }}>
          <h2 style={{ 
            fontSize: "24px", 
            fontWeight: "700", 
            margin: "0 0 24px 0", 
            color: "#000000",
            textAlign: "center"
          }}>
            Set Scores
          </h2>
          
          <div style={{ 
            display: "grid", 
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", 
            gap: "16px" 
          }}>
            {match.setScores.map((set, i) => {
              const usScore = Number(set.us || 0);
              const oppScore = Number(set.opponent || 0);
              const isWin = usScore > oppScore;
              
              return (
                <div key={i} style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "20px",
                  background: isWin 
                    ? "linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)" 
                    : "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
                  border: `2px solid ${isWin ? "#28a745" : "var(--border)"}`,
                  borderRadius: "12px",
                  textAlign: "center"
                }}>
                  <div style={{ 
                    fontSize: "16px", 
                    fontWeight: "600", 
                    color: "var(--text)",
                    marginBottom: "16px"
                  }}>
                    Set {i + 1}
                  </div>
                  
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    gap: "16px"
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: "24px", 
                        fontWeight: "700", 
                        color: isWin ? "#28a745" : "var(--text)",
                        marginBottom: "4px"
                      }}>
                        {usScore}
                      </div>
                      <div style={{ 
                        fontSize: "12px", 
                        color: "var(--muted)",
                        fontWeight: "500"
                      }}>
                        {match.playerTeam || "Team"}
                      </div>
                    </div>
                    
                    <div style={{ 
                      fontSize: "14px", 
                      color: "var(--muted)",
                      fontWeight: "600"
                    }}>
                      VS
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: "24px", 
                        fontWeight: "700", 
                        color: !isWin && oppScore > usScore ? "#dc3545" : "var(--text)",
                        marginBottom: "4px"
                      }}>
                        {oppScore}
                      </div>
                      <div style={{ 
                        fontSize: "12px", 
                        color: "var(--muted)",
                        fontWeight: "500"
                      }}>
                        {match.opponent}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}


      {/* Match Notes */}
      {match.notes && (
        <div style={{
          background: "#fff",
          border: "1px solid var(--border)",
          borderRadius: "16px",
          padding: "32px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)"
        }}>
          <h2 style={{ 
            fontSize: "24px", 
            fontWeight: "700", 
            margin: "0 0 20px 0", 
            color: "#000000",
            textAlign: "center"
          }}>
            Match Notes
          </h2>
          <div style={{
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            border: "1px solid var(--border)",
            borderRadius: "12px",
            padding: "20px"
          }}>
            <p style={{ 
              fontSize: "16px", 
              color: "var(--text)", 
              lineHeight: "1.6",
              margin: "0",
              textAlign: "center"
            }}>
              {match.notes}
            </p>
          </div>
        </div>
      )}

      {/* Match Media Gallery */}
      {match.matchMedia && match.matchMedia.length > 0 && (
      <div style={{
          background: "#fff",
          border: "1px solid var(--border)",
        borderRadius: "16px",
          padding: "32px",
          marginBottom: "24px",
          boxShadow: "0 4px 16px rgba(0, 0, 0, 0.05)"
        }}>
          <h2 style={{ 
            fontSize: "24px", 
            fontWeight: "700", 
            margin: "0 0 24px 0", 
            color: "#000000",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px"
          }}>
            📸 Match Media ({match.matchMedia.length})
          </h2>
          
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "16px"
          }}>
            {match.matchMedia.map((media, index) => (
              <div key={index} style={{
                position: "relative",
                background: "#f8f9fa",
                borderRadius: "12px",
                overflow: "hidden",
        border: "1px solid var(--border)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                cursor: "pointer"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "scale(1.02)";
                e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.15)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "none";
              }}
              onClick={() => {
                if (media.type === 'image') {
                  // Open lightbox for images
                  const imageMedia = match.matchMedia.filter(m => m.type === 'image');
                  const imageIndex = imageMedia.findIndex(img => img.url === media.url);
                  setLightboxIndex(imageIndex);
                  setLightboxOpen(true);
                }
              }}>
                {media.type === 'image' ? (
                  <img
                    src={media.url}
                    alt={media.name}
                    style={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                      display: "block"
                    }}
                  />
                ) : (
        <div style={{
                    width: "100%",
                    height: "150px",
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          display: "flex",
          alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    fontSize: "32px"
                  }}>
                    🎥
                  </div>
                )}
                
                {/* Media type badge */}
                <div style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  background: "rgba(0,0,0,0.7)",
                  color: "white",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  fontSize: "11px",
                  fontWeight: "600",
                  textTransform: "uppercase"
                }}>
                  IMG
                </div>
                
                {/* File name */}
                <div style={{
                  padding: "12px",
                  fontSize: "13px",
                  color: "var(--text)",
                  fontWeight: "500",
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  background: "white"
                }}>
                  {media.name}
        </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: "48px",
        padding: "24px",
        background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
        borderRadius: "16px",
        border: "1px solid var(--border)",
        textAlign: "center"
      }}>
        
        <div style={{
          fontSize: "14px",
          color: "var(--muted)",
          marginBottom: "12px",
          fontWeight: "500"
        }}>
          © {new Date().getFullYear()} VBall Stats
        </div>
        
        <div style={{
          fontSize: "12px",
          color: "var(--muted)",
          lineHeight: "1.5",
          maxWidth: "600px",
          margin: "0 auto"
        }}>
          VBall Stats is a free tool under active development. We can't guarantee that data will be
          saved, stored, or preserved. Please keep your own backups of anything important.
        </div>
      </div>

      {/* Lightbox Modal */}
      <LightboxModal
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={match?.matchMedia?.filter(m => m.type === 'image') || []}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />

    </div>
  );
}

// Expose migration function for easy access in console
if (typeof window !== 'undefined') {
  window.migratePlayerCodes = async () => {
    try {
      const { migratePlayerShareCodes } = await import('./services/playerCodeService');
      const result = await migratePlayerShareCodes();
      console.log('Migration complete:', result);
      return result;
    } catch (error) {
      console.error('Migration failed:', error);
      throw error;
    }
  };
}